import { Tooltip } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState, FC, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import DashboardCharts from "../component/ChartBar";
import HightChart from "../component/HightChart";
import DashboardCharts2 from "../component/ChartBar2";
import DashboardCharts3 from "../component/ChartBar3";
import { EquityCurveProps } from "../type";
import { mixGetV2 } from "services/mixin";
import TradingChart from "../component/TradingChart";
import { useNavigate, useParams } from "react-router-dom";
import { getLiveTrades, getReport } from "../function/api";
import { convertLiveTradeToTradeItems } from "../function/convert";
import html2canvas from "html2canvas-oklch";
import jsPDF from "jspdf";
import { PDFDocument } from "pdf-lib";
import { convertToLocalTime } from "pages/Dashboard/Metrix_v2/function/helper";
import MainAccountBalanceChart from "pages/Dashboard/Metrix_v2/component/main_account_balance_chart";
import { API_URL } from "env";
import ConsistencyScore from "pages/Dashboard/Metrix_v2/component/ConsistencyScore";
import AccountGoals from "pages/Dashboard/Metrix_v2/component/AccountGoals";
import TradingStatistics from "../component/TradingStatistics";
import BehavioralAnalysis from "../component/BehavioralAnalysis";
import { FileArrowDownIcon } from "@phosphor-icons/react";
interface HtmlToPdfOptions {
    jsPDF: {
        unit: "px" | "pt" | "mm" | "in" | "cm";
        format: string | number[];
        orientation: "portrait" | "landscape";
    };
    html2canvas: {
        useCORS: boolean;
        allowTaint: boolean;
        scale: number;
        logging: boolean;
        backgroundColor: string;
        removeContainer: boolean;
        scrollX: number;
        scrollY: number;
        width: number;
        windowWidth: number;
        margin: number;
        padding: number;
    };
    pagebreak: {
        mode: ('avoid-all' | 'css' | 'legacy')[];
        before: string;
        after: string;
        avoid: string;
    };
}
const MetrixPDF: FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { platform, account_number } = useParams();
    const langSetting = i18n.language;
    const [report, setReport] = useState<any>(null);
    const [liveTrade, setLiveTrade] = useState<any>(null);
    const [liveTradeList, setLiveTradeList] = useState<any[]>([]);
    const [isDown, setIsDown] = useState<boolean>(false);
    const printRef = useRef<HTMLDivElement>(null);
    const [message, setMessage] = useState<string>('');
    const [showDownloadCompleteModal, setShowDownloadCompleteModal] = useState<boolean>(false);
    const cancelDownloadRef = useRef<boolean>(false);

    const [user, setUser] = useState<any>(localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : null);
    const [dob, setDob] = useState<string>(user?.birthday || "");

    const [personalNumbers, setPersonalNumbers] = useState({
        personalYearNumber: null,
        personalMonthNumber: null,
        personalDayNumber: null
    });

    const [sectionChartData, setSectionChartData] = useState<{
        currentBalance: number;
        equity: number;
        profitLoss: number;
        chartData: {
            labels: string[];
            datasets: number[];
        };
        chartPoints: {
            balance: number;
            targetBalance: number;
            maxloss: number;
            balanceSize: number;
        };
    }>(
        {
            currentBalance: 0,
            equity: 0,
            profitLoss: 0,
            chartData: {
                labels: [],
                datasets: [],
            },
            chartPoints: {
                balance: 0,
                targetBalance: 0,
                maxloss: 0,
                balanceSize: 0,
            }
        }
    );

    useEffect(() => {
        try {
            const formatDateDDMMYYYY = (d: Date) => {
                const dd = String(d.getDate()).padStart(2, "0");
                const mm = String(d.getMonth() + 1).padStart(2, "0");
                const yyyy = d.getFullYear();
                return `${dd}/${mm}/${yyyy}`;
            };

            const rawDob = user?.birthday || "";
            let dateOnlyDob = "";
            if (rawDob) {
                // extract the date part if there's time
                const datePart = rawDob.includes(" ")
                    ? rawDob.split(" ")[0]
                    : rawDob.includes("T")
                        ? rawDob.split("T")[0]
                        : rawDob;

                // normalize separators to '-' for easier parsing
                const normalized = datePart.replace(/\//g, "-");
                const parts = normalized.split("-");

                if (parts.length === 3) {
                    // yyyy-mm-dd -> dd/mm/yyyy
                    if (parts[0].length === 4) {
                        const [yyyy, mm, dd] = parts;
                        dateOnlyDob = `${dd.padStart(2, "0")}/${mm.padStart(2, "0")}/${yyyy}`;
                    }
                    // dd-mm-yyyy or mm-dd-yyyy -> assume dd-mm-yyyy
                    else if (parts[2].length === 4) {
                        const [dd, mm, yyyy] = parts;
                        dateOnlyDob = `${dd.padStart(2, "0")}/${mm.padStart(2, "0")}/${yyyy}`;
                    } else {
                        dateOnlyDob = datePart;
                    }
                } else {
                    // fallback: parse with Date and format
                    const d = new Date(datePart);
                    if (!isNaN(d.getTime())) {
                        const dd = String(d.getDate()).padStart(2, "0");
                        const mm = String(d.getMonth() + 1).padStart(2, "0");
                        const yyyy = d.getFullYear();
                        dateOnlyDob = `${dd}/${mm}/${yyyy}`;
                    } else {
                        dateOnlyDob = datePart;
                    }
                }
            } else {
                dateOnlyDob = "";
            }
            setDob(dateOnlyDob);
        }
        catch (error) {
            console.error('Error parsing user from localStorage:', error);
        }
    }, []);

    // State for date filters
    const [closeTimeFrom, setCloseTimeFrom] = useState<string>('');
    const [closeTimeTo, setCloseTimeTo] = useState<string>('');
    // Memoize dayjs values to prevent recreation on every render
    const dayjsFrom = useMemo(() => {
        return (closeTimeFrom && closeTimeFrom !== '*' && closeTimeFrom !== '') ? dayjs(closeTimeFrom) : null
    }, [closeTimeFrom])

    const dayjsTo = useMemo(() => {
        return (closeTimeTo && closeTimeTo !== '*' && closeTimeTo !== '') ? dayjs(closeTimeTo) : null
    }, [closeTimeTo])



    useEffect(() => {
        const percentage = Number((Number(report?.consistency_score) * 100)?.toFixed(0));
        const fadeCount = 5
        const subArcs = document.querySelectorAll('g.subArc');
        const total = subArcs.length;
        const fillCount = Math.floor(total * (percentage / 100));
        let color: string

        if (percentage < 30) {
            color = '#FF3648'
        } else if (percentage >= 80 && percentage <= 100) {
            color = '#03C7B4'
        } else {
            color = '#fe9b07'
        }

        subArcs.forEach((el, index) => {
            if (index < fillCount) {
                el.setAttribute('fill', color);
                el.setAttribute('opacity', '1');
            } else if (index < fillCount - 2 + fadeCount) {
                el.setAttribute('fill', '');
                el.setAttribute('opacity', '0');
            } else {
                el.setAttribute('fill', 'rgb(147, 157, 172)');
                el.setAttribute('opacity', '1');
            }
        });
    }, [report])

    useEffect(() => {
        if (report && liveTrade) {
            const data = report;
            const liveTradeData = liveTrade ? liveTrade || [] : [];

            const equityCurve = data.equity_analysis?.equity_curve || [];

            interface EquityCurveItem {
                date?: string | Date;
                balance?: number | string;
            }

            const chartData = {
                labels: equityCurve.map((item: EquityCurveItem) => item.date ? new Date(item.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }) : ""),
                datasets: equityCurve.map((item: EquityCurveItem) => Number(item.balance || 0)),
            };

            interface LiveTradeItem {
                profit?: number | string;
                commission?: number | string;
                swap?: number | string;
            }

            const currentBalance = Number(data.performance_summary?.current_balance || 0);
            const equity = Number(data.performance_summary?.current_equity || 0);
            console.log(liveTradeData, 'liveTradeData')
            const profitLoss = liveTradeData.data.reduce((acc: number, trade: LiveTradeItem) => {
                const net = Number(trade.profit || 0);
                const comm = Number(trade.commission || 0);
                const swap = Number(trade.swap || 0);
                // Loại bỏ hoa hồng và phí swap để ra lợi nhuận chưa tính commission
                return acc + (net - comm - swap);
            }, 0);
            const baseBalance = Number(data.account_info?.initial_balance || (equityCurve.length > 0 ? equityCurve[0].balance : 0));
            const chartPoints = {
                balance: currentBalance,
                targetBalance: baseBalance * 1.05,
                maxloss: baseBalance * 0.9,
                balanceSize: baseBalance,
            };
            setSectionChartData({
                chartData,
                currentBalance,
                equity,
                profitLoss,
                chartPoints,
            });
        }
    }, [report, liveTrade]);

    // Equity Simulation Data
    const [isShowRun, setIsShowRun] = useState(false)
    const [avgDataTable, setAvgDataTable] = useState<{
        avg_drawdown_percent: number
        max_consecutive_losers: number
        max_consecutive_winners: number
        max_drawdown_percent: number
        max_equity: number
        min_equity: number
    }>()
    const [equityCurves, setEquityCurves] = useState<EquityCurveProps[]>()
    const [capital, setCapital] = useState(0)
    const [win, setWin] = useState<string>("50");
    const [rrr, setRRR] = useState<string>("1");
    const [iterations, setIterations] = useState(100)
    const [lines, setLines] = useState(10)
    const [risk, setRisk] = useState(1)
    const hasInitializedListData = useRef(false);

    const handleChange = (setter: React.Dispatch<React.SetStateAction<number>>, value: number, min: number, max: number, step = 1) => {
        setter((prev) => {
            let next = prev + value * step
            if (next < min) next = min
            if (next > max) next = max
            return Number(next?.toFixed(8))
        })
    }

    const sendData = async () => {
        try {
            const res = await mixGetV2(`/tool/simulator-matrix-rr-risk?matrix_balance_size=10&matrix_number_trades=10&matrix_rr=10&matrix_list_winrate=0.4%2C%200.5%2C%200.6%2C%200.7&matrix_list_risk_percent=0.002%2C%200.0025%2C%200.003%2C%200.0035%2C%200.004&chart_balance_size=${capital}&chart_lines=${lines}&chart_number_trades=${iterations}&chart_winrate=${Number(win || 0) / 100}&chart_rr=${rrr}&chart_risk_percent=${risk / 100}`);

            if (!res || typeof res !== 'object') {
                console.warn('Simulator returned invalid response:', res);
                setIsShowRun(false);
                setAvgDataTable(undefined);
                setEquityCurves([]);
                return;
            }

            // Transform equity_curves to match expected key names
            let transformedCurves: any[] = [];

            if (Array.isArray((res as any).equity_curves) && (res as any).equity_curves.length > 0) {
                transformedCurves = (res as any).equity_curves.map((curve: any) => ({
                    trade: curve.trade,
                    line1: curve.line_1,
                    line2: curve.line_2,
                    line3: curve.line_3,
                    line4: curve.line_4,
                    line5: curve.line_5,
                    line6: curve.line_6,
                    line7: curve.line_7,
                    line8: curve.line_8,
                    line9: curve.line_9,
                    line10: curve.line_10,
                    avg_equity: curve.avg_equity
                }));
            } else {
                // Fallback when equity_curves missing or null
                console.warn('Simulator response missing equity_curves; using empty dataset.');
                transformedCurves = [];
            }

            // Safely read avg_equity table if present
            const avgEquityTable = (res as any).analyze_equity_curves && (res as any).analyze_equity_curves.avg_equity
                ? (res as any).analyze_equity_curves.avg_equity
                : undefined;

            setIsShowRun(true);
            setAvgDataTable(avgEquityTable);
            setEquityCurves(transformedCurves); // Set transformed data
        } catch (error) {
            console.error('Error while calling simulator API in sendData:', error);
            setIsShowRun(false);
            setAvgDataTable(undefined);
            setEquityCurves([]);
        }
    };

    const fetchPersonalNumbers = useCallback(async () => {
        try {
            const userData = localStorage.getItem('user');
            if (!userData) return;

            const userObject = JSON.parse(userData);
            const dob = userObject.birthday;
            if (!dob) return;

            let formattedDob: string;
            const date = new Date(dob);
            if (isNaN(date.getTime())) {
                formattedDob = dob;
            } else {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                formattedDob = `${day}/${month}/${year}`;
            }

            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No token found in localStorage');
                return;
            }
            const language = localStorage.getItem('i18nextLng') || 'en';
            const response = await fetch(`${API_URL}/api/v1/be/coaching-daily-personal?birthday=${formattedDob}&locale=${language}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            setPersonalNumbers({
                personalYearNumber: data.data.personal_year.number,
                personalMonthNumber: data.data.personal_month.number,
                personalDayNumber: data.data.personal_day.number
            });
        } catch (error) {
            console.error('Error fetching personal numbers:', error);
        }
    }, []);

    // Executed on init
    useEffect(() => {
        fetchPersonalNumbers();
        const onInit = async () => {
            try {
                const reportData = await getReport(account_number || '', closeTimeFrom, closeTimeTo);
                setReport(reportData.data || {});
                const liveTradesData = await getLiveTrades(account_number || '', 100, 1);
                setLiveTrade(liveTradesData || {});
                const liveTradesDataList = convertLiveTradeToTradeItems(liveTradesData?.data || []);
                setLiveTradeList(liveTradesDataList || []);
            }
            catch (error) {
                console.error('Error during initialization:', error);
            }
        };
        onInit();
    }, [account_number]);

    useEffect(() => {
        // Chỉ chạy nếu report tồn tại và chưa khởi tạo
        if (report && !hasInitializedListData.current) {
            const newWin = parseFloat((report?.win_rate * 100)?.toFixed(1));
            const newRRR = parseFloat((report?.mean_RRR)?.toFixed(2));
            const newCapital = parseFloat((report?.today_balance)?.toFixed(2));

            // Kiểm tra giá trị hợp lệ trước khi set
            if (!isNaN(newWin) && !isNaN(newRRR) && !isNaN(newCapital)) {
                // store values as strings to match the state types
                setWin(String(Number(newWin.toFixed(1)) || 0));
                setRRR(String(Number(newRRR.toFixed(2)) || 0));
                setCapital(newCapital || 0);
                hasInitializedListData.current = true;
            }
        }
    }, [report]);

    useEffect(() => {
        if (hasInitializedListData.current) {
            sendData();
        }
    }, [win, rrr, capital]);


    const [checkDownPdf, setCheckDownPdf] = useState<number>(0)

    const startDownload = () => {
        cancelDownloadRef.current = false
        setShowDownloadCompleteModal(false)
        setIsDown(true)
        setMessage(t('Preparing PDF...'))
        setCheckDownPdf(checkDownPdf + 1)
        handleDownloadPdf()
    }

    const cancelDownload = () => {
        cancelDownloadRef.current = true
        setIsDown(false)
        setMessage(t('PDF generation canceled.'))
    }

    const goToHome = () => {
        setShowDownloadCompleteModal(false)
        navigate('/')
    }

    const generatePdfBuffer = async (
        element: HTMLElement,
        options: HtmlToPdfOptions
    ): Promise<ArrayBuffer> => {
        // Chờ images load hoàn toàn (Giữ nguyên)
        const images = element.querySelectorAll('img');
        await Promise.all(
            Array.from(images).map(
                (img) =>
                    new Promise((resolve) => {
                        if (img.complete) {
                            resolve(null);
                        } else {
                            img.onload = () => resolve(null);
                            img.onerror = () => resolve(null);
                        }
                    })
            )
        );

        const canvas = await html2canvas(element, options.html2canvas);

        const pdf = new jsPDF(options.jsPDF);
        // Giữ nguyên: Giảm quality xuống 0.85
        const imgData = canvas.toDataURL("image/jpeg", 0.85);

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        // Tỷ lệ chuyển đổi từ pixel của canvas sang đơn vị của jsPDF
        const scaleFactor = pdfWidth / canvas.width;
        const imgHeight = canvas.height * scaleFactor;

        let heightLeft = imgHeight;
        let position = 0;

        // --- BƯỚC 1: Thêm trang đầu tiên ---
        pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight, undefined, 'FAST');
        heightLeft -= pdfHeight;

        // --- BƯỚC 2: Thêm các trang còn lại (Đã chỉnh sửa logic vòng lặp) ---
        // Ngưỡng tối thiểu (Threshold) để xác định xem nội dung còn lại có đáng kể không.
        // Nếu chiều cao còn lại nhỏ hơn 5% chiều cao A4, thì không thêm trang mới.
        const MINIMUM_CONTENT_THRESHOLD = pdfHeight * 0.05;

        while (heightLeft > MINIMUM_CONTENT_THRESHOLD) {
            pdf.addPage();

            // Cần tính lại vị trí dựa trên chiều cao A4 đã trừ đi
            position -= pdfHeight;

            pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight, undefined, 'FAST');
            heightLeft -= pdfHeight;
        }

        // QUAN TRỌNG: Thêm điều kiện kiểm tra cuối cùng nếu page cuối có nội dung rất ít
        // Nếu trang cuối cùng chỉ là một khoảng trắng nhỏ, logic trên sẽ bỏ qua nó.

        return pdf.output("arraybuffer");
    };

    // Hàm mergeAndDownloadPdfs (Giữ nguyên)
    const mergeAndDownloadPdfs = async (
        pdfBuffers: ArrayBuffer[],
        outputFilename: string = "Metrix_Rule_Report.pdf"
    ) => {
        // ... (Code merge không thay đổi)
        // Tạm thời giữ nguyên code của bạn để tập trung vào generatePdfBuffer
        try {
            const mergedPdf = await PDFDocument.create();
            setMessage(t('MergingPDFs...'));
            for (const buffer of pdfBuffers) {
                const pdf = await PDFDocument.load(buffer);
                const copiedPages = await mergedPdf.copyPages(
                    pdf,
                    pdf.getPageIndices()
                );
                copiedPages.forEach((page) => mergedPdf.addPage(page));
            }

            const mergedPdfBytes = await mergedPdf.save();

            // Convert mergedPdfBytes to a plain ArrayBuffer to avoid TypeScript BlobPart type incompatibility
            // using slice() ensures we get a standard ArrayBuffer (avoids SharedArrayBuffer issues).
            const arrayBuffer = (mergedPdfBytes instanceof Uint8Array) ? mergedPdfBytes.slice().buffer : mergedPdfBytes as unknown as ArrayBuffer;
            const blob = new Blob([arrayBuffer], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            setMessage(t('DownloadingPDF...'));
            a.href = url;
            a.download = outputFilename;
            document.body.appendChild(a); // Thêm vào DOM
            a.click();
            document.body.removeChild(a); // Xóa khỏi DOM
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Lỗi merge PDF:", error);
            throw error;
        }
    };


    const fixListItems = (element: HTMLElement) => {
        // Xử lý từ trong ra ngoài (nested lists trước)
        const lists = Array.from(element.querySelectorAll('ol, ul')).reverse();

        lists.forEach((list) => {
            const listEl = list as HTMLElement;

            // Lấy computed style
            const computed = window.getComputedStyle(listEl);

            // Kiểm tra list-style none hoặc class list-none
            const isListStyleNone =
                listEl.classList.contains('list-none') ||
                listEl.style.listStyleType === 'none' ||
                listEl.style.listStyle === 'none' ||
                computed.listStyleType === 'none';

            const items = Array.from(list.children).filter(
                child => child.tagName === 'LI'
            );

            items.forEach((item, index) => {
                const li = item as HTMLElement;

                // Nếu đã xử lý rồi thì bỏ qua
                if (li.querySelector('.list-item-wrapper')) return;

                const isOrdered = list.tagName === 'OL';

                // ❗ Nếu list-none thì giữ nguyên, không thêm marker
                if (isListStyleNone) {
                    li.style.listStyle = 'none';
                    li.style.listStyleType = 'none';
                    return;
                }

                // Lưu nội dung gốc
                const originalContent = li.innerHTML;

                // Wrapper
                const wrapper = document.createElement('div');
                wrapper.className = 'list-item-wrapper';
                wrapper.style.display = 'flex';
                wrapper.style.alignItems = 'flex-start';
                wrapper.style.marginBottom = '8px';

                // Marker (bullet hoặc number)
                const marker = document.createElement('span');
                marker.style.marginRight = '8px';
                marker.style.minWidth = '20px';
                marker.style.lineHeight = '1.5';
                marker.style.flexShrink = '0';
                marker.textContent = isOrdered ? `${index + 1}.` : '•';

                // Content
                const content = document.createElement('div');
                content.innerHTML = originalContent;
                content.style.flex = '1';
                content.style.lineHeight = '1.5';

                wrapper.appendChild(marker);
                wrapper.appendChild(content);

                // Clear & append wrapper
                li.innerHTML = '';
                li.style.listStyle = 'none';
                li.style.listStyleType = 'none';
                li.style.display = 'block';
                li.style.paddingLeft = '0';
                li.appendChild(wrapper);
            });

            // Remove native list bullets
            listEl.style.listStyle = 'none';
            listEl.style.listStyleType = 'none';
        });
    };



    // Prevent automatic execution on mount
    const _skipInitialDownloadRef = useRef(false);

    async function handleDownloadPdf() {
        let htmlElement: HTMLElement | null = null;
        let styleBackup: Partial<CSSStyleDeclaration> | null = null;

        const throwIfCancelled = () => {
            if (cancelDownloadRef.current) {
                throw new Error('PDF_DOWNLOAD_CANCELLED');
            }
        };

        try {
            const element = printRef.current;
            if (!element) {
                console.error("Không tìm thấy printRef");
                return;
            }

            htmlElement = element as HTMLElement;
            const part1 = htmlElement.querySelector<HTMLDivElement>("#part1");
            const part2 = htmlElement.querySelector<HTMLDivElement>("#part2");
            const part3 = htmlElement.querySelector<HTMLDivElement>("#part3");
            const part4 = htmlElement.querySelector<HTMLDivElement>("#part4");
            const part5 = htmlElement.querySelector<HTMLDivElement>("#part5");

            if (!part1 || !part2 || !part3 || !part4 || !part5) {
                console.error("Không tìm thấy các part");
                return;
            }

            // CỐ ĐỊNH WIDTH cho tất cả trang
            const FIXED_WIDTH = 1200;
            const PAGE_HEIGHT = 1697; // Chiều cao 1 trang A4
            const PDF_SCALE = 3.2;

            const commonOptions: HtmlToPdfOptions = {
                jsPDF: {
                    unit: "px",
                    format: "a4",
                    orientation: "portrait",
                },
                html2canvas: {
                    useCORS: true,
                    allowTaint: true,
                    scale: PDF_SCALE,
                    logging: false,
                    backgroundColor: "#ffffff",
                    removeContainer: true,
                    scrollX: 0,
                    scrollY: 0,
                    // KEY: Chỉ cố định WIDTH, để HEIGHT tự động
                    width: FIXED_WIDTH,
                    windowWidth: FIXED_WIDTH,
                    margin: 0,
                    padding: 0,
                },
                // QUAN TRỌNG: Thêm pagebreak để tự động chia trang
                pagebreak: {
                    mode: ['avoid-all', 'css', 'legacy'],
                    before: '.page-break-before',
                    after: '.page-break-after',
                    avoid: '.no-page-break'
                }
            };

            // Backup style
            styleBackup = {
                position: htmlElement.style.position,
                visibility: htmlElement.style.visibility,
                opacity: htmlElement.style.opacity,
                width: htmlElement.style.width,
                height: htmlElement.style.height,
                overflow: htmlElement.style.overflow,
                transform: htmlElement.style.transform,
                transformOrigin: htmlElement.style.transformOrigin,
            };

            // Set style để render
            htmlElement.style.position = "absolute";
            htmlElement.style.left = "-9999px";
            htmlElement.style.top = "0";
            htmlElement.style.visibility = "visible";
            htmlElement.style.opacity = "1";
            htmlElement.style.width = `${FIXED_WIDTH}px`;
            htmlElement.style.height = "auto"; // ĐỂ AUTO để render hết nội dung
            htmlElement.style.minHeight = "0";
            htmlElement.style.overflow = "visible"; // ĐỂ VISIBLE để không cắt
            htmlElement.style.transformOrigin = "top left";
            htmlElement.style.transform = "scale(1)";

            const pdfBuffers: ArrayBuffer[] = [];

            // Hàm tiện ích để thiết lập hiển thị
            const setupPart = (partToShow: HTMLDivElement) => {
                // Ẩn tất cả
                [part1, part2, part3, part4, part5].forEach(part => {
                    part.style.display = "none";
                });

                // Hiển thị part cần render - KHÔNG SET HEIGHT
                partToShow.style.display = "block";
                partToShow.style.width = `${FIXED_WIDTH}px`;
                partToShow.style.maxWidth = `${FIXED_WIDTH}px`;
                partToShow.style.height = "auto"; // ĐỂ AUTO
                partToShow.style.minHeight = "0";
                partToShow.style.overflow = "visible"; // ĐỂ VISIBLE
                partToShow.style.boxSizing = "border-box";

                // Đảm bảo htmlElement tự động fit với nội dung part
                htmlElement.style.height = "auto";
                htmlElement.style.overflow = "visible";

                fixListItems(partToShow);

                // THÊM: Tự động thêm page-break sau mỗi PAGE_HEIGHT pixels
                addPageBreaks(partToShow, PAGE_HEIGHT);
            };

            // Hàm thêm page-break markers để tự động chia trang
            const addPageBreaks = (part: HTMLDivElement, pageHeight: number) => {
                // Xóa các page-break cũ nếu có
                part.querySelectorAll('.auto-page-break').forEach(el => el.remove());

                const children = Array.from(part.children) as HTMLElement[];
                let currentHeight = 0;

                children.forEach((child, index) => {
                    const childHeight = child.offsetHeight;

                    // Nếu vượt quá chiều cao 1 trang, thêm page-break
                    if (currentHeight + childHeight > pageHeight && currentHeight > 0) {
                        const pageBreak = document.createElement('div');
                        pageBreak.className = 'auto-page-break page-break-before';
                        pageBreak.style.pageBreakBefore = 'always';
                        pageBreak.style.breakBefore = 'page';
                        child.parentNode?.insertBefore(pageBreak, child);
                        currentHeight = childHeight; // Reset về chiều cao element hiện tại
                    } else {
                        currentHeight += childHeight;
                    }
                });
            };

            // --- RENDER PART 1 ---
            throwIfCancelled();
            setMessage(t('GeneratingPart1PDF...'));
            console.log("Bắt đầu render Part 1...");
            setupPart(part1);
            await new Promise((r) => setTimeout(r, 1500));
            const buffer1 = await generatePdfBuffer(htmlElement, commonOptions);
            pdfBuffers.push(buffer1);
            console.log("Part 1 hoàn thành, size:", buffer1.byteLength);

            // --- RENDER PART 2 ---
            throwIfCancelled();
            setMessage(t('GeneratingPart2PDF...'));
            console.log("Bắt đầu render Part 2...");
            setupPart(part2);
            await new Promise((r) => setTimeout(r, 1500));
            const buffer2 = await generatePdfBuffer(htmlElement, commonOptions);
            pdfBuffers.push(buffer2);
            console.log("Part 2 hoàn thành, size:", buffer2.byteLength);

            // --- RENDER PART 3 ---
            throwIfCancelled();
            setMessage(t('GeneratingPart3PDF...'));
            console.log("Bắt đầu render Part 3...");
            setupPart(part3);
            await new Promise((r) => setTimeout(r, 1500));
            const buffer3 = await generatePdfBuffer(htmlElement, commonOptions);
            pdfBuffers.push(buffer3);
            console.log("Part 3 hoàn thành, size:", buffer3.byteLength);

            // --- RENDER PART 4 ---
            throwIfCancelled();
            setMessage(t('GeneratingPart4PDF...'));
            console.log("Bắt đầu render Part 4...");
            setupPart(part4);
            await new Promise((r) => setTimeout(r, 1500));
            const buffer4 = await generatePdfBuffer(htmlElement, commonOptions);
            pdfBuffers.push(buffer4);
            console.log("Part 4 hoàn thành, size:", buffer4.byteLength);

            // --- RENDER PART 5 ---
            throwIfCancelled();
            setMessage(t('GeneratingPart5PDF...'));
            console.log("Bắt đầu render Part 5...");
            setupPart(part5);
            await new Promise((r) => setTimeout(r, 1500));
            const buffer5 = await generatePdfBuffer(htmlElement, commonOptions);
            pdfBuffers.push(buffer5);
            console.log("Part 5 hoàn thành, size:", buffer5.byteLength);

            // Merge + download
            throwIfCancelled();
            setMessage(t('MergingAndDownloadingPDF'));
            if (pdfBuffers.length > 0) {
                console.log("Bắt đầu Merge PDF...");
                await mergeAndDownloadPdfs(pdfBuffers, "Metrix_Rule_Report.pdf");
                throwIfCancelled();
                setMessage(t('PDFGenerationCompleted'));
                setShowDownloadCompleteModal(true);
                console.log("Download hoàn tất!");
            }

        } catch (error) {
            if (error instanceof Error && error.message === 'PDF_DOWNLOAD_CANCELLED') {
                setShowDownloadCompleteModal(false);
                return;
            }
            console.error("Lỗi tạo/ghép PDF:", error);
            alert("Có lỗi xảy ra khi tạo PDF. Vui lòng thử lại.");
        } finally {
            if (htmlElement && styleBackup) {
                Object.assign(htmlElement.style, styleBackup);
            }
            setIsDown(false);
        }
    }

    return (
        <>
            <Helmet>
                <title>Metrix PDF Report</title>
            </Helmet>
            {isDown && (
                <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center px-4">
                    <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md text-center">
                        <div className="mx-auto mb-4 h-14 w-14 rounded-full border-4 border-gray-200 border-t-blue-600 animate-spin" />
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{t('Generating PDF...')}</h3>
                        <p className="text-sm text-gray-600">{t(message) || t('Please wait while we prepare your report.')}</p>
                        <button
                            onClick={cancelDownload}
                            className="mt-5 border border-gray-300 hover:bg-gray-100 text-gray-700 font-semibold py-2 px-5 rounded"
                        >
                            {t('Cancel')}
                        </button>
                    </div>
                </div>
            )}

            {showDownloadCompleteModal && (
                <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center px-4">
                    <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md text-center">
                        <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-3xl">✓</div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{t('Download completed')}</h3>
                        <p className="text-sm text-gray-600 mb-6">{t('Your Metrix PDF Report is ready.')}</p>
                        <button
                            onClick={goToHome}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-5 rounded"
                        >
                            {t('Go to home')}
                        </button>
                    </div>
                </div>
            )}

            {!isDown && (
                <div className="fixed top-16 right-5 z-50 flex items-center gap-4 p-4 rounded flex-col">
                    <button onClick={startDownload}
                        disabled={isDown}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
                    >
                        {isDown ? (
                            <>
                                <span className="loader mr-2"></span> {t('Generating PDF...')}
                            </>
                        ) : (
                            <div className="flex items-center gap-2">
                                <FileArrowDownIcon size={24} />
                                {t('Download Metrix PDF Report')}
                            </div>
                        )}
                    </button>
                    <div className="text-sm text-gray-600 w-48 text-center bg-gray-100 p-2 rounded">
                        {t(message)}
                    </div>
                </div>
            )}

            <div className="pdf-container">

                <div ref={printRef}
                    className="mx-auto bg-white"
                    style={{
                        width: '1122px',
                    }}
                >
                    <div className="w-full" id="part1">
                        {langSetting === 'vi' ? (
                            <div className="w-full" style={{ height: '1698px', overflow: 'hidden', backgroundColor: '#2B3F50', position: 'relative' }}>
                                <img src="/downPdf/firstpage.jpg" alt="" />
                                <div style={{ position: "absolute", top: "200px", width: "100%", textAlign: "center", color: "#fff", fontSize: "18px", }}>
                                    <span style={{ fontSize: "2rem", fontWeight: "bold" }}> {langSetting == "vi" ? user.last_name + " " + user.first_name : user.first_name + " " + user.last_name || ""} </span><br />
                                    <span style={{ fontSize: "1.75rem" }}>
                                        {dob || ""}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="w-full" style={{ height: '1698px', overflow: 'hidden', backgroundColor: '#2B3F50', position: 'relative' }}>
                                <img src="/downPdf/firstpageEn.jpg" alt="" />
                                <div style={{ position: "absolute", top: "200px", width: "100%", textAlign: "center", color: "#fff", fontSize: "18px", }}>
                                    <span style={{ fontSize: "2rem", fontWeight: "bold" }}> {langSetting == "vi" ? user.last_name + " " + user.first_name : user.first_name + " " + user.last_name || ""} </span><br />
                                    <span style={{ fontSize: "1.75rem" }}>
                                        {dob || ""}
                                    </span>
                                </div>
                            </div>
                        )}
                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-10 ">
                            <HeaderPart />

                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-8 text-3xl font-bold yellow-color'>👋 {t('Hi there')}</p>
                                <div className="flex flex-col gap-3 pt-10">
                                    <p className='text-2xl m-0 text-black-normal' style={{ lineHeight: '30px' }}>{t('The financial market has never been easy. Every trade is a combination of')} <strong>{t('knowledge, emotions')}</strong> {t('and')} <strong>{t('discipline')}</strong> – {t('and just one weak link can completely change the outcome.')}</p>
                                    <p className='text-2xl m-0 text-black-normal' style={{ lineHeight: '30px' }}>{t('Through years of observation, we have found that most failures do not come from choosing the wrong strategy, but from')} <strong>{t('the lack of a clear tracking and alert system')}</strong>. {t('Humans are naturally emotional; and when the market fluctuates, emotions often override rational judgment')}.</p>

                                    <p className='text-2xl m-0 text-black-normal' style={{ lineHeight: '30px' }}>{t('That is why we developed the')} <strong>{t('LUMIR Metrix Rule Report')}</strong> – {t('một cuốn')} <strong>{t('a playbook')}</strong> {t('that standardizes')} <strong>{t('33 LIVE METRIX indicators')}</strong> {t('into clear, visual, and actionable tools.')}</p>
                                    <p className='text-2xl m-0 text-black-normal' style={{ lineHeight: '30px' }}>

                                        {t('Each indicator is not just a dry number, but is associated with')} <strong>{t('Good/Warning/Danger thresholds')}</strong> {t('and')} <strong>{t('3 practical action suggestions')}</strong>, {t('helping you turn data into')} <strong>{t('timely decisions')}</strong>.
                                    </p>

                                    <p className='text-2xl m-0 pt-2 text-black-normal' style={{ lineHeight: '30px' }}>
                                        {t('This is not just a set of indicators, but a')} <strong>{t('trading playbook')}</strong> – {t('accompanying you on the journey of')} <strong>{t('risk management')}</strong>, {t('maintaining')} <strong>{t('discipline')}</strong> {t('and')} <strong>{t('profit optimization')}</strong>.
                                    </p>

                                    <p className='text-2xl m-0 text-black-normal' style={{ lineHeight: '30px' }}>
                                        {t('This is not a')} <strong>{t('“magic key”')}</strong> {t('that guarantees profit, but a')} <strong>{t('guiding map')}</strong>, {t('helping you navigate turbulent markets with')} <strong>{t('greater confidence')}</strong>.
                                    </p>

                                    <p className='text-2xl m-0 text-black-normal' style={{ lineHeight: '30px' }}>
                                        {t('We believe that with')} <strong>{t('discipline')}</strong> {t('and the right')} <strong>{t('measurement system')}</strong>, {t('traders can turn mistakes into lessons, numbers into power, and scattered trades into a')} <strong>{t('sustainable journey')}</strong>.
                                    </p>

                                    <p className='text-2xl m-0 text-black-normal' style={{ lineHeight: '30px' }}>
                                        {t('We hope you will embrace this material as a')} <strong>{t('compass')}</strong>, {t('so that each number is no longer dry, but becomes a signal that helps you make')} <strong>{t('more accurate decisions')}</strong>.
                                    </p>

                                    <p className='text-2xl m-0 text-black-normal' style={{ lineHeight: '30px' }}>
                                        <strong>{t('Metrix Rule Report')}</strong> – {t('The playbook for a sustainable trading journey')}.
                                    </p>

                                    <p className='text-2xl m-0 text-black-normal' style={{ lineHeight: '30px' }}>
                                        <strong>{t('Turn data into power')}</strong>
                                    </p>

                                    <p className='pt-10 text-2xl text-black-normal' style={{ lineHeight: '30px' }}>
                                        <strong>{t('Regards')}</strong>
                                    </p>

                                    <p className='text-2xl m-0 text-black-normal' style={{ lineHeight: '30px' }}>
                                        <strong>{t('LUMIR Trading Behavior Coaching Team (TBI Coach Team)')}</strong>
                                    </p>
                                </div>
                            </div>

                            <div className='px-20'>
                                <FooterPart page={1} />
                            </div>
                        </div>
                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12 ">
                            <HeaderPart />
                            <div style={{ height: '1384px', overflow: 'hidden' }}>
                                <div className="my-12 pt-0 flex flex-col items-center">
                                    <p className='m-0 font-bold text-5xl yellow-color whitespace-nowrap'>BeQ Ops Lumir</p>
                                    <p className='m-0 font-bold text-4xl pt-10 bold-blue-color whitespace-nowrap'>Metrix Rule Report / Playbook – v1</p>
                                    <p className='m-0 text-2xl pt-12 whitespace-nowrap text-black-normal'>
                                        <strong>{t('Metrix Rule Report')}</strong> – {t('The playbook for a sustainable trading journey')}.
                                    </p>
                                    <p className='m-0 text-[28px] font-bold pt-3 italic whitespace-nowrap bold-blue-color'>{t('Turn data into power')}</p>
                                </div>
                                <div className="px-20">
                                    <p className='m-0 mt-3 text-2xl text-black-normal' style={{ lineHeight: '30px' }}>
                                        <strong>80%</strong> {t('of traders fail not because of a lack of strategy, but because of a lack of discipline and an effective measurement system')}.
                                    </p>

                                    <p className='m-0 mt-3 text-2xl text-black-normal' style={{ lineHeight: '30px' }}>
                                        <strong>{t('Metrix Rule Report')}</strong> {t('by LUMIR was created to change that. With')} <strong>{t('33 standardized LIVE METRIX indicators')}</strong>, {t('you can monitor your trading health like a doctor reading test results')}.
                                    </p>

                                    <p className='m-0 mt-3 text-2xl text-black-normal' style={{ lineHeight: '30px' }}>
                                        {t('Each indicator comes with thresholds')} <strong>{t('Good/Warning/Danger')} </strong>
                                        {t('along with')} <strong>{t('3 suggested actions')} </strong>
                                        {t('for each level, helping you promptly identify risks, preserve capital, and optimize profits.')}
                                    </p>

                                    <p className='m-0 mt-3 text-2xl text-black-normal' style={{ lineHeight: '30px' }}>
                                        {t('This is not just a reference document, but a')} <strong>{t('practical playbook')}</strong>
                                        {t('– a compass to help you trade with discipline, intelligence, and sustainability.')}
                                    </p>

                                    <p className='m-0 mt-3 text-2xl text-black-normal' style={{ lineHeight: '30px' }}>
                                        {t('The structure of the')} <strong>{t('Metrix Rule')}</strong> {t('consists of')} <strong>{t('6 groups:')}</strong>
                                    </p>

                                    <ul className='text-2xl list-decimal mt-3 ml-16 text-black-normal'>
                                        <li className='font-bold list-decimal mt-3'>Financial</li>
                                        <li className='font-bold list-decimal mt-3'>Performance</li>
                                        <li className='font-bold list-decimal mt-3'>Trades</li>
                                        <li className='font-bold list-decimal mt-3'>Volume</li>
                                        <li className='font-bold list-decimal mt-3'>Behavioral</li>
                                        <li className='font-bold list-decimal mt-3'>Risk Management & Discipline</li>
                                    </ul>

                                    <p className='w-full h-0.5 mt-4 mb-4 bg-black-cts' />

                                    <p className='m-0 mt-3 text-2xl text-black-normal' style={{ lineHeight: '30px' }}>
                                        <strong>{t('Convention')}</strong>
                                    </p>

                                    <ul className='text-2xl list-disc mt-3 ml-16 text-black-normal'>
                                        <li className='list-disc mt-3'>
                                            <strong>{t('Direction:')}</strong> {t('Higher better / Lower better / Neutral (cao hơn tốt hơn / thấp hơn tốt hơn / trung tính).')}
                                        </li>
                                        <li className='list-disc mt-3'>
                                            <strong>{t('Ranges')}:</strong> ✅ Good | ⚠ Warning | ❌ Danger.
                                        </li>
                                        <li className='list-disc mt-3'>
                                            <strong>{t('Examples:')}</strong> {t('Value (example) to illustrate typical values.')}
                                        </li>
                                    </ul>

                                </div>
                            </div>
                            <div className='px-20'>
                                <FooterPart page={2} />
                            </div>
                        </div>
                        <div style={{ height: '1696px', width: '1200px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-18" style={{ height: '1380px', overflow: 'hidden' }}>
                                <div className='pdf-section'>
                                    <div className="rounded-1 bg-background-secondary fix-boder-head">
                                    </div>
                                </div>
                                <div className='wrap-box1'>
                                    {/* Row 1: Charts & Account Info */}
                                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                                        <div className="xl:col-span-2 bg-white rounded-xl shadow-premium border border-slate-100 p-6 flex flex-col relative overflow-hidden">
                                            <div className="flex justify-between items-center mb-6 relative z-10">
                                                <h2 className="!text-base font-bold !text-slate-900">Kết quả hiện tại</h2>
                                            </div>
                                            <div className="grid grid-cols-3 gap-4 mb-6 relative z-10">
                                                {[
                                                    { label: 'Số dư tài khoản', value: sectionChartData.currentBalance ? `$${sectionChartData.currentBalance.toLocaleString()}` : '$0.00' },
                                                    { label: 'Vốn chủ sở hữu', value: sectionChartData.equity ? `$${sectionChartData.equity.toLocaleString()}` : '$0.00', isEmpty: !sectionChartData.equity, color: sectionChartData.equity < 0 ? '!text-rose-600' : '!text-emerald-600' },
                                                    { label: 'Lãi/lỗ chưa thực hiện', value: sectionChartData.profitLoss ? `${sectionChartData.profitLoss > 0 ? `$${sectionChartData.profitLoss.toLocaleString()}` : `-$${Math.abs(sectionChartData.profitLoss).toLocaleString()}`}` : 'Không có dữ liệu', isEmpty: !sectionChartData.profitLoss, color: sectionChartData.profitLoss < 0 ? '!text-rose-600' : '!text-emerald-600' },
                                                ].map((kpi, idx) => (
                                                    <div key={idx}>
                                                        <div className="flex items-center gap-1 mb-2">
                                                            <p className="!text-sm !text-slate-600 font-medium">{kpi.label}</p>
                                                        </div>
                                                        <div className="bg-slate-100/80 rounded py-2 !text-center border border-slate-200/50">
                                                            {kpi.isEmpty ? (
                                                                <p className="!text-xs !m-0 font-medium !text-slate-400 py-1">{kpi.value}</p>
                                                            ) : (
                                                                <p className={`!text-[15px] !m-0 font-bold  font-mono-num tracking-tight ${kpi.color ? kpi.color : '!text-slate-900'}`}>{kpi.value}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="flex-1 min-h-[300px] relative w-full mt-2">
                                                <div className="absolute inset-0">
                                                    <MainAccountBalanceChart
                                                        data={sectionChartData.chartData.datasets}
                                                        labels={sectionChartData.chartData.labels}
                                                        chartPoints={sectionChartData.chartPoints}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right: Account Information */}
                                        <div className="bg-white rounded-xl shadow-premium border border-slate-100 p-6 flex flex-col">
                                            <h2 className="!text-base font-bold !text-slate-900 mb-6">Thông tin tài khoản</h2>
                                            <div className="flex-1 flex flex-col justify-between !text-sm">
                                                {[
                                                    { label: 'Kết quả:', value: 'Trong quá trình', type: 'badge', color: 'bg-teal-500' },
                                                    { label: 'Trạng thái:', value: report?.account_info?.account_status || 'N/A', type: 'mono' },
                                                    { label: 'Số tài khoản:', value: account_number || 'N/A', type: 'number' },
                                                    { label: 'Bắt đầu:', value: convertToLocalTime(report?.account_info?.created_at || '', 'DD/MM/YYYY') || 'N/A', type: 'text' },
                                                    { label: 'Số vốn ban đầu:', value: '$' + (report?.account_info?.initial_balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || report?.account_info?.initial_balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'), type: 'number' },
                                                    { label: 'Loại tài khoản:', value: report?.account_info?.broker?.toUpperCase() || 'N/A', type: 'text' },
                                                    {
                                                        label: 'Chỉ số căn chỉnh:',
                                                        value: (
                                                            <span className="flex items-center gap-1.5 cursor-pointer" >
                                                                DAI <strong className="font-bold text-blue-600">{personalNumbers?.personalDayNumber || 'N/A'}</strong>
                                                                MRI <strong className="font-bold text-blue-600">{personalNumbers?.personalMonthNumber || 'N/A'}</strong>
                                                                AMI <strong className="font-bold text-blue-600">{personalNumbers?.personalYearNumber || 'N/A'}</strong>
                                                            </span>
                                                        ),
                                                        type: 'info',
                                                        className: 'text-xs !text-slate-400',
                                                    },
                                                    { label: `Nền tảng ${platform?.toUpperCase() || 'N/A'}:`, value: 'Tải xuống', type: 'link' },
                                                ].map((item, idx) => (
                                                    <div key={idx}
                                                        className={`flex justify-between items-center py-3 ${idx !== 7 ? 'border-b border-slate-100' : 'pt-4'}`}>
                                                        <span className="!text-slate-500 font-medium">{item.label}</span>
                                                        {item.type === 'badge' ? (
                                                            <span className={`px-3 py-1 rounded-full !text-xs font-bold !text-white shadow-sm ${item.color}`}>{item.value}</span>
                                                        ) : item.type === 'mono' ? (
                                                            <span className="font-bold !text-slate-900 bg-slate-100 px-2 py-0.5 rounded !text-xs uppercase tracking-wider">{item.value}</span>
                                                        ) : item.type === 'bot' ? (
                                                            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded !text-xs font-bold bg-emerald-50 !text-emerald-600 border border-emerald-200 shadow-sm">
                                                                <span className="relative flex h-1.5 w-1.5">
                                                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                                                                </span>
                                                                {item.value}
                                                            </span>
                                                        ) : item.type === 'number' ? (
                                                            <span className="font-bold !text-slate-900 font-mono-num">{item.value}</span>
                                                        ) : item.type === 'link' ? (
                                                            <a href="#" className="font-medium !text-brand-600 hover:!text-brand-700 hover:underline">{item.value}</a>
                                                        ) : (
                                                            <span className="font-medium !text-slate-800">{item.value}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {report && (
                                    <TradingStatistics report={report} liveTrade={liveTrade} />
                                )}
                            </div>
                            <div className='px-20'>
                                <FooterPart page={3} />
                            </div>
                        </div>
                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12 ">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <div className="flex justify-center mt-10">
                                    <img src="/downPdf/icon1.png" className='w-40 h-40' alt="" />
                                </div>
                                <p className='font-bold text-5xl pt-8 text-center text-black-normal'>Financial Group</p>
                                <p className='pt-14 small-blue-title'>
                                    {t('Group 1: Financial – Measuring overall financial health')}
                                </p>
                                <p className='normal-text text-black-normal'>
                                    {t('The Financial group reflects the overall financial condition of the trading account. This is the core set of indicators that helps traders capture capital health, profitability, as well as the costs required to sustain market operations. When monitored and analyzed regularly, the Financial group serves as the foundation for all risk management and capital allocation decisions.')}
                                </p>

                                <p className='small-blue-title'>
                                    {t('Meaning for traders')}
                                </p>

                                <ul className='text-2xl list-disc ml-8 text-black-normal'>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        {t('Measure current capital health and account value fluctuations.')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        {t('Help traders clearly understand real net and gross profit.')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        {t('Detect the degree of profit “erosion” from trading costs.')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        {t('Detect the degree of profit “erosion” from trading costs.')}
                                    </li>
                                </ul>

                                <p className='small-blue-title'>
                                    {t('Main indicator categories')}
                                </p>
                                <ul className='text-2xl list-decimal mt-3 ml-8 text-black-normal'>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>Equity</strong> – {t('The actual capital value if all positions were closed immediately')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>Balance</strong> – {t('The account balance from closed positions, excluding unrealized profit/loss')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>Fees/Profit Ratio</strong> – {t('The ratio of trading fees to profit; measuring “profit erosion” from costs')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>Net Profit</strong> – {t('Net profit after deducting all fees and losses')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>Gross Profit</strong> – {t('Total gross profit before deducting fees and losses')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>Total Fees</strong> – {t('Total trading costs (spread, commission, swap…)')}
                                    </li>
                                </ul>

                                <p className='normal-text text-black-normal'>
                                    {t('The Financial group gives traders an overview of the account’s financial health. When combined with the Performance group, traders not only know whether their capital is safe but also understand whether the strategy they are using generates sustainable profits.')}
                                </p>

                            </div>
                            <div className='px-20'>
                                <FooterPart page={4} />
                            </div>
                        </div>
                        <div className="w-full p-12" style={{ height: '1696px', overflow: 'hidden' }}>
                            <HeaderPart />
                            <div className="pt-10" style={{ height: '1384px', overflow: 'hidden' }}>
                                <div className="w-full max-w-full flex justify-center">
                                    <DashboardCharts
                                        monthlyAnalysis={report?.time_analysis?.monthly_analysis || []}
                                        pdfShow
                                    />
                                </div>
                                <div className="w-full max-w-full flex justify-center">
                                    <DashboardCharts2
                                        dailyAnalysis={report?.time_analysis?.daily_analysis || []}
                                        pdfShow
                                    />
                                </div>
                                <div className="w-full max-w-full flex justify-center">
                                    <DashboardCharts3
                                        hourlyAnalysis={report?.time_analysis?.hourly_analysis || []}
                                        pdfShow
                                    />
                                </div>
                            </div>
                            <div className='px-20'>
                                <FooterPart page={5} />
                            </div>
                        </div>
                        <div className="w-full p-12" style={{ height: '1696px', overflow: 'hidden' }}>
                            <HeaderPart />
                            <div className="pt-2 px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <div className="p-3">
                                    <div className="flex flex-col gap-6 md:flex-row w-full flex-grow mt-6">
                                        {/* ConsistencyScore chỉ chiếm đúng chiều rộng tự nhiên, không bị méo */}
                                        <div className="w-full md:w-auto flex-shrink-0">
                                            <ConsistencyScore score={report?.consistency_score || 0} />
                                        </div>

                                        {/* AccountGoals sử dụng flex-1 để chiếm trọn phần không gian còn dư */}
                                        <div className="flex-1 w-full min-w-0">
                                            <AccountGoals report={report} />
                                        </div>
                                    </div>
                                    {report?.behavioral_analysis && (
                                        <BehavioralAnalysis report={report} />
                                    )}
                                </div>
                            </div>
                            <div className='px-20'>
                                <FooterPart page={6} />
                            </div>
                        </div>
                        <div className="w-full p-12" style={{ height: '1696px', overflow: 'hidden' }}>
                            <HeaderPart />
                            <div className="pt-10 px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='small-blue-title'>1. Equity</p>
                                <p className='normal-text text-black-normal'>
                                    {t('Equity represents the current account value if all open positions were closed immediately. This is the most important metric because it reflects the actual capital health.')}
                                </p>

                                <ul className='text-2xl list-disc ml-8 text-black-normal'>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}: </strong>
                                        {Number(((Number(report?.performance_summary?.current_equity) - report?.balance_init) / report?.balance_init) * 100).toFixed(2)}{t('% compared to the beginning')}
                                    </li>

                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Note:')}</strong> {t('Evaluate capital increase/decrease during the period (compared to opening equity)')}
                                    </li>

                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Direction:')}</strong> {t('Higher value is better - Higher better')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>
                                    <ul className='text-2xl list-decimal mt-3 ml-14 text-black-normal'>
                                        <li style={{ lineHeight: '48px' }}>
                                            ✅ Good: ≥ 0%
                                        </li>
                                        <li style={{ lineHeight: '48px' }}>
                                            ⚠ Warning: -0% to -5%
                                        </li>
                                        <li style={{ lineHeight: '48px' }}>
                                            ❌ Danger: &lt; -5%
                                        </li>
                                    </ul>
                                </ul>

                                {(() => {
                                    const equity = liveTrade?.data && liveTrade.data.length > 0 ? Number(liveTrade.data[0]?.equity) : 0;
                                    const percent = ((equity - report?.balance_init) / report?.balance_init) * 100;

                                    if (percent > 0) {
                                        return <p className='normal-text font-bold text-black-normal'>{t('Insight suggestion:')}</p>
                                    }
                                    else {
                                        return null
                                    }
                                })()}

                                <ul className="text-2xl ml-8 text-black-normal list-none" style={{ listStyle: 'none' }}>
                                    {(() => {
                                        const equity = liveTrade?.data && liveTrade.data.length > 0 ? Number(liveTrade.data[0]?.equity) : 0;
                                        const percent = ((equity - report?.balance_init) / report?.balance_init) * 100;

                                        if (percent > 0) {
                                            return (
                                                <li className="list-nont" style={{ lineHeight: '48px' }}>
                                                    <strong>✅ Good (≥ 0%): </strong>{t('Stable capital growth')}
                                                    <ul className="mt-3 ml-14 list-none" style={{ lineHeight: '48px' }}>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Maintain current strategy, keep risk ≤ 1%.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Reinvest part of profits to scale sustainably.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Journal best trades to replicate success.')}</li>
                                                    </ul>
                                                </li>
                                            );
                                        }

                                        if (percent < 0 && percent > -5) {
                                            return (
                                                <li className="list-none" style={{ lineHeight: '48px' }}>
                                                    <strong>⚠ Warning (-0% {t('to')} -5%): </strong>{t('Slight capital decrease')}
                                                    <ul className="mt-3 ml-14 list-none" style={{ lineHeight: '48px' }}>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Reduce trade size by 20–30%.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Review recent losing trades to adjust entry/exit points.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Pause new capital injection until recovery.')}</li>
                                                    </ul>
                                                </li>
                                            );
                                        }

                                        if (percent <= -5) {
                                            return (
                                                <li className="list-none mt-6" style={{ lineHeight: '48px' }}>
                                                    <strong>❌ Danger (&lt;-5%): </strong>{t('Significant capital drawdown > 5%.')}
                                                    <ul className="mt-3 ml-14 list-none" style={{ lineHeight: '48px' }}>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Activate capital preservation mode: cut size by 50%.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Stop experimental strategies, keep only core setup.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Immediately reassess risk management system.')}</li>
                                                    </ul>
                                                </li>
                                            );
                                        }

                                        return null;
                                    })()}
                                </ul>

                            </div>
                            <div className='px-20'>
                                <FooterPart page={7} />
                            </div>
                        </div>
                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='small-blue-title pt-10'>2. Balance</p>
                                <p className='normal-text text-black-normal'>
                                    Balance {t('represents the account balance after all closed trades (excluding P/L from open positions). This is used to compare with Equity to assess the impact of floating P/L.')}
                                </p>

                                <ul className='text-2xl list-disc ml-8 text-black-normal'>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}: </strong>{Number(((Number(report?.today_balance) - report?.balance_init) / report?.balance_init) * 100).toFixed(2)}% {t('compared to the beginning of the period')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Note:')}</strong> {t('Account balance after closing all trades (compared to opening balance).')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Direction:')}</strong> {t('Higher value is better')} - Higher better
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}><strong>{t('Ranges')}:</strong></li>
                                    <ul className='text-2xl list-decimal mt-3 ml-14'>
                                        <li style={{ lineHeight: '48px' }}>✅ Good: ≥ 0%</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ Warning: -0% {t('to')} -5%</li>
                                        <li style={{ lineHeight: '48px' }}>❌ Danger: &lt; -5%</li>
                                    </ul>
                                </ul>

                                <p className='normal-text font-bold text-black-normal'>
                                    Insight {t('suggestion')}:
                                </p>

                                <ul className="text-2xl ml-8 text-black-normal list-none" style={{ listStyle: 'none' }}>
                                    {(() => {
                                        const percent = ((Number(report?.today_balance) - report?.balance_init) / report?.balance_init) * 100;

                                        if (percent > 0) {
                                            return (
                                                <li className="list-none" style={{ lineHeight: '48px', listStyle: 'none' }}>
                                                    <strong>✅ Good (≥ 0%): </strong>{t('Account balance is growing well.')}
                                                    <ul className="mt-3 ml-14 list-none" style={{ lineHeight: '48px' }}>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Account balance is growing well.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Consider tracking capital growth weekly/monthly.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Note periods of favorable market conditions.')}</li>
                                                    </ul>
                                                </li>
                                            );
                                        }

                                        if (percent < 0 && percent > -5) {
                                            return (
                                                <li className="list-none" style={{ lineHeight: '48px', listStyle: 'none' }}>
                                                    <strong>⚠ Warning (-0% {t('to')} -5%): </strong>{t('Balance decreased slightly.')}
                                                    <ul className="mt-3 ml-14 list-none" style={{ lineHeight: '48px' }}>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Identify the cause: fees, drawdown, or losing streak.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Limit daily trades to control losses.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Focus on high Win Rate strategies.')}</li>
                                                    </ul>
                                                </li>
                                            );
                                        }

                                        if (percent <= -5) {
                                            return (
                                                <li className="list-none mt-6" style={{ lineHeight: '48px', listStyle: 'none' }}>
                                                    <strong>❌ Danger (&lt;-5%): </strong>{t('Balance dropped sharply.')}
                                                    <ul className="mt-3 ml-14 list-none" style={{ lineHeight: '48px' }}>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Stop trading immediately for 1–2 days.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Review top 10 losing trades to find system errors.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Replan capital with max allowable DD ≤ 20%.')}</li>
                                                    </ul>
                                                </li>
                                            );
                                        }

                                        return null;
                                    })()}
                                </ul>

                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={8} />
                            </div>
                        </div>
                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />

                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 small-blue-title'>3. Net Profit</p>
                                <p className='normal-text text-black-normal'>
                                    {t('Net Profit is the total net profit after deducting all fees and losses. This is the ultimate measure of trading performance over a specific period.')}
                                </p>
                                <ul className='text-2xl list-disc ml-8 text-black-normal'>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}: </strong>{report?.performance_summary?.net_profit}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Note:')}</strong>{t('Net profit during the period (preferably positive).')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Direction:')} </strong>{t('Higher value is better')} - Higher better
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}><strong>{t('Ranges')}: </strong></li>
                                    <ul className='text-2xl list-decimal mt-3 ml-14'>
                                        <li style={{ lineHeight: '48px' }}>✅ Good: ≥ 0%</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ Warning: -2% equity {t('to')} &lt; 0</li>
                                        <li style={{ lineHeight: '48px' }}>❌ Danger: ≤ -2% equity</li>
                                    </ul>
                                </ul>
                                {(() => {
                                    const percent = Number(((Number(report?.performance_summary?.net_profit) + report?.balance_init) / report?.balance_init) * 100);

                                    if (percent >= 0) {
                                        return <p className='normal-text font-bold text-black-normal'>{t('Insight suggestion:')}</p>
                                    }
                                    else {
                                        return null
                                    }
                                })()}
                                <ul
                                    className="list-none pl-6 mb-6 text-2xl text-black-normal"
                                    style={{ lineHeight: '34px' }}
                                >
                                    {Number(((Number(report?.performance_summary?.net_profit) + report?.balance_init) / report?.balance_init) * 100) >= 0 && (
                                        <li>
                                            <span className="font-bold">✅ {t('Good (≥ 0%)')}:</span>{" "}
                                            <span>{t('Profit is stable.')}</span>
                                            <ul
                                                className="list-none pl-6 mt-2 space-y-1"
                                                style={{ lineHeight: '34px' }}
                                            >
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Maintain strategy with PF ≥ 1.5.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Log detailed winning trades for replication.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Reinvest part of the profit.')}</li>
                                            </ul>
                                        </li>
                                    )}

                                    {Number(((Number(report?.performance_summary?.net_profit) + report?.balance_init) / report?.balance_init) * 100) > -2 &&
                                        Number(((Number(report?.performance_summary?.net_profit) + report?.balance_init) / report?.balance_init) * 100) < 0 && (
                                            <li>
                                                <span className="font-bold">⚠ {t('Warning (-2% equity to < 0)')}:</span>{" "}
                                                <span>{t('Profit decreased slightly (near breakeven).')}</span>
                                                <ul
                                                    className="list-none pl-6 mt-2 space-y-1"
                                                    style={{ lineHeight: '34px' }}
                                                >
                                                    <li style={{ paddingLeft: '1rem' }}>_ {t('Limit experimental trades.')}</li>
                                                    <li style={{ paddingLeft: '1rem' }}>_ {t('Reduce volume until recovery expectation.')}</li>
                                                    <li style={{ paddingLeft: '1rem' }}>_ {t('Reduce volume until recovery expectation.')}</li>
                                                </ul>
                                            </li>
                                        )}

                                    {Number(((Number(report?.performance_summary?.net_profit) + report?.balance_init) / report?.balance_init) * 100) <= -2 && (
                                        <li>
                                            <span className="font-bold">❌ {t('Danger (≤ -2% equity)')}:</span>{" "}
                                            <span>{t('Significant loss.')}</span>
                                            <ul
                                                className="pl-6 mt-2 space-y-1"
                                                style={{ lineHeight: '34px', listStyleType: 'none' }}
                                            >
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Activate risk reduction mode (cut 50% volume).')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Stop non-core strategies.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Coaching or review the system.')}</li>
                                            </ul>
                                        </li>
                                    )}
                                </ul>

                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={9} />
                            </div>
                        </div>
                    </div>

                    <div className="w-full" id="part2">
                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 small-blue-title'>4. <strong>{t('Gross Profit')}</strong></p>
                                <p className='normal-text text-black-normal'>
                                    <strong>{t('Gross Profit')}</strong> {t('is the total gross profit before deducting fees and losses. This indicator shows')} <strong>{t('the raw profit potential')}</strong> {t('of the strategy, helping traders distinguish between strategy efficiency and incurred costs.')}
                                </p>
                                <ul className='text-2xl list-disc ml-8 text-black-normal'>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {Number(report?.performance_summary?.gross_profit)}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Total profit before deducting losses/fees.')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Higher better')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}><strong>{t('Ranges')}:</strong></li>
                                    <ul className='text-2xl list-decimal mt-3 ml-14'>
                                        <li style={{ lineHeight: '48px' }}>✅ Good: ≥ 0</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ Warning: = 0</li>
                                        <li style={{ lineHeight: '48px' }}>❌ Danger: &lt; 0</li>
                                    </ul>
                                </ul>
                                {(() => {
                                    const percent = Number(((Number(report?.performance_summary?.gross_profit) + report?.balance_init) / report?.balance_init) * 100);

                                    if (percent > 0) {
                                        return <p className='normal-text font-bold text-black-normal'>{t('Insight suggestion:')}</p>
                                    }
                                    else {
                                        return null
                                    }
                                })()}
                                <ul
                                    className="list-none pl-6 mb-6 text-2xl text-black-normal"
                                    style={{ lineHeight: '34px', listStyleType: 'none' }}
                                >
                                    {Number(((Number(report?.performance_summary?.gross_profit) + report?.balance_init) / report?.balance_init) * 100) > 0 && (
                                        <li>
                                            <span className="font-bold">✅ {t('Good (≥ 0%)')}:</span>{" "}
                                            <span>{t('Gross profit is positive.')}</span>
                                            <ul
                                                className="list-none pl-6 mt-2 space-y-1"
                                                style={{ lineHeight: '34px', listStyleType: 'none !important' }}
                                            >
                                                <li style={{ paddingLeft: '1rem' }}>_  {t('Maintain current risk management.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_  {t('Increase volume gradually when equity rises.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_  {t('Monitor fees/profit ratio.')}</li>
                                            </ul>
                                        </li>
                                    )}

                                    {Number(((Number(report?.performance_summary?.gross_profit) + report?.balance_init) / report?.balance_init) * 100) === 0 && (
                                        <li>
                                            <span className="font-bold">⚠ {t('Warning (= 0)')}:</span>{" "}
                                            <span>{t('Break-even.')}</span>
                                            <ul
                                                className="list-none pl-6 mt-2 space-y-1"
                                                style={{ lineHeight: '34px', listStyleType: 'none' }}
                                            >
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Pause strategy expansion.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Reassess RR for each setup.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Prioritize periods with high liquidity.')}</li>
                                            </ul>
                                        </li>
                                    )}

                                    {Number(((Number(report?.performance_summary?.gross_profit) + report?.balance_init) / report?.balance_init) * 100) < 0 && (
                                        <li>
                                            <span className="font-bold">❌ {t('Danger (< 0)')}:</span>{" "}
                                            <span>{t('Gross loss.')}</span>
                                            <ul
                                                className="list-none pl-6 mt-2 space-y-1"
                                                style={{ lineHeight: '34px', listStyleType: 'none' }}
                                            >
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Stop trading immediately.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Review strategy in backtest/demo.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Eliminate ineffective setup.')}</li>
                                            </ul>
                                        </li>
                                    )}
                                </ul>

                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={10} />
                            </div>
                        </div>
                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 small-blue-title'>5. <strong>{t('Total Fees')}</strong></p>
                                <p className='normal-text text-black-normal'>
                                    <strong>{t('Total Fees')}</strong> {t('is the sum of all trading costs')} (spread, commission, swap…). {t('This is the “price to pay” to operate in the market and directly affects net profit.')}
                                </p>
                                <ul className='text-2xl list-disc ml-8 text-black-normal'>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {Number(report?.performance_summary?.total_fees)} ({t('accounts for')} {Number(Number(report?.performance_summary?.total_fees) / Number(report?.performance_summary?.gross_profit) * 100).toFixed(2)}% {t('Gross Profit')})
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Total cost:')} commission, spread, swap.
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Lower better')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}><strong>{t('Ranges')}:</strong></li>
                                    <ul className='text-2xl list-decimal mt-3 ml-14'>
                                        <li style={{ lineHeight: '48px' }}>✅ Good: ≤ 15% {t('Gross Profit')}</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ Warning: &gt;15% – 25%</li>
                                        <li style={{ lineHeight: '48px' }}>❌ Danger: &gt;25%</li>
                                    </ul>
                                </ul>

                                <p className='normal-text font-bold text-black-normal'>{t('Insight')} {t('suggestion')}:</p>
                                {/* Good */}
                                {(Number(Number(report?.performance_summary?.total_fees) / Number(report?.performance_summary?.gross_profit)) * 100) <= 15 && (
                                    <ul
                                        className="list-none pl-6 mb-6 text-2xl text-black-normal"
                                        style={{ lineHeight: '34px' }}
                                    >
                                        <li>
                                            <span className="font-bold">✅ {t('Good (≤ 15% Gross Profit):')}</span>{" "}
                                            <span>{t('Trading cost is reasonable.')}</span>
                                            <ul
                                                className="list-none pl-6 mt-2 space-y-1"
                                                style={{ lineHeight: '34px' }}
                                            >
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Maintain current broker/timeframe.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Prioritize fewer but higher-quality trades.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Continue controlling unnecessary small trades.')}</li>
                                            </ul>
                                        </li>
                                    </ul>
                                )}

                                {/* Warning */}
                                {(Number(Number(report?.performance_summary?.total_fees) / Number(report?.performance_summary?.gross_profit)) * 100) > 15 &&
                                    (Number(Number(report?.performance_summary?.total_fees) / Number(report?.performance_summary?.gross_profit)) * 100) < 25 && (
                                        <ul
                                            className="list-disc pl-6 mb-6 text-2xl text-black-normal"
                                            style={{ lineHeight: '34px' }}
                                        >
                                            <li>
                                                <span className="font-bold">⚠ {t('Warning (>15% – 25%):')}</span>{" "}
                                                <span>{t('Costs are starting to get high.')}</span>
                                                <ul
                                                    className="list-decimal pl-6 mt-2 space-y-1"
                                                    style={{ lineHeight: '34px' }}
                                                >
                                                    <li style={{ paddingLeft: '1rem' }}>_ {t('Reduce small trades, focus on A+ setup.')}</li>
                                                    <li style={{ paddingLeft: '1rem' }}>_ {t('Reassess broker, spread, swap.')}</li>
                                                    <li style={{ paddingLeft: '1rem' }}>_ {t('Avoid trading low-liquidity sessions.')}</li>
                                                </ul>
                                            </li>
                                        </ul>
                                    )}

                                {/* Danger */}
                                {(Number(Number(report?.performance_summary?.total_fees) / Number(report?.performance_summary?.gross_profit)) * 100) >= 25 && (
                                    <ul
                                        className="list-disc pl-6 mb-6 text-2xl text-black-normal"
                                        style={{ lineHeight: '34px' }}
                                    >
                                        <li>
                                            <span className="font-bold">❌ {t('Danger (>25%):')}</span>{" "}
                                            <span>{t('Costs exceed threshold (>25%).')}</span>
                                            <ul
                                                className="list-decimal pl-6 mt-2 space-y-1"
                                                style={{ lineHeight: '34px' }}
                                            >
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Renegotiate fees with broker or switch broker.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Cut unnecessary trades drastically.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Review scalping strategy with high costs.')}</li>
                                            </ul>
                                        </li>
                                    </ul>
                                )}

                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={11} />
                            </div>
                        </div>
                        <div className={`w-full p-12`} style={{ height: '1696px', overflow: 'hidden' }}>
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 small-blue-title'>{t('6. Fees/Profit Ratio')}</p>
                                <p className='normal-text text-black-normal'>
                                    {t('Fees/Profit Ratio is the ratio between')} <strong>{t('total trading costs')}</strong> {t('and')} <strong>{t('total profit')}</strong>. {t('This indicates the level of profit erosion due to fees.')} {t('A lower ratio means fees affect performance less; a high ratio warns that the strategy may be consuming too much in fees relative to profit.')}
                                </p>
                                <ul className='text-2xl list-disc ml-8 text-black-normal'>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}><strong>{t('Lebel:')}</strong> {t('Fees/Profit Ratio')}</li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Ratio of fees to gross profit.')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}><strong>{t('Value')}:</strong> {Number(Number(Number(report?.performance_summary?.total_fees) / Number(report?.performance_summary?.net_profit)) * 100).toFixed(2)}%</li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Lower better')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>
                                    <ul className='text-2xl list-decimal mt-3 ml-14'>
                                        <li style={{ lineHeight: '47px' }}>✅ Good: ≤ 15%</li>
                                        <li style={{ lineHeight: '47px' }}>⚠ Warning: &gt;15% – 25%</li>
                                        <li style={{ lineHeight: '47px' }}>❌ Danger: &gt;25%</li>
                                    </ul>
                                </ul>
                                <p className='normal-text font-bold text-black-normal'>{t('Insight')} {t('suggestion')}:</p>

                                {(() => {
                                    const totalFees = Number(report?.performance_summary?.total_fees || 0)
                                    const netProfit = Number(report?.performance_summary?.net_profit || 0)
                                    const feeRate = netProfit !== 0 ? (totalFees / netProfit) * 100 : 0

                                    return (
                                        <ul className="list-none pl-6 mb-6 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                            {/* Good */}
                                            {feeRate <= 15 && (
                                                <li>
                                                    <span className="font-bold">✅ {t('Good (≤ 15%):')}</span>{" "}
                                                    <span>{t('Trading costs are under control.')}</span>
                                                    <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Maintain brokers and high-liquidity sessions.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Focus on high-quality setups (A+).')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Continue controlling small trades to limit fees.')}</li>
                                                    </ul>
                                                </li>
                                            )}

                                            {/* Warning */}
                                            {feeRate > 15 && feeRate < 25 && (
                                                <li>
                                                    <span className="font-bold">⚠ {t('Warning (> 15% – 25%):')}</span>{" "}
                                                    <span>{t('Costs are starting to rise, affecting profit.')}</span>
                                                    <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Review entry frequency, reduce overtrading.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Avoid trading sessions with high spread (Asia/late sessions).')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Renegotiate broker fees or consider changing fee plans.')}</li>
                                                    </ul>
                                                </li>
                                            )}

                                            {/* Danger */}
                                            {feeRate >= 25 && (
                                                <li>
                                                    <span className="font-bold">❌ {t('Danger (> 25%):')}</span>{" "}
                                                    <span>{t('Costs take too much share, eroding profit.')}</span>
                                                    <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Cut scalping/overly small trade strategies.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Switch broker or use lower-fee account.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Rebuild strategy to focus on fewer trades but higher quality to reduce fees per lot.')}</li>
                                                    </ul>
                                                </li>
                                            )}
                                        </ul>
                                    )
                                })()}


                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={12} />
                            </div>
                        </div>
                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <div className="mt-10 flex justify-center">
                                    <img src="/downPdf/icon2.png" className='w-40 h-40' alt="" />
                                </div>
                                <p className='font-bold text-5xl pt-8 text-center text-black-normal'>{t('Performance Group')}</p>
                                <p className='pt-14 small-blue-title'>{t('Group 2: Performance – Measuring strategy quality and effectiveness')}</p>
                                <p className='normal-text text-black-normal'>
                                    {t('The Performance group reflects the stability, expected profit, and risk–reward efficiency of the trading system.')}
                                    {t('This group of metrics is crucial for traders to know whether their strategy is reliable or just luck in the short term.')}
                                    {t('When monitored regularly, the Performance group helps detect early signs of declining performance, allowing traders to adjust entry/exit points, position size, and trading frequency in time.')}
                                </p>
                                <p className='small-blue-title'>{t('Significance for traders')}</p>
                                <ul className='text-2xl list-disc ml-8 text-black-normal'>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>{t('Assess the long-term profitability of the strategy.')}</li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>{t('Detect signs of instability or performance decline.')}</li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>{t('Help fine-tune entry/exit points and trade volume to optimize results.')}</li>
                                    <li className='list-disc' style={{ lineHeight: '48px' }}>{t('Help fine-tune entry/exit points and trade volume to optimize results.')}</li>
                                </ul>
                                <p className='small-blue-title'>{t('Main indicators')}</p>
                                <ul className='text-2xl list-decimal mt-3 ml-8 text-black-normal'>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>Win Rate</strong> – {t('Percentage of winning trades out of total trades.')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>Average Profit</strong> – {t('Average profit per winning trade.')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>Average Loss</strong> – {t('Average loss per losing trade.')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>Expectancy</strong> – {t('Average expected profit per trade, considering win rate and profit/loss.')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>Profit Factor</strong> – {t('Ratio of total profit to total loss.')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>Sharpe Ratio</strong> – {t('Profit adjusted for risk (profit per unit of volatility).')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>Risk-Reward Ratio (RRR)</strong> – {t('Compare the average risk on losing trades to the average profit on winning trades.')}
                                    </li>
                                    <li style={{ lineHeight: '40px' }}>
                                        <strong>Win/Loss Ratio</strong> – {t('Average profit of winning trades compared to average loss of losing trades.')}
                                    </li>
                                </ul>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={13} />
                            </div>
                        </div>
                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='small-blue-title pt-6'>1. <strong>Win Rate</strong></p>
                                <p className='normal-text text-black-normal'>
                                    {t('Win Rate is the percentage of winning trades out of total trades.')}
                                    {t('This metric is easy to understand but can be misleading: a high win rate does not guarantee profit if losses are too large.')}
                                </p>
                                <ul className='text-2xl list-disc ml-8 text-black-normal'>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Note:')}</strong> {t('Percentage of winning trades out of total trades.')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>Value: </strong>{(Number(report?.win_rate) * 100).toFixed(2)}%
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Trend:')}</strong> {t('Higher value is better')} - Higher better
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Threshold')}</strong>
                                    </li>
                                    <ul className='text-2xl list-decimal mt-3 ml-14'>
                                        <li style={{ lineHeight: '47px' }}>
                                            ✅ Good: ≥ 55%
                                        </li>
                                        <li style={{ lineHeight: '47px' }}>
                                            ⚠ Warning: 45% – &lt; 55%
                                        </li>
                                        <li style={{ lineHeight: '47px' }}>
                                            ❌ Danger: &lt; 45%
                                        </li>
                                    </ul>
                                </ul>
                                {(() => {
                                    const winRate = Number(report?.win_rate || 0) * 100

                                    if (winRate >= 55) {
                                        return <p className='normal-text font-bold text-black-normal'>{t('Insight suggestion:')}</p>
                                    }
                                    else {
                                        return null
                                    }
                                })()}
                                {(() => {
                                    const winRate = Number(report?.win_rate || 0) * 100

                                    if (winRate >= 55) {
                                        return (
                                            <ul
                                                className="list-none pl-6 mb-6 text-2xl text-black-normal"
                                                style={{ lineHeight: '34px' }}
                                            >
                                                <li>
                                                    <span className="font-bold">✅ {t('Good (≥ 55%):')}</span>{' '}
                                                    <span>{t('Win Rate is stable.')}</span>
                                                    <ul
                                                        className="list-none pl-6 mt-2 space-y-1"
                                                        style={{ lineHeight: '34px' }}
                                                    >
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Maintain checklist before entering trades.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Focus on A+ setups.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Record successful patterns for replication.')}</li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        )
                                    }

                                    if (winRate < 55 && winRate > 45) {
                                        return (
                                            <ul
                                                className="list-none pl-6 mb-6 text-2xl text-black-normal"
                                                style={{ lineHeight: '34px' }}
                                            >
                                                <li>
                                                    <span className="font-bold">⚠ {t('Warning (45% – < 55%):')}</span>{' '}
                                                    <span>{t('Slight decrease in win rate.')}</span>
                                                    <ul
                                                        className="list-none pl-6 mt-2 space-y-1"
                                                        style={{ lineHeight: '34px' }}
                                                    >
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Review last 20 trades to find the cause.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Reduce test trades.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Adjust exit points to improve Win Rate.')}</li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        )
                                    }

                                    if (winRate <= 45) {
                                        return (
                                            <ul
                                                className="list-none pl-6 mb-6 text-2xl text-black-normal"
                                                style={{ lineHeight: '34px' }}
                                            >
                                                <li>
                                                    <span className="font-bold">❌ {t('Danger (< 45%):')}</span>{' '}
                                                    <span>{t('Low Win Rate.')}</span>
                                                    <ul
                                                        className="list-none pl-6 mt-2 space-y-1"
                                                        style={{ lineHeight: '34px' }}
                                                    >
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Temporarily stop real account trading.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Backtest the strategy.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Reduce trade size to limit losses.')}</li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        )
                                    }

                                    return null
                                })()}

                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={14} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 small-blue-title pt-6'>2. {t('Average Profit')}</p>
                                <p className='normal-text text-black-normal'>
                                    {t('Average Profit đo lường lợi nhuận trung bình trên mỗi lệnh thắng. Nó cho thấy chiến lược có tạo ra “cú ăn dày” hay chỉ lợi nhuận nhỏ lẻ.')}
                                </p>
                                <ul className='text-2xl list-disc ml-8 text-black-normal'>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Lợi nhuận trung bình mỗi lệnh thắng (so với độ lớn lỗ trung bình).')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Value')}:</strong> +{report?.performance_summary?.avg_profit_per_win} USD (Avg Loss = {report?.performance_summary?.avg_loss_per_loss} USD → {Number(Number(report?.performance_summary?.avg_profit_per_win) / (Number(report?.performance_summary?.avg_loss_per_loss) * -1)).toFixed(2)}x)
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng cao càng tốt - Higher better')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>
                                    <ul className='text-2xl list-decimal mt-3 ml-14'>
                                        <li style={{ lineHeight: '47px' }}>✅ Good: ≥ 1.5 × Avg Loss</li>
                                        <li style={{ lineHeight: '47px' }}>⚠ Warning: 1.0 – &lt; 1.5 × Avg Loss</li>
                                        <li style={{ lineHeight: '47px' }}>❌ Danger: &lt; 1.0 × Avg Loss</li>
                                    </ul>
                                </ul>

                                <p className='normal-text font-bold text-black-normal'>{t('Insight gợi ý:')}</p>

                                {(() => {
                                    const avgWin = Number(report?.performance_summary?.avg_profit_per_win || 0)
                                    const avgLoss = Number(report?.performance_summary?.avg_loss_per_loss || 0) * -1
                                    const ratio = avgLoss ? avgWin / avgLoss : 0

                                    if (ratio >= 1.5) {
                                        return (
                                            <ul
                                                className="list-none pl-6 mb-6 text-2xl text-black-normal"
                                                style={{ lineHeight: '34px' }}
                                            >
                                                <li>
                                                    <span className="font-bold">✅ {t('Good (≥ 1.5 × Avg Loss):')}</span>{' '}
                                                    <span>{t('Lãi trung bình cao hơn lỗ trung bình.')}</span>
                                                    <ul
                                                        className="list-none pl-6 mt-2 space-y-1"
                                                        style={{ lineHeight: '34px' }}
                                                    >
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Duy trì mức TP hiện tại.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Nhật ký các lệnh lời lớn để nhân bản.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Tăng trưởng bền vững với RR tốt.')}</li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        )
                                    }

                                    if (ratio < 1.5 && ratio > 1.0) {
                                        return (
                                            <ul
                                                className="list-none pl-6 mb-6 text-2xl text-black-normal"
                                                style={{ lineHeight: '34px' }}
                                            >
                                                <li>
                                                    <span className="font-bold">
                                                        ⚠ {t('Warning (1.0 – < 1.5 × Avg Loss):')}
                                                    </span>{' '}
                                                    <span>{t('Lãi trung bình chỉ ngang ngửa lỗ trung bình.')}</span>
                                                    <ul
                                                        className="list-none pl-6 mt-2 space-y-1"
                                                        style={{ lineHeight: '34px' }}
                                                    >
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Nâng TP tối thiểu ≥ 1.5R.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Tránh chốt lời non.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Tập trung vào setup có biên lợi nhuận rõ ràng.')}</li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        )
                                    }

                                    if (ratio <= 1.0) {
                                        return (
                                            <ul
                                                className="list-none pl-6 mb-6 text-2xl text-black-normal"
                                                style={{ lineHeight: '34px' }}
                                            >
                                                <li>
                                                    <span className="font-bold">❌ {t('Danger (< 1.0 × Avg Loss):')}</span>{' '}
                                                    <span>{t('Lãi trung bình thấp hơn lỗ trung bình.')}</span>
                                                    <ul
                                                        className="list-none pl-6 mt-2 space-y-1"
                                                        style={{ lineHeight: '34px' }}
                                                    >
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Ngưng chiến lược hiện tại.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Rà soát lại setup để tăng TP.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Dùng trailing stop để bảo vệ lãi.')}</li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        )
                                    }

                                    return null
                                })()}

                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={15} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 small-blue-title pt-6'>3. {t('Average Loss')}</p>
                                <p className='normal-text text-black-normal'>
                                    {t('Average Loss là mức lỗ trung bình trên mỗi lệnh thua. Khi so sánh với Average Profit, trader có thể đánh giá mức cân bằng giữa rủi ro và lợi nhuận.')}
                                </p>
                                <ul className='text-2xl list-disc ml-8 text-black-normal'>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Mức lỗ trung bình mỗi lệnh thua (so với lãi trung bình).')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Value')}:</strong> {report?.performance_summary?.avg_loss_per_loss} USD (Avg Profit = +{report?.performance_summary?.avg_profit_per_win} USD → {Number(Number(report?.performance_summary?.avg_loss_per_loss) / Number(report?.performance_summary?.avg_profit_per_win)).toFixed(2)}x)
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng thấp càng tốt - Lower better')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>
                                    <ul className='text-2xl list-decimal mt-3 ml-14'>
                                        <li style={{ lineHeight: '47px' }}>✅ Good: ≤ -0.67 × Avg Profit</li>
                                        <li style={{ lineHeight: '47px' }}>⚠ Warning: -0.67 {t('to')} -1.0 × Avg Profit</li>
                                        <li style={{ lineHeight: '47px' }}>❌ Danger: &lt; -1.0 × Avg Profit</li>
                                    </ul>
                                </ul>

                                <p className='normal-text font-bold text-black-normal'>{t('Insight gợi ý:')}</p>

                                {(() => {
                                    const avgLoss = Number(report?.performance_summary?.avg_loss_per_loss || 0)
                                    const avgWin = Number(report?.performance_summary?.avg_profit_per_win || 0)
                                    const ratio = avgWin ? avgLoss / avgWin : 0

                                    if (ratio >= -0.67) {
                                        return (
                                            <ul
                                                className="list-none pl-6 mb-6 text-2xl text-black-normal"
                                                style={{ lineHeight: '34px' }}
                                            >
                                                <li>
                                                    <span className="font-bold">✅ {t('Good (≤ -0.67 × Avg Profit):')}</span>{' '}
                                                    <span>{t('Lỗ trung bình nhỏ hơn lãi trung bình.')}</span>
                                                    <ul
                                                        className="list-none pl-6 mt-2 space-y-1"
                                                        style={{ lineHeight: '34px' }}
                                                    >
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Giữ nguyên nguyên tắc RR ≥ 1.5.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Duy trì SL hợp lý theo setup.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Ghi chú mẫu lệnh lỗ ít để học.')}</li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        )
                                    }

                                    if (ratio > -1 && ratio < -0.67) {
                                        return (
                                            <ul
                                                className="list-none pl-6 mb-6 text-2xl text-black-normal"
                                                style={{ lineHeight: '34px' }}
                                            >
                                                <li>
                                                    <span className="font-bold">
                                                        ⚠ {t('Warning (-0.67 đến -1.0 × Avg Profit):')}
                                                    </span>{' '}
                                                    <span>{t('Lỗ trung bình gần bằng lãi trung bình.')}</span>
                                                    <ul
                                                        className="list-none pl-6 mt-2 space-y-1"
                                                        style={{ lineHeight: '34px' }}
                                                    >
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Điều chỉnh SL để giảm mức lỗ.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Cải thiện entry để tránh false break.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Giới hạn chuỗi lỗ liên tiếp.')}</li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        )
                                    }

                                    if (ratio <= -1) {
                                        return (
                                            <ul
                                                className="list-none pl-6 mb-6 text-2xl text-black-normal"
                                                style={{ lineHeight: '34px' }}
                                            >
                                                <li>
                                                    <span className="font-bold">❌ {t('Danger (< -1.0 × Avg Profit):')}</span>{' '}
                                                    <span>{t('Lỗ trung bình lớn hơn lãi trung bình.')}</span>
                                                    <ul
                                                        className="list-none pl-6 mt-2 space-y-1"
                                                        style={{ lineHeight: '34px' }}
                                                    >
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Ngưng giao dịch.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Review toàn bộ SL placement.')}</li>
                                                        <li style={{ paddingLeft: '1rem' }}>_ {t('Backtest lại hệ thống quản trị lệnh.')}</li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        )
                                    }

                                    return null
                                })()}

                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={16} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 small-blue-title pt-6'>4. {t('Expectancy ($/trade)')}</p>
                                <p className='normal-text text-black-normal'>
                                    {t('Expectancy thể hiện lợi nhuận kỳ vọng trung bình cho mỗi giao dịch, tính đến cả tỷ lệ thắng và mức lời/lỗ. Đây là “thước đo vàng” cho biết nếu lặp lại chiến lược nhiều lần, trader có khả năng lời hay lỗ.')}
                                </p>
                                <ul className='text-2xl list-disc ml-8 text-black-normal'>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Giá trị kỳ vọng lợi nhuận trên mỗi lệnh.')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Value')}:</strong> {Number(report?.expectancy).toFixed(2)} USD
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Direction:')}</strong> {t('Higher better')}
                                    </li>
                                    <li className='list-disc' style={{ lineHeight: '47px' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>
                                    <ul className='text-2xl list-decimal mt-3 ml-14'>
                                        <li style={{ lineHeight: '47px' }}>✅ Good: ≥ 5</li>
                                        <li style={{ lineHeight: '47px' }}>⚠ Warning: 0 – &lt; 5</li>
                                        <li style={{ lineHeight: '47px' }}>❌ Danger: &lt; 0</li>
                                    </ul>
                                </ul>

                                <p className='normal-text font-bold text-black-normal'>{t('Insight gợi ý:')}</p>

                                {/* Expectancy Analysis */}
                                <ul className="list-none pl-6 mb-6 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                    {/* Good */}
                                    {Number(report?.expectancy) >= 5 && (
                                        <li>
                                            <span className="font-bold">✅ {t('Good (≥ 5):')}</span>{" "}
                                            <span>{t('Mỗi lệnh kỳ vọng dương.')}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Duy trì chiến lược hiện tại.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Mở rộng giao dịch với mẫu hình hiệu quả.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Giữ risk per trade ≤ 1%.')}</li>
                                            </ul>
                                        </li>
                                    )}

                                    {/* Warning */}
                                    {Number(report?.expectancy) < 5 && Number(report?.expectancy) > 0 && (
                                        <li>
                                            <span className="font-bold">⚠ {t('Warning (0 – < 5):')}</span>{" "}
                                            <span>{t('Expectancy gần bằng 0.')}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Giảm lệnh noise.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Chỉ trade trong phiên thanh khoản cao.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Review lại setup chưa hiệu quả.')}</li>
                                            </ul>
                                        </li>
                                    )}

                                    {/* Danger */}
                                    {Number(report?.expectancy) < 0 && (
                                        <li>
                                            <span className="font-bold">{t('Danger (< 0):')}</span>{" "}
                                            <span>{t('Expectancy âm.')}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Dừng giao dịch tài khoản thật.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Backtest hệ thống.')}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t('Điều chỉnh RR để kéo expectancy về dương.')}</li>
                                            </ul>
                                        </li>
                                    )}
                                </ul>

                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={17} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className="pt-10 small-blue-title">5. {t('Profit Factor')}</p>
                                <p className="normal-text text-black-normal" style={{ lineHeight: '48px' }}>
                                    {t('Profit Factor là tỷ lệ tổng lợi nhuận so với tổng thua lỗ. PF > 1 cho thấy hệ thống có lợi; PF < 1 cho thấy hệ thống thua lỗ. Đây là chỉ số phổ biến để đánh giá tính khả thi của một chiến lược.')}
                                </p>
                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Kích thước lệnh lớn nhất trong kỳ (lots).')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {Number(report?.profit_factor)?.toFixed(2)} lots
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng thấp càng tốt - Lower better')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ranges')}:</strong>
                                        <ul className="text-2xl list-decimal mt-3 ml-14">
                                            <li style={{ lineHeight: '48px' }}>✅ Good: ≤ 2 lots</li>
                                            <li style={{ lineHeight: '48px' }}>⚠ Warning: &gt; 2 {t('to')} 5 lots</li>
                                            <li style={{ lineHeight: '48px' }}>❌ Danger: &gt; 5 lots</li>
                                        </ul>
                                    </li>
                                </ul>

                                <p className="normal-text font-bold mt-8 text-black-normal" style={{ lineHeight: '48px' }}>{t('Insight gợi ý')}</p>
                                <ul className="text-2xl list-none ml-8 text-black-normal">
                                    {Number(report?.profit_factor) >= 1.5 && (
                                        <li style={{ lineHeight: '48px' }}>
                                            <strong>✅ {t('Good (≥ 1.5):')}</strong> {t('Hệ thống sinh lời tốt.')}
                                            <ul className="text-2xl list-none mt-3 ml-14">
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ risk per trade ≤ 1%.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Duy trì setup hiệu quả.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Theo dõi PF theo tháng để đảm bảo bền vững.')}</li>
                                            </ul>
                                        </li>
                                    )}
                                    {Number(report?.profit_factor) < 1.5 && Number(report?.profit_factor) > 1.2 && (
                                        <li style={{ lineHeight: '48px' }}>
                                            <strong>⚠ {t('Warning (1.2 – <1.5):')}</strong> {t('PF trung bình.')}
                                            <ul className="text-2xl list-none mt-3 ml-14">
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Loại bỏ entry yếu/noise.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giảm scalping tốn phí.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Nâng chuẩn lọc setup A+.')}</li>
                                            </ul>
                                        </li>
                                    )}
                                    {Number(report?.profit_factor) <= 1.2 && (
                                        <li style={{ lineHeight: '48px' }}>
                                            <strong>❌ {t('Danger: PF < 1.2.')}</strong>
                                            <ul className="text-2xl list-none mt-3 ml-14">
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Đừng giao dịch ngay.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Review 20 lệnh thua lớn nhất.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Test lại hệ thống ở demo trước khi quay lại real.')}</li>
                                            </ul>
                                        </li>
                                    )}
                                </ul>
                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={18} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className="pt-10 small-blue-title">6.{t('Sharpe Ratio (Sharpe-like)')}</p>
                                <p className="normal-text text-black-normal" style={{ lineHeight: '48px' }}>
                                    {t('Sharpe Ratio đo lường mức lợi nhuận điều chỉnh theo rủi ro (profit per unit of volatility). Nó cho biết chiến lược có "đáng" với rủi ro bỏ ra hay không.')}
                                </p>
                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Ghi chú:')}</strong> {t('Lợi nhuận trên đơn vị biến động (proxy Sharpe).')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Value')}:</strong> {Number(report?.sharpe_ratio || 0)?.toFixed(2)}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng cao càng tốt - Higher better')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Ranges')}:</strong></li>
                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ Good: ≥ 1.0</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ Warning: 0.5 - &lt;1.0</li>
                                        <li style={{ lineHeight: '48px' }}>❌ Danger: &lt; 0.5</li>
                                    </ul>
                                </ul>

                                <p className="normal-text font-bold mt-8 text-black-normal" style={{ lineHeight: '48px' }}>{t('Insight gợi ý')}</p>
                                <ul className="text-2xl list-none ml-8 text-black-normal">
                                    {Number(report?.sharpe_ratio) >= 1 && <li style={{ lineHeight: '48px' }}>
                                        <strong>✅ {t('Good (≥ 1.0):')}</strong> {t('Hiệu quả lợi nhuận trên rủi ro tốt.')}
                                        <ul className="text-2xl list-none mt-3 ml-14">
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tiếp tục quản trị vốn hiện tại.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Scale up khi equity tăng.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ setup RR cao.')}</li>
                                        </ul>
                                    </li>}
                                    {Number(report?.sharpe_ratio) > 0.5 && Number(report?.sharpe_ratio) < 1 && <li style={{ lineHeight: '48px' }}>
                                        <strong>⚠ {t('Warning (0.5 – <1.0):')}</strong> {t('Hiệu quả trung bình.')}
                                        <ul className="text-2xl list-none mt-3 ml-14">
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Hạn chế overtrade.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ lot size nhất quán.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ưu tiên khung giờ thị trường rõ xu hướng.')}</li>
                                        </ul>
                                    </li>}
                                    {Number(report?.sharpe_ratio) <= 0.5 && <li style={{ lineHeight: '48px' }}>
                                        <strong>❌ {t('Danger (< 0.5):')}</strong> {t('Hiệu quả kém.')}
                                        <ul className="text-2xl list-none mt-3 ml-14">
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ngừng giao dịch thật.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Rà soát chiến lược gây drawdown.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Chuyển sang quản trị vốn an toàn hơn.')}</li>
                                        </ul>
                                    </li>}
                                </ul>
                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={19} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className="pt-10 small-blue-title">7.{t('Risk-Reward Ratio (RRR)')}</p>
                                <p className="normal-text text-black-normal" style={{ lineHeight: '48px' }}>
                                    {t('RRR so sánh mức rủi ro trung bình trên mỗi lệnh thua với lợi nhuận trung bình trên mỗi lệnh thắng. RRR thấp nghĩa là trader chấp nhận rủi ro nhỏ để tìm kiếm lợi nhuận lớn – đây là đặc điểm của một hệ thống bền vững.')}
                                </p>
                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Ghi chú:')}</strong> {t('Tỷ lệ lợi nhuận kỳ vọng so với rủi ro.')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Value')}:</strong> {Number(report?.mean_RRR)?.toFixed(2)}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng cao càng tốt - Higher better')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Ranges')}:</strong></li>
                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ Good: ≥ 1.5</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ Warning: 1.2 – &lt;1.5</li>
                                        <li style={{ lineHeight: '48px' }}>❌ Danger: &lt; 1.2</li>
                                    </ul>
                                </ul>

                                <p className="normal-text font-bold mt-8 text-black-normal" style={{ lineHeight: '48px' }}>{t('Insight gợi ý')}</p>
                                <ul className="text-2xl list-none ml-8 text-black-normal">
                                    {Number(report?.mean_RRR) >= 1.5 && <li style={{ lineHeight: '48px' }}>
                                        <strong>✅ {t('Good (≥ 1.5):')}</strong> {t('RR bền vững.')}
                                        <ul className="text-2xl list-none mt-3 ml-14">
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ kỷ luật SL/TP.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tập trung setup có RR cao.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Nhật ký RR tốt nhất để nhân bản.')}</li>
                                        </ul>
                                    </li>}
                                    {Number(report?.mean_RRR) < 1.5 && Number(report?.mean_RRR) > 1.2 && <li style={{ lineHeight: '48px' }}>
                                        <strong>⚠ {t('Warning (1.2 – <1.5):')}</strong> {t('RR hơi thấp.')}
                                        <ul className="text-2xl list-none mt-3 ml-14">
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Điều chỉnh SL/TP để nâng RR.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Loại bỏ lệnh RR < 1.2.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Backtest tìm setup tối ưu.')}</li>
                                        </ul>
                                    </li>}
                                    {Number(report?.mean_RRR) <= 1.2 && <li style={{ lineHeight: '48px' }}>
                                        <strong>❌ {t('Danger (< 1.2):')}</strong> {t('RR dưới chuẩn.')}
                                        <ul className="text-2xl list-none mt-3 ml-14">
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ngừng chiến lược hiện tại.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Review toàn bộ entry.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ lại setup RR ≥ 1.5.')}</li>
                                        </ul>
                                    </li>}
                                </ul>
                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={20} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className="pt-10 small-blue-title">{t('Win/Loss Ratio')}</p>
                                <p className="normal-text text-black-normal" style={{ lineHeight: '48px' }}>
                                    {t('Win/Loss Ratio thể hiện mức lợi nhuận trung bình của lệnh thắng so với mức lỗ trung bình của lệnh thua. Khác với Win Rate, chỉ số này tập trung vào kích thước của việc thắng và thua thay vì số lượng.')}
                                </p>
                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Ghi chú:')}</strong> {t('Tỷ số số lệnh thắng / số lệnh thua.')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Value')}:</strong> {Number(Number(report?.performance_summary?.profitable_trades) / Number(report?.performance_summary?.losing_trades)).toFixed(2)}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng cao càng tốt - Higher better')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Ranges')}:</strong></li>
                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ Good: ≥ 1.1</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ Warning: 0.9 – &lt;1.1</li>
                                        <li style={{ lineHeight: '48px' }}>❌ Danger: &lt; 0.9</li>
                                    </ul>
                                </ul>

                                <p className="normal-text font-bold mt-8 text-black-normal" style={{ lineHeight: '48px' }}>{t('Insight gợi ý')}</p>
                                <ul className="text-2xl list-none ml-8 text-black-normal">
                                    {Number(Number(report?.performance_summary?.profitable_trades) / Number(report?.performance_summary?.losing_trades)) >= 1.1 && <li style={{ lineHeight: '48px' }}>
                                        <strong>✅ {t('Good (≥ 1.1):')}</strong> {t('Lệnh thắng nhiều hơn lệnh thua.')}
                                        <ul className="text-2xl list-none mt-3 ml-14">
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Duy trì setup A+.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Quản trị rủi ro ≤ 1% mỗi lệnh.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Nhân đông mẫu hình thành công.')}</li>
                                        </ul>
                                    </li>}
                                    {Number(Number(report?.performance_summary?.profitable_trades) / Number(report?.performance_summary?.losing_trades)) < 1.1 && Number(Number(report?.performance_summary?.profitable_trades) / Number(report?.performance_summary?.losing_trades)) > 0.9 && <li style={{ lineHeight: '48px' }}>
                                        <strong>⚠ {t('Warning (0.9 – <1.1):')}</strong> {t('Tỷ lệ gần hòa.')}
                                        <ul className="text-2xl list-none mt-3 ml-14">
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giảm lệnh không có xác suất cao.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Cải thiện kỹ năng quản lý lệnh.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Review 10 lệnh gần nhất để điều chỉnh.')}</li>
                                        </ul>
                                    </li>}
                                    {Number(Number(report?.performance_summary?.profitable_trades) / Number(report?.performance_summary?.losing_trades)) <= 0.9 && <li style={{ lineHeight: '48px' }}>
                                        <strong>❌ {t('Danger (< 0.9):')}</strong> {t('Lệnh thua nhiều hơn thắng.')}
                                        <ul className="text-2xl list-none mt-3 ml-14">
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ngừng giao dịch ngay.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Rà soát toàn bộ chiến lược.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Quay về demo cho đến khi ổn định.')}</li>
                                        </ul>
                                    </li>}
                                </ul>
                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={21} />
                            </div>
                        </div>
                    </div>

                    <div className="w-full" id="part3">
                        <div style={{ height: '1696px', overflow: 'hidden' }} className={`w-full p-12`}>
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1450px', overflow: 'hidden' }}>
                                {/* Logo trên đầu */}
                                <div className="pt-10 flex justify-center mb-8">
                                    <img src="/downPdf/page-20.png" alt="Trade Group Logo" className="w-40 h-40" />
                                </div>

                                <h1 className="text-4xl font-bold text-center mb-6">{t('Trade Group')}</h1>

                                <p className="small-blue-title">{t('Nhóm 3: Trades – Đặc điểm hành vi giao dịch')}</p>
                                <p className="normal-text text-black-normal" style={{ lineHeight: '48px' }}>
                                    {t('Nhóm Trades phân tích số lượng và kết quả của các lệnh giao dịch, qua đó cho thấy cách trader thực thi chiến lược và quản lý các lệnh. Những chỉ số trong nhóm này phản ánh mức độ hoạt động, sự cân bằng giữa lệnh thắng và thua, cũng như các điểm cực trị như lệnh có lãi/lỗ lớn nhất. Theo đó, nhóm Trades giúp trader nhận rõ overtrading, quản lý tâm lý về tỷ lệ thắng và cải thiện tính kỷ luật trong giao dịch.')}
                                </p>

                                <p className="normal-text font-bold mt-8 text-black-normal" style={{ lineHeight: '48px' }}>{t('Ý nghĩa với trader')}</p>
                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>{t('Giúp trader hiểu rõ tần suất và cường độ giao dịch.')}</li>
                                    <li style={{ lineHeight: '48px' }}>{t('Cho thấy sự phân bố giữa số lệnh thắng và lệnh thua.')}</li>
                                    <li style={{ lineHeight: '48px' }}>{t('Xác định các giao dịch tốt nhất/worst trade để rút ra bài học kinh nghiệm.')}</li>
                                    <li style={{ lineHeight: '48px' }}>{t('Phát hiện chuỗi thắng/thua bất thường, cảnh báo nguy cơ mất kiểm soát tâm lý.')}</li>
                                </ul>

                                <p className="normal-text font-bold mt-8 text-black-normal" style={{ lineHeight: '48px' }}>{t('Danh mục chỉ số chính')}</p>
                                <ol className="text-2xl list-decimal ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Number of Trades')}</strong> – {t('Tổng số lệnh đã thực hiện trong giai đoạn phân tích.')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Profitable Trades')}</strong> – {t('Số lượng lệnh có lãi.')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Losing Trades')}</strong> – {t('Số lượng lệnh thua lỗ.')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Best Trade (Profit)')}</strong> – {t('Lệnh có lợi nhuận cao nhất trong giai đoạn phân tích.')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Worst Trade (Loss)')}</strong> – {t('Lệnh thua lỗ nặng nhất trong giai đoạn phân tích.')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Max Consecutive Wins')}</strong> – {t('Số lệnh thắng liên tiếp nhiều nhất.')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Max Consecutive Losses')}</strong> – {t('Số lệnh thua liên tiếp nhiều nhất.')}</li>
                                </ol>


                            </div>
                            <div className={`px-20`} style={{ transform: 'translateY(-50px)' }}>
                                <FooterPart page={22} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className="normal-text mt-1 text-black-normal" style={{ lineHeight: '48px' }}>
                                    {t('Nhóm Trades tương đối đơn giản, nhưng cung cấp cái nhìn tổng quan hiệu quả về từng lệnh. Khi kết hợp với nhóm Performance, trader sẽ hiểu rõ hơn về chất lượng tổng thể')}
                                </p>

                                <p className="small-blue-title">{t('1. Number of Trades')}</p>
                                <p className="normal-text text-black-normal" style={{ lineHeight: '48px' }}>
                                    {t('Tổng số lệnh được thực hiện trong giai đoạn phân tích. Con số này phản ánh mức độ hoạt động, quá nhiều hoặc quá ít đều có thể là dấu hiệu không tối ưu.')}
                                </p>
                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Ghi chú:')}</strong> {t('Tổng số lệnh thực hiện so với kế hoạch.')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Value')}:</strong> {report?.total_trades} {t('lệnh')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Chiều hướng:')}</strong> {t('Trung tính - Neutral - không đánh giá theo hướng cao/thấp.')}</li>
                                    <li style={{ lineHeight: '48px' }}><strong>{t('Ranges')}:</strong></li>
                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ {t('Good: Trong ±20% kế hoạch')}</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ {t('Warning: Lệch 20-50%')}</li>
                                        <li style={{ lineHeight: '48px' }}>❌ {t('Danger: Lệch > 50%')}</li>
                                    </ul>
                                </ul>

                                <p className="normal-text font-bold mt-8 text-black-normal" style={{ lineHeight: '48px' }}>{t('Insight gợi ý')}</p>
                                <ul className="text-2xl list-none ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>✅ {t('Good (Trong ±20% kế hoạch):')}</strong> {t('Tần suất giao dịch hợp lý.')}
                                        <ul className="text-2xl list-none mt-3 ml-14">
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tiếp tục bám sát kế hoạch trading.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Đảm bảo mỗi lệnh có lý do rõ ràng.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Nhật ký setup hiệu quả để giữ nhịp độ.')}</li>
                                        </ul>
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>⚠ {t('Warning (Lệch 20-50%):')}</strong> {t('Lệch nhẹ so với kế hoạch.')}
                                        <ul className="text-2xl list-none mt-3 ml-14">
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Kiểm tra nguyên nhân (overtrade hoặc bỏ lỡ cơ hội).')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giới hạn số lệnh/ngày.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Điều chỉnh lại kế hoạch cho sát thực tế.')}</li>
                                        </ul>
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>❌ {t('Danger (Lệch > 50%):')}</strong> {t('Lệch quá xa kế hoạch.')}
                                        <ul className="text-2xl list-none mt-3 ml-14">
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ngừng giao dịch để tránh rủi ro.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Rà soát lại kỷ luật vào/thoát lệnh.')}</li>
                                            <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Đặt giới hạn max/min lệnh hàng ngày.')}</li>
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={23} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className="pt-10 small-blue-title">{t('2. Profitable Trades')}</p>
                                <p className="normal-text text-black-normal" style={{ lineHeight: '48px' }}>
                                    {t('Số lượng lệnh có lãi. Chỉ số này khi kết hợp với Win Rate và Expectancy sẽ cho cái nhìn rõ hơn về mức độ thành công của chiến lược.')}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Số lượng lệnh thắng.')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {report?.performance_summary?.profitable_trades} {t('lệnh')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng cao càng tốt - Higher better')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>
                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ {t('Good: ≥ 55% tổng số lệnh')}</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ {t('Warning: 45 - <55%')}</li>
                                        <li style={{ lineHeight: '48px' }}>❌ {t('Danger: <45%')}</li>
                                    </ul>
                                </ul>

                                <p className="normal-text font-bold mt-8 text-black-normal" style={{ lineHeight: '48px' }}>
                                    {t('Insight gợi ý')}
                                </p>

                                <ul className="text-2xl list-none ml-8 text-black-normal">
                                    {/* Case Good */}
                                    {(Number(Number(report?.performance_summary?.profitable_trades) / Number(report?.total_trades)) * 100) >= 55 && (
                                        <li style={{ lineHeight: '48px' }}>
                                            <strong>✅ {t('Good (≥ 55% tổng số lệnh):')}</strong> {t('Tỷ lệ lệnh thắng cao.')}
                                            <ul className="text-2xl list-none mt-3 ml-14">
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tiếp tục duy trì hệ thống lọc setup.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tăng dần quy mô cho hiệu quả chất lượng cao.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Nhật ký chi tiết để nhân bản mẫu hình.')}</li>
                                            </ul>
                                        </li>
                                    )}

                                    {/* Case Warning */}
                                    {(Number(Number(report?.performance_summary?.profitable_trades) / Number(report?.total_trades)) * 100) > 45 &&
                                        (Number(Number(report?.performance_summary?.profitable_trades) / Number(report?.total_trades)) * 100) < 55 && (
                                            <li style={{ lineHeight: '48px' }}>
                                                <strong>⚠ {t('Warning (45 - <55%):')}</strong> {t('Lệnh thắng giảm.')}
                                                <ul className="text-2xl list-none mt-3 ml-14">
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Kiểm tra điểm entry/exit để tối ưu.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tránh lệnh thử nghiệm RR thấp.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tập trung trade khung giờ thành khoản.')}</li>
                                                </ul>
                                            </li>
                                        )}

                                    {/* Case Danger */}
                                    {(Number(Number(report?.performance_summary?.profitable_trades) / Number(report?.total_trades)) * 100) <= 45 && (
                                        <li style={{ lineHeight: '48px' }}>
                                            <strong>❌ {t('Danger (< 45%):')}</strong> {t('Lệnh thắng thấp.')}
                                            <ul className="text-2xl list-none mt-3 ml-14">
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ngừng trading tại khoản thật.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Backtest lại chiến lược.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giảm volume để bảo toàn vốn.')}</li>
                                            </ul>
                                        </li>
                                    )}
                                </ul>
                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={24} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className="pt-10 small-blue-title">{t('3. Losing Trades')}</p>
                                <p className="normal-text text-black-normal" style={{ lineHeight: '48px' }}>
                                    {t('Số lượng lệnh thua lỗ. Nếu tỷ trọng lệnh thua cao hoặc mức lỗ lớn, trader cần đánh giá lại chiến lược hoặc quản lý vốn.')}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Số lượng lệnh thua.')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {report?.performance_summary?.losing_trades} {t('lệnh')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng thấp càng tốt - Lower better')}
                                    </li>
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>
                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ {t('Good: ≤ 45% tổng số lệnh')}</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ {t('Warning: 45-55%')}</li>
                                        <li style={{ lineHeight: '48px' }}>❌ {t('Danger: > 55%')}</li>
                                    </ul>
                                </ul>

                                <p className="normal-text font-bold mt-8 text-black-normal" style={{ lineHeight: '48px' }}>
                                    {t('Insight gợi ý')}
                                </p>

                                <ul className="text-2xl list-none ml-8 text-black-normal">
                                    {/* Case Good */}
                                    {(Number(Number(report?.performance_summary?.losing_trades) / Number(report?.total_trades)) * 100) <= 45 && (
                                        <li style={{ lineHeight: '48px' }}>
                                            <strong>✅ {t('Good (≤ 45% tổng số lệnh):')}</strong> {t('Tỷ lệ lệnh thua thấp.')}
                                            <ul className="text-2xl list-none mt-3 ml-14">
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ nguyên kỷ luật SL.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Duy trì risk ≤ 1% mỗi lệnh.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Nhật ký lệnh để học cách tránh sai lầm.')}</li>
                                            </ul>
                                        </li>
                                    )}

                                    {/* Case Warning */}
                                    {(Number(Number(report?.performance_summary?.losing_trades) / Number(report?.total_trades)) * 100) > 45 &&
                                        (Number(Number(report?.performance_summary?.losing_trades) / Number(report?.total_trades)) * 100) < 55 && (
                                            <li style={{ lineHeight: '48px' }}>
                                                <strong>⚠ {t('Warning (45-55%):')}</strong> {t('Lệnh thua tăng.')}
                                                <ul className="text-2xl list-none mt-3 ml-14">
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Kiểm tra lại quy trình vào lệnh.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giảm tần suất giao dịch.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Loại bỏ setup kém hiệu quả.')}</li>
                                                </ul>
                                            </li>
                                        )}

                                    {/* Case Danger */}
                                    {(Number(Number(report?.performance_summary?.losing_trades) / Number(report?.total_trades)) * 100) >= 55 && (
                                        <li style={{ lineHeight: '48px' }}>
                                            <strong>❌ {t('Danger (> 55%):')}</strong> {t('Lệnh thua quá nhiều.')}
                                            <ul className="text-2xl list-none mt-3 ml-14">
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tạm ngừng giao dịch.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Rà soát toàn bộ chiến lược.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Thử nghiệm lại trên demo.')}</li>
                                            </ul>
                                        </li>
                                    )}
                                </ul>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={25} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />

                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 text-2xl font-semibold' style={{ color: '#00789D' }}>
                                    {t('4. Best Trade (Profit)')}
                                </p>
                                <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                                    {t('Lệnh có lợi nhuận cao nhất trong giai đoạn phân tích. Đây là điểm cực trị dương, phản ánh tiềm năng tối đa mà chiến lược có thể đạt được.')}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Lợi nhuận cao nhất của một lệnh (so với |Avg Loss|).')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> +{report?.performance_summary?.best_trade?.profit} USD
                                        (Avg Loss = {report?.performance_summary?.avg_loss_per_loss} USD →
                                        {Number(
                                            Number(
                                                Number(report?.performance_summary?.best_trade?.profit) /
                                                Number(report?.performance_summary?.avg_loss_per_loss)
                                            ) * -1
                                        ).toFixed(2)}
                                        ×)
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng cao càng tốt - Higher better')}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ {t('Good: ≤ 45% tổng số lệnh')}</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ {t('Warning: 45–55%')}</li>
                                        <li style={{ lineHeight: '48px' }}>❌ {t('Danger: > 55%')}</li>
                                    </ul>
                                </ul>

                                <p className='pt-3 text-2xl font-bold text-black-normal'>{t('Insight gợi ý')}</p>
                                <div className="p-4">
                                    <ul className="list-none pl-6  mb-6  text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {Number(Number(Number(report?.performance_summary?.best_trade?.profit) / Number(report?.performance_summary?.avg_loss_per_loss)) * -1) >= 2 &&
                                            <li>
                                                <span className="font-bold">✅ {t('Good (≥ 2 × Avg Loss):')}</span>{" "}
                                                <span>{t('Lệnh tốt tạo biên lợi nhuận cao.')}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Nhân rộng mẫu setup đã đem lại lãi lớn.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ RR tối thiểu 1.5.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Lưu lại chiến lược vào playbook.')}</li>
                                                </ul>
                                            </li>}
                                        {Number(Number(Number(report?.performance_summary?.best_trade?.profit) / Number(report?.performance_summary?.avg_loss_per_loss)) * -1) > 1 &&
                                            Number(Number(Number(report?.performance_summary?.best_trade?.profit) / Number(report?.performance_summary?.avg_loss_per_loss)) * -1) < 2 &&
                                            <li>
                                                <span className="font-bold">⚠ {t('Warning (1 – < 2 × Avg Loss):')}</span>{" "}
                                                <span>{t('Lãi tối đa chỉ ngang lỗ')} <i>{t('trung bình')}</i>.</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Nâng TP với setup có xác suất cao.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tránh chốt lời quá sớm.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Chọn khung thời gian có biên dao động lớn.')}</li>
                                                </ul>
                                            </li>}
                                        {Number(Number(Number(report?.performance_summary?.best_trade?.profit) / Number(report?.performance_summary?.avg_loss_per_loss)) * -1) <= 1 &&
                                            <li>
                                                <span className="font-bold">❌ {t('Danger (< 1 × Avg Loss):')}</span>{" "}
                                                <span>{t('Không có lệnh vượt quá lỗ')} <i>{t('trung bình')}</i>.</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ngừng chiến lược hiện tại.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Xem lại cách chọn TP.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ưu tiên setup RR ≥ 2.')}</li>
                                                </ul>
                                            </li>}
                                    </ul>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={26} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 text-2xl font-semibold' style={{ color: '#00789D' }}>
                                    {t('5. Worst Trade (Loss)')}
                                </p>
                                <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                                    {t('Lệnh thua lỗ nặng nhất trong giai đoạn phân tích. Đây là điểm cực trị âm, cảnh báo mức rủi ro tối đa mà trader đã phải chịu.')}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Mức lỗ lớn nhất của một lệnh (so với |Avg Loss|).')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {report?.performance_summary?.worst_trade.loss} USD
                                        (Avg Loss = {report?.performance_summary?.avg_loss_per_loss} USD →
                                        {Number(
                                            Number(
                                                Number(report?.performance_summary?.worst_trade.loss) /
                                                Number(report?.performance_summary?.avg_loss_per_loss)
                                            ) * -1
                                        ).toFixed(2)}
                                        ×)
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng thấp càng tốt - Lower better')}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ {t('Good: ≥ -1.5 × Avg Loss')}</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ {t('Warning: -2.5 đến -1.5 × Avg Loss')}</li>
                                        <li style={{ lineHeight: '48px' }}>❌ {t('Danger: < -2.5 × Avg Loss')}</li>
                                    </ul>
                                </ul>

                                <p className='pt-3 text-2xl font-bold text-black-normal'>{t('Insight gợi ý')}</p>
                                <div className="p-4">
                                    <ul className="list-none pl-6  mb-6  text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {Number(Number(Number(report?.performance_summary?.worst_trade.loss) / Number(report?.performance_summary?.avg_loss_per_loss)) * -1) >= -1.5 &&
                                            <li>
                                                <span className="font-bold">✅ {t('Good (≥ -1.5 × Avg Loss):')}</span>{" "}
                                                <span>{t('Thua lỗ lớn được kiểm soát.')}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tiếp tục duy trì discipline SL.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ risk ≤ 1%.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Nhật ký cách xử lý lệnh lỗ ít.')}</li>
                                                </ul>
                                            </li>}
                                        {Number(Number(Number(report?.performance_summary?.worst_trade.loss) / Number(report?.performance_summary?.avg_loss_per_loss)) * -1) > -2.5 &&
                                            Number(Number(Number(report?.performance_summary?.worst_trade.loss) / Number(report?.performance_summary?.avg_loss_per_loss)) * -1) < -1.5 &&
                                            <li>
                                                <span className="font-bold">⚠ {t('Warning (-2.5 đến -1.5 × Avg Loss):')}</span>{" "}
                                                <span>{t('Lỗ tối đa hơi lớn.')}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Kiểm tra nguyên nhân: không đặt SL hoặc kéo SL sai.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giảm khối lượng lệnh rủi ro cao.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Loại bỏ setup dễ tạo lỗ sâu.')}</li>
                                                </ul>
                                            </li>}
                                        {Number(Number(Number(report?.performance_summary?.worst_trade.loss) / Number(report?.performance_summary?.avg_loss_per_loss)) * -1) <= -2.5 &&
                                            <li>
                                                <span className="font-bold">❌ {t('Danger (< -2.5 × Avg Loss):')}</span>{" "}
                                                <span>{t('Lỗ cực lớn ngoài kiểm soát.')}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ngưng trading ngay.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Rà soát hệ thống SL.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Reset chiến lược quản trị vốn.')}</li>
                                                </ul>
                                            </li>}
                                    </ul>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={27} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 text-2xl font-semibold' style={{ color: '#00789D' }}>
                                    {t('6. Max Consecutive Wins')}
                                </p>
                                <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                                    {t('Số lệnh thắng liên tiếp nhiều nhất. Chỉ số này giúp đánh giá tính ổn định của hệ thống, đồng thời cảnh báo nguy cơ chủ quan sau chuỗi thắng dài.')}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Số lệnh thắng liên tiếp nhiều nhất.')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {report?.behavioral_analysis.max_consecutive_wins} {t('lệnh')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng cao càng tốt - Higher better')}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ {t('Good: ≥ 4')}</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ {t('Warning: 2–3')}</li>
                                        <li style={{ lineHeight: '48px' }}>❌ {t('Danger: ≤ 1')}</li>
                                    </ul>
                                </ul>

                                <p className='pt-3 text-2xl font-bold text-black-normal'>{t('Insight gợi ý')}</p>
                                <div className="p-4">
                                    <ul className="list-none pl-6  mb-6  text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {Number(report?.behavioral_analysis.max_consecutive_wins) >= 4 && <li>
                                            <span className="font-bold">✅ {t('Good (≥ 4):')}</span>{" "}
                                            <span>{t('Chuỗi thắng ổn định.')}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ nguyên chiến lược entry.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tập trung vào các setup đem lại chuỗi chiến thắng.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Nâng dần lot size cho lệnh chất lượng cao.')}</li>
                                            </ul>
                                        </li>}
                                        {(Number(report?.behavioral_analysis.max_consecutive_wins) === 3 || Number(report?.behavioral_analysis.max_consecutive_wins) === 2) && <li>
                                            <span className="font-bold">⚠ {t('Warning (2–3):')}</span>{" "}
                                            <span>{t('Chuỗi thắng ngắn.')}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Xem lại xác suất từng setup.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giảm số lệnh không đạt tiêu chuẩn.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ưu tiên setup có Win Rate cao.')}</li>
                                            </ul>
                                        </li>}
                                        {Number(report?.behavioral_analysis.max_consecutive_wins) <= 1 && <li>
                                            <span className="font-bold">❌ {t('Danger (≤ 1):')}</span>{" "}
                                            <span>{t('Không duy trì được chuỗi thắng.')}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ngưng trading thật')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Backtest để tìm setup mạnh.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Chỉ giữ các mô hình đã chứng minh hiệu quả.')}</li>
                                            </ul>
                                        </li>}
                                    </ul>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={28} />
                            </div>
                        </div>
                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 text-2xl font-semibold' style={{ color: '#00789D' }}>
                                    {t('7. Max Consecutive Losses')}
                                </p>

                                <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                                    {t('Số lệnh thua liên tiếp nhiều nhất. Đây là chỉ số quan trọng để kiểm tra khả năng chịu đựng rủi ro và đánh giá xem vốn có đủ để vượt qua giai đoạn drawdown hay không.')}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Chuỗi lệnh thua liên tiếp nhiều nhất.')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {report?.behavioral_analysis.max_consecutive_losses} {t('lệnh')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng thấp càng tốt - Lower better')}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ {t('Good: ≤ 3')}</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ {t('Warning: 4–5')}</li>
                                        <li style={{ lineHeight: '48px' }}>❌ {t('Danger: ≥ 6')}</li>
                                    </ul>
                                </ul>

                                <p className='pt-3 text-2xl font-bold text-black-normal'>{t('Insight gợi ý')}</p>

                                <div className="p-4">
                                    <ul className="list-none pl-6 mb-6 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {Number(report?.behavioral_analysis.max_consecutive_losses) <= 3 && (
                                            <li>
                                                <span className="font-bold">✅ {t('Good (≤ 3):')}</span>{" "}
                                                <span>{t('Chuỗi thua ngắn, kiểm soát tốt.')}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tiếp tục giữ discipline SL.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Nhật ký nguyên nhân lỗ để học.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ rủi ro nhỏ mỗi lệnh.')}</li>
                                                </ul>
                                            </li>
                                        )}

                                        {(Number(report?.behavioral_analysis.max_consecutive_losses) === 4 ||
                                            Number(report?.behavioral_analysis.max_consecutive_losses) === 5) && (
                                                <li>
                                                    <span className="font-bold">⚠ {t('Warning (4–5):')}</span>{" "}
                                                    <span>{t('Chuỗi thua dài.')}</span>
                                                    <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                        <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giảm tần suất giao dịch.')}</li>
                                                        <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tạm dừng khi thua 3 lệnh liên tiếp.')}</li>
                                                        <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Review setup gây drawdown.')}</li>
                                                    </ul>
                                                </li>
                                            )}

                                        {Number(report?.behavioral_analysis.max_consecutive_losses) >= 6 && (
                                            <li>
                                                <span className="font-bold">❌ {t('Danger (≥ 6):')}</span>{" "}
                                                <span>{t('Chuỗi thua kéo dài.')}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ngưng giao dịch ngay.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Rà soát toàn bộ chiến lược.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Quay lại demo để phục hồi kỷ luật.')}</li>
                                                </ul>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={29} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <div>
                                    <div className="flex justify-center mt-20 mb-4">
                                        <img
                                            src="/downPdf/ftmo.png"
                                            alt="Group 4"
                                            className="w-40 h-40"
                                        />
                                    </div>

                                    <h1 className="text-4xl font-bold text-center mb-16">
                                        {t('Financial Group')}
                                    </h1>

                                    <p className="font-semibold text-cts-cyan-700 mb-2 mt-4 text-2xl">
                                        {t('Nhóm 4: Volume – Quy mô và nhất quán vốn giao dịch')}
                                    </p>

                                    <p className="mb-4 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {t('Nhóm Volume phản ánh quy mô giao dịch và cách trader quản lý khối lượng vốn cho từng lệnh. Đây là nhóm chỉ số quan trọng để đánh giá mức độ cam kết vốn, sự nhất quán trong việc quản lý vị thế, cũng như nguy cơ tiềm ẩn từ việc sử dụng lot size quá lớn hoặc quá nhỏ. Quản lý khối lượng hiệu quả là nền tảng của quản trị rủi ro, giúp trader duy trì sự ổn định và tránh tình trạng over-leverage.')}
                                    </p>

                                    <p className="font-semibold text-cts-cyan-700 mb-2 text-2xl">
                                        {t('Ý nghĩa đối với trader')}
                                    </p>
                                    <ul className="list-disc pl-6 space-y-3 mb-4 text-2xl text-black-normal">
                                        <li>{t('Đo lường tổng quy mô vốn được sử dụng trong giai đoạn phân tích.')}</li>
                                        <li>{t('Kiểm tra sự nhất quán trong quản lý vị thế giữa các lệnh.')}</li>
                                        <li>{t('Xác định điểm cực trị khối lượng (lớn nhất/nhỏ nhất) để nhận diện rủi ro tiềm ẩn.')}</li>
                                        <li>{t('Giúp cân đối khối lượng nhằm đảm bảo tính bền vững của hệ thống giao dịch.')}</li>
                                    </ul>

                                    <p className="font-semibold text-cts-cyan-700 mb-2 text-2xl">
                                        {t('Danh mục chỉ số chính')}
                                    </p>
                                    <ol className="list-decimal pl-6 space-y-3 text-2xl mb-6 text-black-normal">
                                        <li>
                                            <span className="font-bold">{t('Lots Traded')}</span> – {t('Tổng số lot đã giao dịch trong giai đoạn phân tích.')}
                                        </li>
                                        <li>
                                            <span className="font-bold">{t('Average Volume')}</span> – {t('Khối lượng trung bình của mỗi lệnh.')}
                                        </li>
                                        <li>
                                            <span className="font-bold">{t('Max Volume')}</span> – {t('Khối lượng lớn nhất được sử dụng trong một lệnh.')}
                                        </li>
                                        <li>
                                            <span className="font-bold">{t('Min Volume')}</span> – {t('Khối lượng nhỏ nhất được sử dụng trong một lệnh.')}
                                        </li>
                                    </ol>

                                    <p className="text-2xl text-black-normal text-black-normal" style={{ lineHeight: '34px' }}>
                                        {t('Nhóm Volume giúp trader có cái nhìn toàn diện về cách sử dụng vốn trong từng giao dịch. Khi kết hợp với nhóm Financial, trader có khả năng đánh giá được bức tranh tài chính tổng thể và mức độ cam kết vốn để vận hành từng lệnh, từ đó tối ưu hóa việc quản lý rủi ro và gia tăng sự ổn định trong giao dịch dài hạn.')}
                                    </p>
                                </div>
                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={30} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className="pt-10 text-2xl font-semibold" style={{ color: "#00789D" }}>
                                    {t("1. Lots Traded vs Plan")}
                                </p>
                                <p className="pt-3 text-2xl text-black-normal" style={{ lineHeight: "34px" }}>
                                    {t("Tổng số lot đã giao dịch trong giai đoạn phân tích. Con số này cho thấy quy mô tổng thể của hoạt động giao dịch và mức độ sử dụng vốn.")}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Tổng khối lượng lot giao dịch so với kế hoạch đề ra.')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {t('52 lots (plan = 50 lots → +4%)')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Trung tính - Neutral - không đánh giá theo hướng cao/thấp.')}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>
                                            ✅ {t('Good: Trong ±20% kế hoạch')}
                                        </li>
                                        <li style={{ lineHeight: '48px' }}>
                                            ⚠ {t('Warning: Lệch 20–50%')}
                                        </li>
                                        <li style={{ lineHeight: '48px' }}>
                                            ❌ {t('Danger: Lệch > 50%')}
                                        </li>
                                    </ul>
                                </ul>

                                <p className="pt-3 text-2xl font-bold text-black-normal">{t("Insight gợi ý")}</p>
                                <div className="p-4">
                                    <ul className="list-none pl-2 mb-6 text-2xl text-black-normal" style={{ lineHeight: "34px" }}>
                                        <li>
                                            <span className="font-bold">✅ {t("Good (Trong ±20% kế hoạch):")}</span>{" "}
                                            <span>{t("Khối lượng phù hợp với kế hoạch.")}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: "34px" }}>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t("Tiếp tục giữ nhịp độ khối lượng giao dịch.")}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t("Điều chỉnh nhẹ theo vốn tăng/giảm.")}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t("Duy trì quản trị vốn ổn định.")}</li>
                                            </ul>
                                        </li>
                                        <li>
                                            <span className="font-bold">⚠ {t("Warning (Lệch 20–50%):")}</span>{" "}
                                            <span>{t("Khối lượng lệch nhẹ.")}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: "34px" }}>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t("Kiểm tra nguyên nhân (overtrade hoặc bỏ lỡ cơ hội).")}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t("Giới hạn lots/ngày theo khung quản trị vốn.")}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t("Cập nhật lại kế hoạch sát với thực tế thị trường.")}</li>
                                            </ul>
                                        </li>
                                        <li>
                                            <span className="font-bold">❌ {t("Danger (Lệch > 50%):")}</span>{" "}
                                            <span>{t("Khối lượng lệch xa kế hoạch.")}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: "34px" }}>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t("Ngừng trading để tránh rủi ro.")}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t("Reset kế hoạch lot phù hợp equity.")}</li>
                                                <li style={{ paddingLeft: '1rem' }}>_ {t("Rà soát lại tâm lý gây over/under trade")}</li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={31} />
                            </div>
                        </div>
                    </div>

                    <div className="w-full" id="part4">
                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className="pt-10 text-2xl font-semibold" style={{ color: "#00789D" }}>
                                    {t("2. Average Volume")}
                                </p>
                                <p className="pt-3 text-2xl text-black-normal" style={{ lineHeight: "34px" }}>
                                    {t("Khối lượng trung bình mỗi lệnh. Chỉ số này phản ánh tính nhất quán trong quản lý vị thế; dao động quá lớn giữa các lệnh có thể là dấu hiệu thiếu kỷ luật.")}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Độ ổn định kích thước lệnh (CV = sd/mean).')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {t('CV = 120%')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Trung tính - Neutral - không đánh giá theo hướng cao/thấp.')}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ {t('Good: CV ≤ 100%')}</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ {t('Warning: 100% < CV ≤ 300%')}</li>
                                        <li style={{ lineHeight: '48px' }}>❌ {t('Danger: > 300%')}</li>
                                    </ul>
                                </ul>

                                <p className="pt-3 text-2xl font-bold text-black-normal">{t("Insight gợi ý")}</p>
                                <div className="p-4">
                                    <ul className="list-none pl-6 mb-6 text-2xl text-black-normal" style={{ lineHeight: "34px" }}>
                                        <li>
                                            <span className="font-bold">✅ {t("Good (CV ≤ 100%):")}</span>{" "}
                                            <span>{t("Volume ổn định.")}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: "34px" }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Giữ lot size nhất quán")}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Duy trì risk cố định ≤ 1%/lệnh.")}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Nhật ký volume cho từng setup.")}</li>
                                            </ul>
                                        </li>
                                        <li>
                                            <span className="font-bold">⚠ {t("Warning (100% < CV ≤ 300%):")}</span>{" "}
                                            <span>{t("Volume biến động trung bình.")}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: "34px" }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Kiểm tra việc tăng hoặc giảm khối lượng giao dịch dựa trên cảm nhận của bạn.")}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Chuẩn hóa quy tắc chọn LOT cho từng chiến lược.")}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Tránh mở rộng khối lượng giao dịch khi giao dịch trả thù.")}</li>
                                            </ul>
                                        </li>
                                        <li>
                                            <span className="font-bold">❌ {t("Danger (> 300%):")}</span>{" "}
                                            <span>{t("Volume quá biến động.")}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: "34px" }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Ngưng trading để tránh rủi ro vốn.")}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Đặt cố định max lot/ trade")}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Reset chiến lược quản trị vốn.")}</li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={32} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className="pt-10 small-blue-title">3. {t("Max Volume")}</p>
                                <p className="normal-text text-black-normal" style={{ lineHeight: "48px" }}>
                                    {t(
                                        "Khối lượng lớn nhất được sử dụng trong một lệnh. Đây là thước đo mức cam kết rủi ro cao nhất, giúp trader nhận diện điểm cực trị và kiểm soát over-leverage."
                                    )}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Kích thước lệnh lớn nhất trong kỳ (lots).')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {Number(report?.risk_management.max_volume).toFixed(2)} {t('lots')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng thấp càng tốt - Lower better')}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ Good: ≤ 2 lots</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ Warning: &gt; 2 {t('to')} 5 lots</li>
                                        <li style={{ lineHeight: '48px' }}>❌ Danger: &gt; 5 lots</li>
                                    </ul>
                                </ul>

                                <p
                                    className="normal-text font-bold mt-8 text-black-normal"
                                    style={{ lineHeight: "48px" }}
                                >
                                    {t("Insight gợi ý")}
                                </p>
                                <ul className="text-2xl list-none ml-8 text-black-normal">
                                    {Number(report?.risk_management.max_volume) <= 2 && (
                                        <li style={{ lineHeight: "48px" }}>
                                            <strong>✅ {t("Good (≤ 2 lots):")}</strong>{" "}
                                            {t("Lệnh lớn trong kiểm soát.")}
                                            <ul className="text-2xl list-none mt-3 ml-14">
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_
                                                    _ {t("Tiếp tục giữ risk ≤ 1%/lệnh.")}
                                                </li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_
                                                    _ {t("Tăng lot theo vốn, không vượt giới hạn.")}
                                                </li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_
                                                    _ {t("Duy trì nhất quán khối lượng giao dịch (consistency size)")}
                                                </li>
                                            </ul>
                                        </li>
                                    )}

                                    {Number(report?.risk_management.max_volume) > 2 &&
                                        Number(report?.risk_management.max_volume) < 5 && (
                                            <li style={{ lineHeight: "48px" }}>
                                                <strong>⚠ {t("Warning (> 2 đến 5 lots):")}</strong>{" "}
                                                {t("Lot tối đa hơi cao.")}
                                                <ul className="text-2xl list-none mt-3 ml-14">
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_
                                                        _ {t("Giới hạn max lot = 2% vốn.")}
                                                    </li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_
                                                        _ {t("Tránh all-in khi có tín hiệu mạnh.")}
                                                    </li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_
                                                        _ {t("Chuẩn hóa quy tắc scale-in.")}
                                                    </li>
                                                </ul>
                                            </li>
                                        )}

                                    {Number(report?.risk_management.max_volume) >= 5 && (
                                        <li style={{ lineHeight: "48px" }}>
                                            <strong>❌ {t("Danger (> 5 lots):")}</strong>{" "}
                                            {t("Lot cực lớn ngoài chuẩn.")}
                                            <ul className="text-2xl list-none mt-3 ml-14">
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>
                                                    _ {t("Ngừng ngay hành vi phóng lot.")}
                                                </li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>
                                                    _ {t("Rà soát lại kỷ luật quản trị vốn.")}
                                                </li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>
                                                    _ {t("Giảm max lot xuống mức an toàn.")}
                                                </li>
                                            </ul>
                                        </li>
                                    )}
                                </ul>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={33} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-6 text-2xl font-semibold' style={{ color: '#00789D' }}>
                                    {t("4. Min Volume")}
                                </p>
                                <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                                    {t("Khối lượng nhỏ nhất được sử dụng trong một lệnh. Chỉ số này phản ánh mức tối thiểu mà trader chấp nhận vào lệnh; khoảng cách quá xa so với Max Volume có thể cho thấy sự thiếu đồng nhất trong quản lý vốn.")}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Kích thước lệnh nhỏ nhất trong kỳ (lots).')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {Number(report?.risk_management.min_volume).toFixed(2)} {t('lots')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong>{' '}
                                        {t('Trung tính - Neutral - không đánh giá theo hướng cao/thấp. (quá nhỏ → phí cao, quá lớn → risk cao)')}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>
                                            ✅ {t('Good: ≥ 0.1 lots và phù hợp kế hoạch vốn')}
                                        </li>
                                        <li style={{ lineHeight: '48px' }}>
                                            ⚠ {t('Warning: < 0.1 lots hoặc lệch khỏi chuẩn risk')}
                                        </li>
                                        <li style={{ lineHeight: '48px' }}>
                                            ❌ {t('Danger: Không nhất quán, min volume chênh quá xa max volume (>10×)')}
                                        </li>
                                    </ul>
                                </ul>

                                <p className='pt-3 text-2xl font-bold text-black-normal'>{t("Insight gợi ý")}</p>
                                <div className="p-4">
                                    <ul className="list-none pl-6 mb-6 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {Number(report?.risk_management.min_volume) >= 0.1 && (
                                            <li>
                                                <span className="font-bold">✅ {t("Good (≥ 0.1 lots và phù hợp kế hoạch vốn):")}</span>{" "}
                                                <span>{t("Min lot hợp lý.")}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Giữ nhất quán min lot với quản trị vốn.")}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Sử dụng min lot khi thử nghiệm setup mới.")}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Tiếp tục tối ưu phí/lot.")}</li>
                                                </ul>
                                            </li>
                                        )}
                                        {Number(report?.risk_management.min_volume) <= 0.1 && (
                                            <li>
                                                <span className="font-bold">⚠ {t("Warning (< 0.1 lots hoặc lệch khỏi chuẩn risk):")}</span>{" "}
                                                <span>{t("Min lot quá nhỏ hoặc bất thường.")}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Tránh chia nhỏ lệnh quá mức gây phí cao.")}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Cân đối min lot ≥ 0.1 cho tài khoản tiêu chuẩn.")}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Chuẩn hóa min lot trong kế hoạch rủi ro")}</li>
                                                </ul>
                                            </li>
                                        )}
                                        {Number(report?.risk_management.max_volume) / Number(report?.risk_management.min_volume) > 10 && (
                                            <li>
                                                <span className="font-bold">❌ {t("Danger (Không nhất quán, min volume chênh quá xa max volume (>10×):")}</span>{" "}
                                                <span>{t("Min lot cực thấp, không nhất quán với max lot.")}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Ngừng việc mở lệnh micro không cần thiết.")}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Reset lại size chuẩn theo vốn.")}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Giữ nhất quán để tính toán rủi ro chính xác.")}</li>
                                                </ul>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={34} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12 ">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <div className='mt-12'>
                                    <div className="flex justify-center mt-10 mb-4">
                                        <img
                                            src="/downPdf/ftmo5.png"
                                            alt="Group 5"
                                            className="w-40 h-40"
                                        />
                                    </div>
                                    <h1 className="text-4xl font-bold text-center mb-16">
                                        {t("Behavioral Group")}
                                    </h1>
                                    <p className="font-semibold text-cts-cyan-700 mb-2 mt-4 text-2xl">
                                        {t("Nhóm 5: Behavioral – Hành vi và tâm lý giao dịch")}
                                    </p>
                                    <p className="mb-4 text-2xl text-black-normal" style={{ lineHeight: "34px" }}>
                                        {t(
                                            "Nhóm Behavioral phản ánh các thói quen và mẫu hành vi của trader trong quá trình giao dịch, đặc biệt là những hành vi tiêu cực có thể phá hỏng hiệu quả của chiến lược. Đây là nhóm chỉ số quan trọng để phát hiện các tình huống như giao dịch bốc đồng, trả thù thị trường (revenge trade), tần suất quá cao bất thường, hay rơi vào chuỗi thua kéo dài. Việc kiểm soát nhóm Behavioral giúp trader nâng cao tính kỷ luật, giữ tâm lý vững vàng và tránh được các sai lầm thường gặp."
                                        )}
                                    </p>

                                    <p className="font-semibold text-cts-cyan-700 mb-2 text-2xl">
                                        {t("Ý nghĩa đối với trader")}
                                    </p>
                                    <ul className="list-disc pl-6 space-y-3 mb-4 text-2xl text-black-normal">
                                        <li>{t("Phát hiện sớm các hành vi rủi ro cao như overtrading hay revenge trading.")}</li>
                                        <li>{t("Giúp trader tự nhận diện và điều chỉnh thói quen giao dịch thiếu kỷ luật.")}</li>
                                        <li>{t("Cảnh báo những giai đoạn tâm lý bất ổn dễ dẫn đến thua lỗ liên tục.")}</li>
                                        <li>{t("Tạo nền tảng để phát triển phong cách giao dịch ổn định, bền vững hơn.")}</li>
                                    </ul>

                                    <p className="font-semibold text-cts-cyan-700 mb-2 text-2xl">
                                        {t("Danh mục chỉ số chính")}
                                    </p>
                                    <ol
                                        className="list-decimal pl-6 space-y-3 text-2xl mb-6 text-black-normal"
                                        style={{ lineHeight: "34px" }}
                                    >
                                        <li>
                                            <span className="font-bold">{t("Rapid Fire Trades")}</span> – {t("Số lượng lệnh được mở/đóng liên tục trong thời gian ngắn.")}
                                        </li>
                                        <li>
                                            <span className="font-bold">{t("Revenge Trades")}</span> – {t("Các lệnh mở ngay sau thua lỗ với mục đích “gỡ lại”.")}
                                        </li>
                                        <li>
                                            <span className="font-bold">{t("High Frequency Trading")}</span> – {t("Giao dịch với tần suất cực cao trong khoảng thời gian ngắn.")}
                                        </li>
                                        <li>
                                            <span className="font-bold">{t("Revenge Trading Detected")}</span> – {t("Cảnh báo khi phát hiện hành vi trả thù thị trường lặp lại.")}
                                        </li>
                                        <li>
                                            <span className="font-bold">{t("Long Losing Streaks")}</span> – {t("Chuỗi thua kéo dài bất thường.")}
                                        </li>
                                    </ol>
                                    <p className="text-2xl text-black-normal text-black-normal" style={{ lineHeight: "34px" }}>
                                        {t(
                                            "Nhóm Behavioral giúp trader hiểu rõ hơn về mặt tâm lý và hành vi trong giao dịch, yếu tố vốn thường gây ra thua lỗ nặng nề hơn cả bản thân chiến lược. Khi kết hợp với nhóm Risk, trader có thể xây dựng một hệ thống không chỉ mạnh về vốn và chiến lược, mà còn vững chắc về kỷ luật và tâm lý."
                                        )}
                                    </p>
                                </div>
                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={35} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 text-2xl font-semibold' style={{ color: '#00789D' }}>
                                    {t("1. Rapid Fire Trades")}
                                </p>
                                <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                                    {t("Số lượng lệnh được mở/đóng liên tục trong thời gian ngắn, thường mang tính bốc đồng. Đây là dấu hiệu cảnh báo overtrading và mất kiểm soát cảm xúc.")}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Chuỗi lệnh mở liên tiếp trong thời gian ngắn sau thua lỗ.')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {report?.behavioral_analysis.rapid_fire_trades} {t('lệnh liên tục trong 30 phút')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng thấp càng tốt - Lower better')}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ {t('Good: 0')}</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ {t('Warning: 1 – 5')}</li>
                                        <li style={{ lineHeight: '48px' }}>❌ {t('Danger: > 5')}</li>
                                    </ul>
                                </ul>

                                <p className='pt-3 text-2xl font-bold text-black-normal'>{t("Insight gợi ý")}</p>
                                <div className="p-4">
                                    <ul className="list-none pl-6 mb-6 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {Number(report?.behavioral_analysis.rapid_fire_trades) === 0 && (
                                            <li>
                                                <span className="font-bold">✅ {t("Good (0):")}</span>{" "}
                                                <span>{t("Không có hành vi vào lệnh bốc đồng.")}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Duy trì kỷ luật vào lệnh theo kế hoạch.")}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Tiếp tục kiểm soát tâm lý sau lệnh thua.")}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Giữ quy định tối thiểu 15 phút giữa các lệnh.")}</li>
                                                </ul>
                                            </li>
                                        )}

                                        {Number(report?.behavioral_analysis.rapid_fire_trades) >= 1 &&
                                            Number(report?.behavioral_analysis.rapid_fire_trades) <= 5 && (
                                                <li>
                                                    <span className="font-bold">⚠ {t("Warning (1 – 5):")}</span>{" "}
                                                    <span>{t("Có vài lệnh nhanh sau thua.")}</span>
                                                    <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                        <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Đặt quy định “cooldown” sau lệnh lỗ.")}</li>
                                                        <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Giới hạn max 2 lệnh/phiên.")}</li>
                                                        <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Ghi lại nhật ký cảm xúc khi giao dịch.")}</li>
                                                    </ul>
                                                </li>
                                            )}

                                        {Number(report?.behavioral_analysis.rapid_fire_trades) >= 6 && (
                                            <li>
                                                <span className="font-bold">❌ {t("Danger (> 5):")}</span>{" "}
                                                <span>{t("Chuỗi lệnh liên tục vượt kiểm soát.")}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Ngưng giao dịch ngay.")}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Đặt cơ chế khoá tài khoản sau X lệnh.")}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Tìm huấn luyện tâm lý để kiểm soát bốc đồng.")}</li>
                                                </ul>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={36} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 text-2xl font-semibold' style={{ color: '#00789D' }}>
                                    {t('2. Revenge Trades')}
                                </p>
                                <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                                    {t('Những lệnh được thực hiện ngay sau khi thua lỗ với mục đích “gỡ lại”. Đây là hành vi nguy hiểm vì thường dẫn đến tăng rủi ro không hợp lý.')}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Lệnh mở ngay sau thua với khối lượng lớn hơn.')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {report?.behavioral_analysis.revenge_trades} {t('lần/tuần')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng thấp càng tốt - Lower better')}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ {t('Good: 0')}</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ {t('Warning: 1')}</li>
                                        <li style={{ lineHeight: '48px' }}>❌ {t('Danger: ≥ 2')}</li>
                                    </ul>
                                </ul>

                                <p className='pt-3 text-2xl font-bold text-black-normal'>{t('Insight gợi ý')}</p>
                                <div className="p-4">
                                    <ul className="list-none pl-6 mb-6 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {Number(report?.behavioral_analysis.revenge_trades) === 0 && (
                                            <li>
                                                <span className="font-bold">
                                                    ✅ {t('Good (0):')}
                                                </span>{" "}
                                                <span>{t('Không có giao dịch trả thù.')}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Duy trì kỷ luật quản trị vốn.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tiếp tục viết nhật ký giao dịch để kiểm soát tâm lý.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ nguyên khối lượng giao dịch nhất quán.')}</li>
                                                </ul>
                                            </li>
                                        )}
                                        {Number(report?.behavioral_analysis.revenge_trades) === 1 && (
                                            <li>
                                                <span className="font-bold">
                                                    ⚠ {t('Warning (1)')}
                                                </span>{" "}
                                                <span>{t('Xuất hiện giao dịch trả thù.')}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Đặt nguyên tắc cứng (hard rule) không tăng lot sau lỗ.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ghi chú nguyên nhân tâm lý.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tạm dừng giao dịch khi bực tức')}</li>
                                                </ul>
                                            </li>
                                        )}
                                        {Number(report?.behavioral_analysis.revenge_trades) >= 2 && (
                                            <li>
                                                <span className="font-bold">
                                                    ❌ {t('Danger (≥ 2):')}
                                                </span>{" "}
                                                <span>{t('Giao dịch trả thù nhiều lần.')}</span>
                                                <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Dừng trading ngay.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giới hạn max lot cố định.')}</li>
                                                    <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Thực hiện kiểm soát tâm lý bắt buộc.')}</li>
                                                </ul>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={37} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 text-2xl font-semibold' style={{ color: '#00789D' }}>
                                    {t('3. High Frequency Trading')}
                                </p>
                                <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                                    {t('Giao dịch với tần suất cực cao trong khoảng thời gian ngắn, vượt quá mức bình thường của chiến lược. Nếu không nằm trong kế hoạch, đây có thể là dấu hiệu mất kỷ luật.')}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Số lệnh quá nhiều trong ngày (không theo chiến lược).')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {t('120 lệnh/ngày')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng thấp càng tốt - Lower better')}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ {t('Good: ≤ 50 lệnh/ngày (tùy plan)')}</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ {t('Warning: 51 – 100')}</li>
                                        <li style={{ lineHeight: '48px' }}>❌ {t('Danger: > 100')}</li>
                                    </ul>
                                </ul>

                                <p className='pt-3 text-2xl font-bold text-black-normal'>{t('Insight gợi ý')}</p>
                                <div className="p-4">
                                    <ul className="list-disc pl-6 mb-6 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        <li>
                                            <span className="font-bold">
                                                ✅ {t('Good (≤ 50 lệnh/ngày - tùy kế hoạch):')}
                                            </span>{" "}
                                            <span>{t('Tần suất giao dịch hợp lý.')}</span>
                                            <ul className="list-decimal pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ khối lượng lệnh trong plan.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Duy trì setup ít nhưng chất lượng.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tập trung vào phiên có thanh khoản.')}</li>
                                            </ul>
                                        </li>
                                        <li>
                                            <span className="font-bold">
                                                ⚠ {t('Warning (51 – 100)')}
                                            </span>{" "}
                                            <span>{t('Số lệnh tăng cao.')}</span>
                                            <ul className="list-decimal pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giới hạn max lệnh/ngày.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Lọc bỏ setup không đủ tiêu chuẩn.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Theo dõi phí/lot để tránh ăn mòn lãi.')}</li>
                                            </ul>
                                        </li>
                                        <li>
                                            <span className="font-bold">
                                                ❌ {t('Danger (> 100):')}
                                            </span>{" "}
                                            <span>{t('Over-trading nghiêm trọng.')}</span>
                                            <ul className="list-decimal pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ngưng trading ngay.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Reset lại kế hoạch giao dịch theo tần suất.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Sử dụng cài đặt quy định giới hạn (max trade/day).')}</li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={38} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 text-2xl font-semibold' style={{ color: '#00789D' }}>
                                    {t('4. Revenge Trading Detected')}
                                </p>
                                <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                                    {t('Chỉ số cảnh báo khi hệ thống phát hiện mẫu hành vi lặp lại của revenge trading. Đây là tín hiệu cho thấy trader cần dừng giao dịch và đánh giá lại tâm lý.')}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Hành vi trả thù sau chuỗi lỗ (pattern AI detect).')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {t('2 lần trong tháng')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng thấp càng tốt - Lower better')}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>
                                            ✅ <span className="font-bold">Good</span>: 0
                                        </li>
                                        <li style={{ lineHeight: '48px' }}>
                                            ⚠ <span className="font-bold">Warning</span>: 1
                                        </li>
                                        <li style={{ lineHeight: '48px' }}>
                                            ❌ <span className="font-bold">Danger</span>: ≥ 2
                                        </li>
                                    </ul>
                                </ul>

                                <p className='pt-3 text-2xl font-bold text-black-normal'>{t('Insight gợi ý')}</p>
                                <div className="p-4">
                                    <ul className="list-none pl-6 mb-6 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        <li>
                                            <span className="font-bold">
                                                ✅ {t('Good (0):')}
                                            </span>{" "}
                                            <span>{t('Tần suất giao dịch hợp lý. Không phát hiện giao dịch trả thù.')}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tiếp tục tuân thủ quản lý rủi ro.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ việc viết nhật ký chi tiết.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Duy trì tâm lý bình ổn.')}</li>
                                            </ul>
                                        </li>
                                        <li>
                                            <span className="font-bold">
                                                ⚠ {t('Warning (1)')}
                                            </span>{" "}
                                            <span>{t('Có dấu hiệu trả thù thị trường.')}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tạm dừng giao dịch 1–2 ngày.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Rà soát chuỗi lệnh lỗ để rút kinh nghiệm')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Áp dụng giới hạn max lot cố định.')}</li>
                                            </ul>
                                        </li>
                                        <li>
                                            <span className="font-bold">
                                                ❌ {t('Danger (≥ 2):')}
                                            </span>{" "}
                                            <span>{t('Trả thù thị trường lặp lại nhiều lần.')}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ngưng toàn bộ hoạt động giao dịch.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tìm coaching tâm lý trading.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Chỉ quay lại khi kỷ luật được phục hồi.')}</li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={39} />
                            </div>
                        </div>
                    </div>

                    <div className="w-full" id="part5">
                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 text-2xl font-semibold' style={{ color: '#00789D' }}>
                                    {t('5. Long Losing Streaks')}
                                </p>
                                <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                                    {t('Chuỗi thua kéo dài bất thường. Đây là tín hiệu tâm lý nguy hiểm vì dễ dẫn đến tuyệt vọng, tăng gấp khối lượng, hoặc bỏ kỷ luật.')}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Ghi chú:')}</strong> {t('Chuỗi thua kéo dài, gây tâm lý tiêu cực.')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Value')}:</strong> {t('8 lệnh thua liên tiếp')}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t('Chiều hướng:')}</strong> {t('Giá trị càng thấp càng tốt - Lower better')}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t('Ranges')}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>
                                            ✅ <span className="font-bold">Good</span>: ≤ 3
                                        </li>
                                        <li style={{ lineHeight: '48px' }}>
                                            ⚠ <span className="font-bold">Warning</span>: 4 – 5
                                        </li>
                                        <li style={{ lineHeight: '48px' }}>
                                            ❌ <span className="font-bold">Danger</span>: ≥ 6
                                        </li>
                                    </ul>
                                </ul>

                                <p className='pt-3 text-2xl font-bold text-black-normal'>{t('Insight gợi ý')}</p>
                                <div className="p-4">
                                    <ul className="list-none pl-6 mb-6 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        <li>
                                            <span className="font-bold">✅ {t('Good (≤ 3):')}</span>{" "}
                                            <span>{t('Chuỗi thua ngắn, kiểm soát tốt.')}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giữ kỷ luật SL/TP')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tiếp tục quản trị vốn ≤ 1%')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Viết nhật ký để phân tích nguyên nhân lỗ.')}</li>
                                            </ul>
                                        </li>
                                        <li>
                                            <span className="font-bold">⚠ {t('Warning (4 – 5)')}</span>{" "}
                                            <span>{t('Chuỗi thua bắt đầu kéo dài.')}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Giảm khối lượng xuống 50%.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Tạm dừng sau 3 lệnh thua liên tiếp.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Đánh giá setup và thị trường hiện tại.')}</li>
                                            </ul>
                                        </li>
                                        <li>
                                            <span className="font-bold">❌ {t('Danger (≥ 6):')}</span>{" "}
                                            <span>{t('Chuỗi thua quá dài.')}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Ngưng trading ngay')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Đánh giá lại toàn bộ hệ thống.')}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t('Chỉ quay lại khi có bằng chứng setup ổn định (demo/backtest).')}</li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={40} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <div>
                                    <div className="flex justify-center mt-10 mb-4">
                                        <img
                                            src="/downPdf/ftmo6.png"
                                            alt={t("Group 6")}
                                            className="w-40 h-40"
                                        />
                                    </div>
                                    <h1 className="text-4xl font-bold text-center mb-10">
                                        {t("Risk Management & Discipline Group")}
                                    </h1>
                                    <p className="font-semibold text-cts-cyan-700 mb-2 mt-4 text-2xl">
                                        {t("Nhóm 6: Risk – Quản trị rủi ro và kỷ luật")}
                                    </p>
                                    <p className="mb-4 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {t("Nhóm Risk phản ánh khả năng quản trị rủi ro và mức độ kỷ luật của trader trong quá trình giao dịch. Đây là nhóm chỉ số đóng vai trò như hệ thống cảnh báo sớm, giúp trader không vượt quá Ranges chịu đựng vốn và duy trì sự an toàn dài hạn. Nếu như các nhóm Financial, Performance, Trades và Volume cho thấy kết quả tài chính và cách vận hành chiến lược, nhóm Behavioral cho thấy mặt tâm lý, thì nhóm Risk chính là hàng rào bảo vệ cuối cùng để ngăn ngừa rủi ro vượt kiểm soát.")}
                                    </p>

                                    <p className="font-semibold text-cts-cyan-700 mb-2 text-2xl">
                                        {t("Ý nghĩa đối với trader")}
                                    </p>
                                    <ul className="list-disc pl-6 space-y-3 mb-4 text-2xl text-black-normal">
                                        <li>{t("Theo dõi mức sụt giảm vốn cực đại để kiểm soát khả năng chịu đựng drawdown.")}</li>
                                        <li>{t("Quản lý việc sử dụng đòn bẩy và margin để tránh margin call")}</li>
                                        <li>{t("Đảm bảo tính kỷ luật trong việc sử dụng stop-loss nhằm bảo vệ vốn.")}</li>
                                        <li>{t("Đánh giá sự tuân thủ hệ thống giao dịch thông qua điểm kỷ luật tổng hợp.")}</li>
                                    </ul>

                                    <p className="font-semibold text-cts-cyan-700 mb-2 text-2xl">
                                        {t("Danh mục chỉ số chính")}
                                    </p>
                                    <ol className="list-decimal pl-6 space-y-3 text-2xl mb-6 text-black-normal">
                                        <li>
                                            <span className="font-bold">{t("Max Drawdown")}</span> – {t("Số lượng lệnh được mở/đóng liên tục trong thời gian ngắn.")}
                                        </li>
                                        <li>
                                            <span className="font-bold">{t("Revenge Trades")}</span> – {t("Mức sụt giảm vốn lớn nhất so với đỉnh trong giai đoạn phân tích.")}
                                        </li>
                                        <li>
                                            <span className="font-bold">{t("Leverage Ratio / Margin Usage")}</span> – {t("Tỷ lệ đòn bẩy hoặc phần trăm margin sử dụng so với vốn.")}
                                        </li>
                                        <li>
                                            <span className="font-bold">{t("Stop-loss Compliance")}</span> – {t("Tỷ lệ lệnh có stop-loss hợp lệ (server-side).")}
                                        </li>
                                        <li>
                                            <span className="font-bold">{t("Discipline Score")}</span> – {t("Điểm tổng hợp kỷ luật dựa trên checklist, nhật ký và hành vi giao dịch.")}
                                        </li>
                                    </ol>

                                    <p className="text-2xl text-black-normal text-black-normal" style={{ lineHeight: '30px' }}>
                                        {t(
                                            "Nhóm Risk cho trader cái nhìn rõ ràng về mức độ an toàn và tính bền vững của hệ thống giao dịch. Khi kết hợp với 5 nhóm trước, trader không chỉ thấy hiệu quả tài chính, chất lượng chiến lược, hành vi và quy mô vốn, mà còn nắm trong tay công cụ quan trọng để bảo vệ tài khoản và duy trì sự ổn định lâu dài."
                                        )}
                                    </p>
                                </div>
                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={41} />
                            </div>
                        </div>
                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <p className='pt-10 text-2xl font-semibold' style={{ color: '#00789D' }}>
                                    {t("1. Max Drawdown")}
                                </p>
                                <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                                    {t("Mức sụt giảm vốn lớn nhất so với đỉnh trong giai đoạn phân tích. Đây là thước đo quan trọng để trader biết vốn có thể chịu được mức lỗ sâu đến đâu.")}
                                </p>

                                <ul className="text-2xl list-disc ml-8 text-black-normal">
                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t("Ghi chú:")}</strong> {t("Mức sụt giảm vốn lớn nhất so với đỉnh.")}
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t("Value")}:</strong> {report?.risk_management.max_drawdown_percent}%
                                    </li>

                                    <li style={{ lineHeight: '48px' }}>
                                        <strong>{t("Chiều hướng:")}</strong> {t("Giá trị càng thấp càng tốt - Lower better")}
                                    </li>

                                    <li style={{ lineHeight: '48px', listStyleType: 'none' }}>
                                        <strong>{t("Ranges")}:</strong>
                                    </li>

                                    <ul className="text-2xl list-decimal mt-3 ml-14">
                                        <li style={{ lineHeight: '48px' }}>✅ <span className="font-bold">Good</span>: ≤ 10%</li>
                                        <li style={{ lineHeight: '48px' }}>⚠ <span className="font-bold">Warning</span>: 10–20%</li>
                                        <li style={{ lineHeight: '48px' }}>❌ <span className="font-bold">Danger</span>: &gt; 20%</li>
                                    </ul>
                                </ul>

                                <p className='pt-3 text-2xl font-bold text-black-normal'>{t("Insight gợi ý")}</p>
                                <div className="p-4">
                                    <ul className="list-none pl-6  mb-6  text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {Number(report?.risk_management.max_drawdown_percent) <= 10 && <li>
                                            <span className="font-bold">
                                                ✅ {t("Good (≤ 10%):")}
                                            </span>{" "}
                                            <span>{t("Drawdown nhỏ, vốn an toàn.")}</span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Giữ risk/lệnh ≤ 1%.")}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Duy trì giới hạn lỗ/ngày.")}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Ghi nhận chuỗi drawdown ngắn để học kinh nghiệm.")}</li>
                                            </ul>
                                        </li>}
                                        {Number(report?.risk_management.max_drawdown_percent) > 10 && Number(report?.risk_management.max_drawdown_percent) < 20 && <li>
                                            <span className="font-bold">
                                                ⚠ {t("Warning (10–20%)")}
                                            </span>{" "}
                                            <span>
                                                {t("Drawdown bắt đầu cao.")}
                                            </span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Giảm khối lượng xuống 50–70%.")}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Tạm dừng khi lỗ ≥ 3R/ngày.")}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Review chiến lược gây drawdown.")}</li>
                                            </ul>
                                        </li>}
                                        {Number(report?.risk_management.max_drawdown_percent) >= 20 && <li>
                                            <span className="font-bold">
                                                ❌ {t("Danger (> 20%):")}
                                            </span>{" "}
                                            <span>
                                                {t("Drawdown quá sâu.")}
                                            </span>
                                            <ul className="list-none pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Ngưng trading ngay")}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Đánh giá lại toàn bộ hệ thống.")}</li>
                                                <li style={{ lineHeight: '48px', paddingLeft: '1rem' }}>_ {t("Quay lại demo/backtest trước khi giao dịch thật.")}</li>
                                            </ul>
                                        </li>}
                                    </ul>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={42} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <div className="mt-6 pdf-section">
                                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-cts-gray-200 shadow-cts-sm" style={{
                                        background: "linear-gradient(to bottom right, #F9FAFB, #FFFFFF)"
                                    }}>
                                        <div className="flex items-center gap-4 mb-6">
                                            <div>
                                                <div className="text-xl font-bold text-cts-gray-900 mb-1">
                                                    {t('EquitySimulation')}
                                                </div>
                                                <p className="text-sm text-cts-gray-600">{t('EquitySimulationDesc1')}</p>
                                            </div>
                                        </div>

                                        <div className="portlet mt-2">
                                            <div className="portlet__body pt-0">
                                                <form id="equity_simulator_form" action="#" method="post">
                                                    <div className="row">
                                                        {/* Capital */}
                                                        <div className="col-12 col-md-4 mt-2 mt-md-0">
                                                            <Tooltip title="Your starting capital for the simulation. Choose a realistic amount to see how your account could evolve. 👉 Example: $50,000.">
                                                                <label htmlFor="capital" className="col-form-label">
                                                                    <span>{t("Capital")}</span>
                                                                    <i className="bi bi-info-circle ms-1"></i>
                                                                </label>
                                                            </Tooltip>
                                                            <div className="number-input__parent">
                                                                <div
                                                                    className="form-control text-success number-input__input flex items-center justify-center"
                                                                    data-decimals="0"
                                                                >{liveTrade?.equity ? Number(liveTrade.equity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : report ? Number(report?.today_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</div>
                                                            </div>
                                                            <div className="invalid-feedback">{t('min')}. 1</div>
                                                        </div>
                                                        {/* Win rate */}
                                                        <div className="col-12 col-md-4 mt-2 mt-md-0">
                                                            <Tooltip title="The percentage of winning trades you expect on average. Even a modest win rate can be profitable with a good RRR. 👉 Try 50% to start.">
                                                                <label htmlFor="win" className="col-form-label">
                                                                    <span>{t('winrate')}</span>
                                                                    <i className="bi bi-info-circle ms-1"></i>
                                                                </label>
                                                            </Tooltip>
                                                            <div className="number-input__parent">
                                                                <div
                                                                    className="form-control text-success number-input__input flex items-center justify-center"
                                                                    data-decimals="0"
                                                                >{win}</div>
                                                            </div>
                                                            <div className="invalid-feedback">0 - 100</div>
                                                        </div>
                                                        {/* RRR */}
                                                        <div className="col-12 col-md-4 mt-2 mt-md-0">
                                                            <Tooltip title="The reward compared to the risk per trade. A higher RRR means more upside per risk taken. 👉 Try 1:2 to see the advantage clearly.">
                                                                <label htmlFor="rrr" className="col-form-label">
                                                                    <span>RRR</span>
                                                                    <i className="bi bi-info-circle ms-1"></i>
                                                                </label>
                                                            </Tooltip>
                                                            <div className="number-input__parent">
                                                                <div
                                                                    className="form-control text-success number-input__input flex items-center justify-center"
                                                                    data-decimals="0"
                                                                >{rrr != null ? Number(rrr).toFixed(2) : '0.00'}</div>
                                                            </div>
                                                            <div className="invalid-feedback">{t('isrequired')}</div>
                                                        </div>
                                                    </div>
                                                    <div className="row mt-2">
                                                        {/* Iterations */}
                                                        <div className="col-12 col-md-4 mt-2">
                                                            <Tooltip title="Number of trades in this simulation. More trades give you a clearer and more realistic account journey. 👉 Try 100 trades to see long-term growth.">
                                                                <label htmlFor="iterations" className="col-form-label">
                                                                    <span>{t("Iterations")}</span>
                                                                    <i className="bi bi-info-circle ms-1"></i>
                                                                </label>
                                                            </Tooltip>
                                                            <div className="number-input__parent">
                                                                <div
                                                                    className="form-control text-success number-input__input flex items-center justify-center"
                                                                    data-decimals="0"
                                                                >{iterations}</div>
                                                            </div>
                                                            <div className="invalid-feedback">{t("min")}. 1</div>
                                                        </div>
                                                        {/* Lines */}
                                                        <div className="col-12 col-md-4 mt-2 mt-md-0">
                                                            <Tooltip title="Number of equity curves to display at once. More lines = more possible scenarios to compare. 👉 Try 10 lines to explore different outcomes.">
                                                                <label htmlFor="lines" className="col-form-label">
                                                                    <span>{t("Lines")}</span>
                                                                    <i className="bi bi-info-circle ms-1"></i>
                                                                </label>
                                                            </Tooltip>
                                                            <div className="number-input__parent">
                                                                <div
                                                                    className="form-control text-success number-input__input flex items-center justify-center"
                                                                    data-decimals="0"
                                                                >{lines}</div>
                                                            </div>
                                                        </div>
                                                        {/* Risk percent */}
                                                        <div className="col-12 col-md-4 mt-2 mt-md-0">
                                                            <Tooltip title="The percentage of capital you risk per trade. A balanced level keeps you safe and growing. 👉 Try 2% for stability and growth.">
                                                                <label htmlFor="lines" className="col-form-label">
                                                                    <span>{t('RiskPercent')}</span>
                                                                    <i className="bi bi-info-circle ms-1"></i>
                                                                </label>
                                                            </Tooltip>
                                                            <div className="number-input__parent">
                                                                <div
                                                                    className="form-control text-success number-input__input flex items-center justify-center"
                                                                    data-decimals="0"
                                                                >{risk}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <hr className="my-4 my-lg-5" />
                                                </form>
                                            </div>
                                        </div>

                                        {equityCurves && avgDataTable ? (
                                            <TradingChart
                                                equityCurves={equityCurves}
                                                avgDataTable={avgDataTable}
                                            />
                                        ) : (
                                            <>
                                                <div className="bg-trang rounded-lg p-8 border text-center" style={{ borderColor: '#F2F4F6' }}>
                                                    <div className="text-4xl mb-4">⏳</div>
                                                    <h4 className="text-lg font-semibold text-cts-gray-900 mb-2">{t('LoadingSimulation')}</h4>
                                                    <p className="text-cts-gray-600 mb-4">
                                                        {t('EquitySimulationDesc2')}

                                                    </p>
                                                    <div className="text-sm text-cts-gray-500">
                                                        {report ? (
                                                            <div className="space-y-1">
                                                                <div>{t('UsingActualtradingData')}:</div>
                                                                <div className="grid grid-cols-2 gap-4 text-xs">
                                                                    <div>{t('Capital')}: <span className="font-semibold">${report?.balance_init?.toLocaleString()}</span></div>
                                                                    <div>{t('winrate')}: <span className="font-semibold">{report?.win_rate?.toFixed(1)}%</span></div>
                                                                    <div>RRR: <span className="font-semibold">{report?.mean_RRR?.toFixed(2)}</span></div>
                                                                    <div>{t('TotalTrades')}: <span className="font-semibold">{report?.total_trades}</span></div>
                                                                    <div>{t('RawTrades')}: <span className="font-semibold">{report?.raw_trades?.length || 0}</span></div>
                                                                    <div>{t("Simulation")}: <span className="font-semibold">{Math.min(200, report?.raw_trades?.length || 0)}</span></div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            'Loading trading data...'
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        <div className="equity-simulation__table mt-4">
                                            <div className="">
                                                <div className="equity-simulation__row">
                                                    <div className="equity-simulation__col bg-trang">
                                                        <Tooltip title="Minimum Equity out of all simulations.">
                                                            <div className="text-primary">
                                                                <span>{t('Minequity')}</span>
                                                                <i className="bi bi-info-circle ms-1"></i>
                                                            </div>
                                                        </Tooltip>
                                                    </div>
                                                    <div className="equity-simulation__col bg--mild">{avgDataTable?.min_equity != null ? Number(avgDataTable.min_equity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</div>
                                                </div>
                                                <div className="equity-simulation__row">
                                                    <div className="equity-simulation__col bg-trang">
                                                        <Tooltip title="Maximum Equity out of all simulations.">
                                                            <div className="text-primary">
                                                                <span>{t('Maxequity')}</span>
                                                                <i className="bi bi-info-circle ms-1"></i>
                                                            </div>
                                                        </Tooltip>
                                                    </div>
                                                    <div className="equity-simulation__col bg--mild">{avgDataTable?.max_equity != null ? Number(avgDataTable.max_equity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</div>
                                                </div>
                                                <div className="equity-simulation__row">
                                                    <div className="equity-simulation__col bg-trang">
                                                        <Tooltip title="Most winners in a row.">
                                                            <div className="text-primary">
                                                                <span>{t('MaxConsecutiveWinners')}</span>
                                                                <i className="bi bi-info-circle ms-1"></i>
                                                            </div>
                                                        </Tooltip>
                                                    </div>
                                                    <div className="equity-simulation__col bg--mild">{avgDataTable?.max_consecutive_winners != null ? `${avgDataTable.max_consecutive_winners}` : '-'}</div>
                                                </div>
                                                <div className="equity-simulation__row">
                                                    <div className="equity-simulation__col bg-trang">
                                                        <Tooltip title="Most losers in a row.">
                                                            <div className="text-primary">
                                                                <span>{t('MaxConsecutiveLosers')}</span>
                                                                <i className="bi bi-info-circle ms-1"></i>
                                                            </div>
                                                        </Tooltip>
                                                    </div>
                                                    <div className="equity-simulation__col bg--mild">{avgDataTable?.max_consecutive_losers != null ? `${avgDataTable.max_consecutive_losers}` : '-'}</div>
                                                </div>
                                            </div>
                                            <div className="">
                                                <div className="equity-simulation__row">
                                                    <div className="equity-simulation__col bg-trang">
                                                        <Tooltip title="Maximum drawdown out of all simulations.">
                                                            <div className="text-primary">
                                                                <span>{t('MaxDrawdown')}</span>
                                                                <i className="bi bi-info-circle ms-1"></i>
                                                            </div>
                                                        </Tooltip>
                                                    </div>
                                                    <div className="equity-simulation__col bg--mild">{avgDataTable?.max_drawdown_percent != null ? `${avgDataTable.max_drawdown_percent}%` : '-'}</div>
                                                </div>
                                                <div className="equity-simulation__row">
                                                    <div className="equity-simulation__col bg-trang">
                                                        <Tooltip title="Average drawdown out of all simulations.">
                                                            <div className="text-primary">
                                                                <span>{t('Avgdrawdown')}</span>
                                                                <i className="bi bi-info-circle ms-1"></i>
                                                            </div>
                                                        </Tooltip>
                                                    </div>
                                                    <div className="equity-simulation__col bg--mild">{avgDataTable?.avg_drawdown_percent != null ? `${avgDataTable.avg_drawdown_percent}%` : '-'}</div>
                                                </div>
                                                <div className="equity-simulation__row">
                                                    <div className="equity-simulation__col bg-trang">
                                                        <Tooltip title="Average maximum drawdown of all lines.">
                                                            <div className="text-primary">
                                                                <span>{t('Avgmaxdrawdown')}.</span>
                                                                <i className="bi bi-info-circle ms-1"></i>
                                                            </div>
                                                        </Tooltip>
                                                    </div>
                                                    <div className="equity-simulation__col bg--mild">10.12%</div>
                                                </div>
                                                <div className="equity-simulation__row d-none d-xl-grid">
                                                    <div className="equity-simulation__col">&nbsp;</div>
                                                    <div className="equity-simulation__col bg--mild">&nbsp;</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={`px-20`}>
                                <FooterPart page={43} />
                            </div>
                        </div>

                        {/* <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                        <HeaderPart />
                        <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                          <p className='pt-10 text-2xl font-semibold' style={{ color: '#00789D' }}>
                            {t('2. Leverage Ratio / Margin Usage')}
                          </p>
                          <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                            {t('Tỷ lệ đòn bẩy hoặc phần trăm margin sử dụng trên vốn. Đây là chỉ số đánh giá mức độ rủi ro tài chính mỗi khi vào lệnh; sử dụng quá mức có thể dẫn đến margin call.')}
                          </p>
                          <ul className="list-disc pl-6 space-y-2 mb-6 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                            <li>
                              <b>{t('Ghi chú:')}</b> {t('Mức sử dụng đòn bẩy hoặc margin so với vốn.')}
                            </li>
                            <li>
                              <b>{t('Value')}:</b> 1:35 (Margin Used = 30%)
                            </li>
                            <li>
                              <b>{t('Chiều hướng:')}</b> {t('Giá trị càng thấp càng tốt - Lower better')}
                            </li>
                            <li>
                              <b className="block pb-2">{t('Ranges')}:</b>
                              <ul className="pl-6 space-y-3 list-[circle]">
                                <li>
                                  <span className="text-cts-green-600 text-lg">✅</span>
                                  <span className="font-bold"> Good</span>: {t('Leverage ≤ 1:20 hoặc Margin ≤ 20%')}
                                </li>
                                <li>
                                  <span className="text-cts-yellow-600 text-lg">⚠</span>
                                  <span className="font-bold"> Warning</span>: {t('1:20 – 1:50 hoặc 20–50% margin')}
                                </li>
                                <li>
                                  <span className="text-cts-red-600 text-lg">❌</span>
                                  <span className="font-bold"> Danger</span>: {t('> 1:50 hoặc Margin > 50%')}
                                </li>
                              </ul>
                            </li>
                          </ul>
                          <p className='pt-3 text-2xl font-bold text-black-normal'>{t('Insight gợi ý')}</p>
                          <div className="p-4">
                            <ul className="list-disc pl-6 mb-6 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                              <li>
                                <span className="font-bold">
                                  ✅ {t('Good (Leverage ≤ 1:20 hoặc Margin ≤ 20%):')}
                                </span>{" "}
                                <span>{t('Đòn bẩy ở mức an toàn.')}</span>
                                <ol className="list-decimal pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                  <li>{t('Duy trì risk/lệnh ≤ 1%.')}</li>
                                  <li>{t('Giữ margin free ≥ 80%.')}</li>
                                  <li>{t('Chỉ tăng lot khi vốn tăng.')}</li>
                                </ol>
                              </li>
                              <li>
                                <span className="font-bold">
                                  ⚠ {t('Warning (1:20 – 1:50 hoặc 20–50% margin)')}
                                </span>{" "}
                                <span>{t('Đòn bẩy trung bình cao.')}</span>
                                <ol className="list-decimal pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                  <li>{t('Giảm số lệnh mở cùng lúc.')}</li>
                                  <li>{t('Tăng vốn hoặc giảm lot size.')}</li>
                                  <li>{t('Tránh trade khi tin tức biến động mạnh.')}</li>
                                </ol>
                              </li>
                              <li>
                                <span className="font-bold">
                                  ❌ {t('Danger (> 1:50 hoặc Margin > 50%):')}
                                </span>{" "}
                                <span>{t('Đòn bẩy cực cao.')}</span>
                                <ol className="list-decimal pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                  <li>{t('Đóng ngay các lệnh dư thừa.')}</li>
                                  <li>{t('Reset kế hoạch quản trị vốn.')}</li>
                                  <li>{t('Giữ leverage ≤ 1:20 để tránh margin call.')}</li>
                                </ol>
                              </li>
                            </ul>
                          </div>
                        </div>
            
                        <div className={`px-20`}>
                          <FooterPart page={41} />
                        </div>
                      </div>
            
                      <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                        <HeaderPart />
                        <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                          <p className='pt-10 text-2xl font-semibold' style={{ color: '#00789D' }}>
                            {t('3. Stop-loss Compliance')} / Stop-loss Compliance
                          </p>
                          <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                            {t('Tỷ lệ lệnh có đặt stop-loss hợp lệ (server-side). Đây là chỉ số then chốt phản ánh mức độ tuân thủ kỷ luật cắt lỗ để bảo vệ vốn.')}
                            <br />
                            {t('The ratio of trades with valid stop-loss (server-side). This key metric reflects how well traders follow stop-loss rules to protect capital.')}
                          </p>
                          <ul className="list-disc pl-6 space-y-2 mb-6 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                            <li>
                              <b>{t('Ghi chú:')}</b> {t('Tỷ lệ lệnh có SL hợp lệ (server-side).')}
                            </li>
                            <li>
                              <b>{t('Value')}:</b> 85% {t('lệnh có SL')}
                            </li>
                            <li>
                              <b>{t('Chiều hướng:')}</b> {t('Giá trị càng cao càng tốt - Higher better')}
                            </li>
                            <li>
                              <b className="block pb-2">{t('Ranges')}</b>
                              <ul className="pl-6 space-y-3 list-[circle]">
                                <li>
                                  <span className="text-cts-green-600 text-lg">✅</span>
                                  <span className="font-bold"> Good</span>: ≥ 95%
                                </li>
                                <li>
                                  <span className="text-cts-yellow-600 text-lg">⚠</span>
                                  <span className="font-bold"> Warning</span>: 80–95%
                                </li>
                                <li>
                                  <span className="text-cts-red-600 text-lg">❌</span>
                                  <span className="font-bold"> Danger</span>: &lt; 80%
                                </li>
                              </ul>
                            </li>
                          </ul>
            
                          <p className='pt-3 text-2xl font-bold text-black-normal'>{t('Insight gợi ý')}:</p>
                          <div className="p-4">
                            <ul className="list-disc pl-6 mb-6 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                              <li>
                                <span className="font-bold">✅ {t('Good (≥ 95%):')}</span>{" "}
                                <span>{t('SL đặt đầy đủ.')}</span>
                                <ol className="list-decimal pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                  <li>{t('Tiếp tục tuân thủ OCO (SL/TP cùng lúc)')}</li>
                                  <li>{t('Giữ RR ≥ 1.5 khi đặt SL/TP')}</li>
                                  <li>{t('Nhật ký cách SL bảo vệ vốn')}</li>
                                </ol>
                              </li>
                              <li>
                                <span className="font-bold">⚠ {t('Warning (80–95%):')}</span>{" "}
                                <span>{t('Một số lệnh không có SL')}</span>
                                <ol className="list-decimal pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                  <li>{t('Bắt buộc check-list SL trước khi vào lệnh')}</li>
                                  <li>{t('Tự động hóa SL qua EA/Script')}</li>
                                  <li>{t('Review lệnh thiếu SL và lý do')}</li>
                                </ol>
                              </li>
                              <li>
                                <span className="font-bold">❌ {t('Danger (< 80%):')}</span>{" "}
                                <span>{t('Phần lớn lệnh không có SL')}</span>
                                <ol className="list-decimal pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                  <li>{t('Ngưng trading ngay')}</li>
                                  <li>{t('Bật rule bắt buộc SL server-side')}</li>
                                  <li>{t('Reset toàn bộ quy trình vào lệnh')}</li>
                                </ol>
                              </li>
                            </ul>
                          </div>
                        </div>
            
                        <div className={`px-20`}>
                          <FooterPart page={42} />
                        </div>
                      </div>
            
                      <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                        <HeaderPart />
                        <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                          <p className='pt-10 text-2xl font-semibold' style={{ color: '#00789D' }}>
                            {t('4. Discipline Score')}
                          </p>
                          <p className='pt-3 text-2xl text-black-normal' style={{ lineHeight: '34px' }}>
                            {t('Điểm tổng hợp kỷ luật, được xây dựng dựa trên checklist, journaling và hành vi vào/ra lệnh. Đây là chỉ số mang tính tổng quát, đánh giá trader có tuân thủ hệ thống của chính mình hay không.')}
                          </p>
                          <ul className="list-disc pl-6 space-y-2 mb-6  text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                            <li>
                              <b>{t('Ghi chú:')}</b> {t('Điểm tổng hợp kỷ luật (dựa trên checklist, journaling, hành vi).')}
                            </li>
                            <li>
                              <b>{t('Value')}:</b>72/100
                            </li>
                            <li>
                              <b>{t('Chiều hướng:')}</b> {t('Giá trị càng cao càng tốt - Higher better')}
                            </li>
                            <li>
                              <b className="block pb-2">{t('Ranges')}:</b>
                              <ul className="pl-6 space-y-3 list-[circle]">
                                <li>
                                  <span className="text-cts-green-600 text-lg">✅</span>
                                  <span className="font-bold"> Good</span>: ≥ 80
                                </li>
                                <li>
                                  <span className="text-cts-yellow-600 text-lg">⚠</span>
                                  <span className="font-bold"> Warning</span>: 60–79
                                </li>
                                <li>
                                  <span className="text-cts-red-600 text-lg">❌</span>
                                  <span className="font-bold"> Danger</span>: &lt; 60
                                </li>
                              </ul>
                            </li>
                          </ul>
                          <p className='pt-3 text-2xl font-bold text-black-normal'>{t('Insight gợi ý')}</p>
                          <div className="p-4">
                            <ul className="list-disc pl-6  mb-6  text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                              <li>
                                <span className="font-bold">
                                  ✅ {t('Good (≥ 80):')}
                                </span>{" "}
                                <span>{t('Kỷ luật tốt.')}</span>
                                <ol className="list-decimal pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                  <li>{t('Duy trì checklist sáng/tối.')}</li>
                                  <li>{t('Tiếp tục journaling mỗi lệnh.')}</li>
                                  <li>{t('Giữ 3 mục tiêu kỷ luật/ngày.')}</li>
                                </ol>
                              </li>
                              <li>
                                <span className="font-bold">
                                  ⚠ {t('Warning (60–79)')}
                                </span>{" "}
                                <span>
                                  {t('Kỷ luật giảm.')}
                                </span>
                                <ol className="list-decimal pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                  <li>{t('Rà soát lý do bỏ checklist.')}</li>
                                  <li>{t('Tham gia coaching/kỷ luật nhóm.')}</li>
                                  <li>{t('Tăng cường ghi chú cảm xúc khi trade.')}</li>
                                </ol>
                              </li>
                              <li>
                                <span className="font-bold">
                                  ❌ {t('Danger (< 60):')}
                                </span>{" "}
                                <span>
                                  {t('Điểm kỷ luật thấp.')}
                                </span>
                                <ol className="list-decimal pl-6 mt-2 space-y-1" style={{ lineHeight: '34px' }}>
                                  <li>{t('Ngưng giao dịch thật.')}</li>
                                  <li>{t('Tham gia thử thách 13 tuần hoặc huấn luyện 1–1.')}</li>
                                  <li>{t('Chỉ trở lại khi Discipline Score ≥ 80.')}</li>
                                </ol>
                              </li>
                            </ul>
                          </div>
                        </div>
            
                        <div className={`px-20`}>
                          <FooterPart page={43} />
                        </div>
                      </div> */}

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <div className="text-center mb-8 mt-10">
                                    <h1 className="text-3xl font-bold text-cts-blue-900">{t('DỰ BÁO')}</h1>
                                </div>
                                <div className="space-y-6 text-2xl leading-[34px]">
                                    <p className="pt-3 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {t('Trong thế giới trading, kẻ sống sót không phải là người nhanh nhất, mà là người kiểm soát tốt nhất.')} <strong>{t('Metrix Rule Report')}</strong> {t('của LUMIR không hứa hẹn một “chiếc đũa thần”, nhưng mang đến một hệ thống đo lường rõ ràng, cảnh báo kịp thời, và hành động có kỷ luật.')}
                                    </p>
                                </div>
                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={44} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <div className="text-center mb-8 mt-10">
                                    <h1 className="text-3xl font-bold text-cts-blue-900">{t('TỔNG KẾT')}</h1>
                                </div>
                                <div className="space-y-6 text-2xl leading-[34px]">
                                    <p className="pt-3 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {t('Trong thế giới trading, kẻ sống sót không phải là người nhanh nhất, mà là người kiểm soát tốt nhất.')} <strong>{t('Metrix Rule Report')}</strong> {t('của LUMIR không hứa hẹn một “chiếc đũa thần”, nhưng mang đến một hệ thống đo lường rõ ràng, cảnh báo kịp thời, và hành động có kỷ luật.')}
                                    </p>
                                    <p className="pt-3 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {t('Toàn bộ hệ thống 6 nhóm chỉ số đã tạo nên một')} <strong>{t('bức tranh toàn diện')}</strong> {t('về hoạt động giao dịch, từ sức khỏe tài chính, chất lượng chiến lược, hành vi thực thi, quy mô vốn, tâm lý đến kỷ luật và quản trị rủi ro. Mỗi nhóm có vai trò riêng, nhưng khi kết hợp lại, chúng tạo thành một khung tham chiếu đầy đủ để trader vừa có thể')} <strong>{t('đo lường, vừa điều chỉnh, vừa tối ưu')}</strong> {t('phong cách giao dịch của mình.')}
                                    </p>
                                    <ul className="list-disc pl-6 space-y-2 leading-[34px] text-black-normal">
                                        <li><strong>{t('Financial')}:</strong> {t('Cho thấy sức khỏe tài chính cốt lõi và mức “ăn mòn” từ chi phí.')}</li>
                                        <li><strong>{t('Performance')}:</strong> {t('Đo lường hiệu quả, độ ổn định và lợi thế thống kê của chiến lược.')}</li>
                                        <li><strong>{t('Trades')}:</strong> {t('Phản ánh thói quen triển khai và các điểm cực trị trong thực tế giao dịch.')}</li>
                                        <li><strong>{t('Volume')}:</strong> {t('Kiểm soát quy mô vốn, sự nhất quán trong khối lượng và rủi ro overleverage.')}</li>
                                        <li><strong>{t('Behavioral')}:</strong> {t('Phát hiện hành vi và tâm lý tiêu cực, từ overtrading đến revengetrading.')}</li>
                                        <li><strong>{t('Risk')}:</strong> {t('Hệ thống cảnh báo sớm về rủi ro, kỷ luật và khả năng bảo vệ tài khoản.')}</li>
                                    </ul>

                                    <div className="space-y-4">
                                        <h2 className="text-2xl font-bold mb-3" style={{ color: '#00789D' }}>{t('Cách sử dụng')}</h2>
                                        <p className="pt-3 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                            {t('Bộ khung')} <strong>{t('6 nhóm chỉ số Matrix Rule Report')}</strong> {t('không chỉ là báo cáo số liệu, mà là bản đồ định hướng để mỗi trader xây dựng sự nghiệp giao dịch bền vững, kỷ luật và hiệu quả.')}
                                        </p>
                                        <ul className="list-disc pl-6 space-y-2 leading-[34px] text-black-normal">
                                            <li><strong style={{ color: '#00789D' }}>{t('Đối với trader mới:')}</strong> {t('bắt đầu từ nhóm')} <strong>{t('Financial')}</strong> {t('và')} <strong>{t('Risk')}</strong> {t('để đảm bảo an toàn vốn.')}</li>
                                            <li><strong style={{ color: '#00789D' }}>{t('Trader có kinh nghiệm:')}</strong> {t('tập trung vào')} <strong>{t('Performance')}</strong> {t('và')} <strong>{t('Behavioral')}</strong>, {t('dùng Risk như "hàng rào cuối cùng".')}</li>
                                            <li><strong style={{ color: '#00789D' }}>{t('Tổ chức/quỹ giao dịch:')}</strong> {t('dùng toàn bộ khung 6 nhóm như dashboard chuẩn hóa.')}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={45} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20" style={{ height: '1384px', overflow: 'hidden' }}>
                                <div className="space-y-6 text-2xl  leading-[34px]">
                                    <p className="pt-3 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                        {t('Đây là cuốn cẩm nang để bạn biến thống kê thành')}
                                        <strong> {t('lợi thế')} </strong>
                                        {t('biến thua lỗ thành')}
                                        <strong> {t('bài học')} </strong>
                                        {t('và biến sự kiên định thành')}
                                        <strong> {t('lợi nhuận bền vững.')} </strong>
                                        {t('Tương lai giao dịch của bạn bắt đầu từ những quy tắc kỷ luật bạn thực hiện hôm nay.')}
                                    </p>
                                    <p className='w-full h-0.5 mt-2 mb-8 bg-black-cts' />
                                    <ul className="text-3xl leading-[32px] font-medium text-cts-gray-800 space-y-3 list-decimal pl-5">
                                        <li className="text-cts-blue-650" style={{ color: '#0B769F' }}>
                                            {t('Mỗi cuối ngày/tuần: cập nhật')}{" "}
                                            <span className="text-cts-orange-550 font-bold">{t('33 chỉ số')}</span>{t(', tick trạng thái (')}
                                            <span className="text-cts-green-600">✅</span>{" "}
                                            <span className="text-cts-yellow-500">⚠️</span>{" "}
                                            <span className="text-cts-red-600">❌</span>).
                                        </li>

                                        <li className="text-cts-blue-650" style={{ color: '#0B769F' }}>
                                            {t('Khi có hệ thống cảnh báo khuyến nghị')}{" "}
                                            <span className="text-cts-yellow-500">⚠️</span> {t('hoặc')}{" "}
                                            <span className="text-cts-blue-650">❌</span>: {t('thực thi')}
                                            <span className="text-cts-orange-550 font-bold" style={{ color: '#CC9900' }}> {t('3 insight')}</span> {t('ngay trong')}
                                            <span className="text-cts-blue-650 font-bold" style={{ color: '#CC9900' }}> {t('phiên kế tiếp')}</span>.
                                        </li>

                                        <li className="text-cts-blue-650" style={{ color: '#0B769F' }}>
                                            {t('Kích hoạt')}
                                            <span className="text-cts-orange-550 font-bold" style={{ color: '#CC9900' }}> {t('Rules R1–R14')}</span> {t('để bật cảnh báo tự động trên')}
                                            <span className="text-cts-blue-650 font-bold"> {t('dashboard')}</span>.
                                        </li>
                                    </ul>
                                </div>
                                <p className='w-full h-0.5 mt-2 mb-8 bg-black-cts' />
                                <p className="pt-3 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                    {t('Giao dịch không chỉ là cuộc chơi của chiến lược, mà là bài kiểm tra của kỷ luật và quản trị rủi ro.')}
                                </p>
                                <p className="pt-3 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                    {t('Với')}
                                    <strong> {t('33 chỉ số LIVE METRIX')} </strong>
                                    {t('và')}
                                    <strong> {t('14 rule cảnh báo trên nền tảng BeQ Ops LUMIR')}, </strong>
                                    {t('bạn đã có trong tay một')}
                                    <strong> {t('playbook')} </strong>
                                    {t('hoàn chỉnh – nơi dữ liệu dẫn đường, và hành động tạo ra kết quả.')}
                                </p>
                                <p className="pt-3 text-2xl text-black-normal" style={{ lineHeight: '34px' }}>
                                    {t('Hãy cập nhật tại')} www.lumir.finance {t('để theo dõi và thực thi từng rule. Từng bước nhỏ kỷ luật hôm nay sẽ tạo nên thành quả lớn ngày mai.')}
                                </p>
                                {report?.behavioral_analysis && (
                                    <div className="grid grid-cols-2 mt-6 gap-6">
                                        {/* Behavioral Analysis Section */}
                                        <div className="bg-background-secondary rounded-1 p-6 border border-cts-gray-200 no-boder">
                                            <div className="flex items-center gap-3 mb-4">
                                                <h3 className="text-headings-h6 text-content-primary"><Tooltip title="Analysis of trading behaviors and patterns that may impact results."><span>{t('BehavioralAnalysis')}</span></Tooltip></h3>
                                            </div>

                                            <div className="space-y-3">
                                                <div className="flex justify-between items-center p-3 bg-trang rounded-lg shadow-cts-sm">
                                                    <Tooltip title="High number of quick, back-to-back trades in a short period."><span className="text-cts-gray-700 font-medium whitespace-nowrap">{t('RapidFireTrades')}</span></Tooltip>
                                                    <span className="text-cts-blue-600 font-semibold">{report?.behavioral_analysis.rapid_fire_trades || 0}</span>
                                                </div>

                                                <div className="flex justify-between items-center p-3 bg-trang rounded-lg shadow-cts-sm">
                                                    <Tooltip title="Trades taken impulsively to recover recent losses."><span className="text-cts-gray-700 font-medium whitespace-nowrap">{t('RevengeTrades')}</span></Tooltip>
                                                    <span className="text-cts-red-600 font-semibold">{report?.behavioral_analysis.revenge_trades || 0}</span>
                                                </div>

                                                <div className="flex justify-between items-center p-3 bg-trang rounded-lg shadow-cts-sm">
                                                    <Tooltip title="Longest streak of winning trades in a row."><span className="text-cts-gray-700 font-medium whitespace-nowrap">{t('MaxConsecutiveWins')}</span></Tooltip>
                                                    <span className="text-cts-green-600 font-semibold">{report?.behavioral_analysis.max_consecutive_wins || 0}</span>
                                                </div>

                                                <div className="flex justify-between items-center p-3 bg-trang rounded-lg shadow-cts-sm">
                                                    <Tooltip title="Longest streak of losing trades in a row."><span className="text-cts-gray-700 font-medium whitespace-nowrap">{t('MaxConsecutiveLosses')}</span></Tooltip>
                                                    <span className="text-cts-red-600 font-semibold">{report?.behavioral_analysis.max_consecutive_losses || 0}</span>
                                                </div>
                                            </div>

                                            {/* Behavioral Flags */}
                                            {report && report?.behavioral_analysis.behavioral_flags && (
                                                <div className="mt-4">
                                                    <h4 className="text-lg font-semibold text-cts-gray-800 mb-3"><Tooltip title="Indicators that highlight potential risk-prone behaviors."><span>{t('BehavioralFlags')}</span></Tooltip></h4>
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2">
                                                            <div className={`w-3 h-3 rounded-full ${report?.behavioral_analysis.behavioral_flags.high_frequency_trading ? 'bg-cts-red-500' : 'bg-cts-green-500'}`}></div>
                                                            <Tooltip title="Very frequent trading activity, which may increase risk."><span className="text-sm text-cts-gray-700 whitespace-nowrap">{t('HighFrequencyTrading')}</span></Tooltip>
                                                            <span className={`ml-auto text-xs px-2 py-1 rounded-full ${report?.behavioral_analysis.behavioral_flags.high_frequency_trading ? 'bg-cts-red-100 text-cts-red-800' : 'bg-cts-green-100 text-cts-green-800'}`}>
                                                                {report?.behavioral_analysis.behavioral_flags.high_frequency_trading ? 'Yes' : 'No'}
                                                            </span>
                                                        </div>

                                                        <div className="flex items-center gap-2">
                                                            <div className={`w-3 h-3 rounded-full ${report?.behavioral_analysis.behavioral_flags.revenge_trading_detected ? 'bg-cts-red-500' : 'bg-cts-green-500'}`}></div>
                                                            <Tooltip title="Signals taking trades driven by emotions after losses."><span className="text-sm text-cts-gray-700 whitespace-nowrap">{t('RevengeTradingDetected')}</span></Tooltip>
                                                            <span className={`ml-auto text-xs px-2 py-1 rounded-full ${report?.behavioral_analysis.behavioral_flags.revenge_trading_detected ? 'bg-cts-red-100 text-cts-red-800' : 'bg-cts-green-100 text-cts-green-800'}`}>
                                                                {report?.behavioral_analysis.behavioral_flags.revenge_trading_detected ? 'Yes' : 'No'}
                                                            </span>
                                                        </div>

                                                        <div className="flex items-center gap-2">
                                                            <div className={`w-3 h-3 rounded-full ${report?.behavioral_analysis.behavioral_flags.long_losing_streaks ? 'bg-cts-red-500' : 'bg-cts-green-500'}`}></div>
                                                            <Tooltip title="Extended sequences of losses that may require risk adjustments."><span className="text-sm text-cts-gray-700 whitespace-nowrap">{t('LongLosingStreaks')}</span></Tooltip>
                                                            <span className={`ml-auto text-xs px-2 py-1 rounded-full ${report?.behavioral_analysis.behavioral_flags.long_losing_streaks ? 'bg-cts-red-100 text-cts-red-800' : 'bg-cts-green-100 text-cts-green-800'}`}>
                                                                {report?.behavioral_analysis.behavioral_flags.long_losing_streaks ? 'Yes' : 'No'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="space-y-3">
                                            {/* Risk Management Section */}
                                            <div className="bg-background-secondary rounded-1 p-6 border border-cts-gray-200 no-boder">
                                                <div className="flex items-center gap-3 mb-4">
                                                    <h3 className="text-headings-h6 text-content-primary"><Tooltip title="Key measures that quantify and monitor trading risk."><span>{t('RiskManagement')}</span></Tooltip></h3>
                                                </div>

                                                {report && <div className="space-y-3">
                                                    <div className="flex justify-between items-center p-3 bg-trang rounded-lg shadow-cts-sm">
                                                        <Tooltip title="Ratio of gross profit to gross loss (values > 1 indicate profitability)."><span className="text-cts-gray-700 font-medium whitespace-nowrap">{t('profitfactor')}</span></Tooltip>
                                                        <span className="text-cts-green-600 font-semibold">
                                                            {report?.risk_management ? Number(report?.risk_management.profit_factor).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
                                                        </span>
                                                    </div>

                                                    <div className="flex justify-between items-center p-3 bg-trang rounded-lg shadow-cts-sm">
                                                        <Tooltip title="Average expected profit per trade (win/loss adjusted)."><span className="text-cts-gray-700 font-medium whitespace-nowrap">{t('Expectancy')}</span></Tooltip>
                                                        <span className={`font-semibold ${report?.risk_management && Number(report?.risk_management.expectancy) >= 0 ? 'text-cts-green-600' : 'text-cts-red-600'}`}>
                                                            {report?.risk_management ? Number(report?.risk_management.expectancy).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
                                                        </span>
                                                    </div>

                                                    <div className="flex justify-between items-center p-3 bg-trang rounded-lg shadow-cts-sm">
                                                        <Tooltip title="Largest peak-to-trough equity decline."><span className="text-cts-gray-700 font-medium whitespace-nowrap">{t('MaxDrawdown')}</span></Tooltip>
                                                        <span className="text-cts-red-600 font-semibold">
                                                            {report?.risk_management ? Number(report?.risk_management.max_drawdown).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
                                                        </span>
                                                    </div>

                                                    <div className="flex justify-between items-center p-3 bg-trang rounded-lg shadow-cts-sm">
                                                        <Tooltip title="Max drawdown expressed as a percentage of equity."><span className="text-cts-gray-700 font-medium whitespace-nowrap">{t('MaxDrawdown')} %</span></Tooltip>
                                                        <span className="text-cts-red-600 font-semibold">
                                                            {report?.risk_management ? Number(report?.risk_management.max_drawdown_percent).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}%
                                                        </span>
                                                    </div>

                                                    <div className="flex justify-between items-center p-3 bg-trang rounded-lg shadow-cts-sm">
                                                        <Tooltip title="Average trade size (lots) over the evaluated period."><span className="text-cts-gray-700 font-medium whitespace-nowrap">{t('AverageVolume')}</span></Tooltip>
                                                        <span className="text-cts-blue-600 font-semibold">
                                                            {report?.risk_management ? Number(report?.risk_management.average_volume).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
                                                        </span>
                                                    </div>

                                                    <div className="flex justify-between items-center p-3 bg-trang rounded-lg shadow-cts-sm">
                                                        <Tooltip title="Largest trade size (lots) recorded."><span className="text-cts-gray-700 font-medium whitespace-nowrap">{t('MaxVolume')}</span></Tooltip>
                                                        <span className="text-cts-blue-600 font-semibold">
                                                            {report?.risk_management?.max_volume ? Number(report?.risk_management.max_volume).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
                                                        </span>
                                                    </div>

                                                    <div className="flex justify-between items-center p-3 bg-trang rounded-lg shadow-cts-sm">
                                                        <Tooltip title="Smallest trade size (lots) recorded."><span className="text-cts-gray-700 font-medium whitespace-nowrap">{t('MinVolume')}</span></Tooltip>
                                                        <span className="text-cts-blue-600 font-semibold">
                                                            {report?.risk_management?.min_volume ? Number(report?.risk_management.min_volume).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>}
                                                {/* Risk Sub-metrics */}
                                                {report?.risk_management?.rick_metrics && (
                                                    <div className="mt-4">
                                                        <h4 className="text-lg font-semibold text-cts-gray-800 mb-3">{t('RiskSubmetrics')}</h4>
                                                        <div className="space-y-2">
                                                            <div className="flex justify-between items-center p-2 bg-trang rounded-lg shadow-cts-sm">
                                                                <span className="text-sm text-cts-gray-700 whitespace-nowrap">{t('LargestLoss')}</span>
                                                                <span className="text-cts-red-600 font-semibold text-sm">
                                                                    ${Number(report?.risk_management.rick_metrics.largest_loss).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </span>
                                                            </div>

                                                            <div className="flex justify-between items-center p-2 bg-trang rounded-lg shadow-cts-sm">
                                                                <span className="text-sm text-cts-gray-700 whitespace-nowrap">{t('LargestWin')}</span>
                                                                <span className="text-cts-green-600 font-semibold text-sm">
                                                                    ${Number(report?.risk_management.rick_metrics.largest_win).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </span>
                                                            </div>

                                                            <div className="flex justify-between items-center p-2 bg-trang rounded-lg shadow-cts-sm">
                                                                <span className="text-sm text-cts-gray-700 whitespace-nowrap">{t('Win/LossRatio')}</span>
                                                                <span className="text-cts-blue-600 font-semibold text-sm">
                                                                    {Number(report?.risk_management.rick_metrics.win_loss_ratio).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </span>
                                                            </div>

                                                            <div className="flex justify-between items-center p-2 bg-trang rounded-lg shadow-cts-sm">
                                                                <span className="text-sm text-cts-gray-700 whitespace-nowrap">{t('DrawdownPeriods')}</span>
                                                                <span className="text-cts-orange-600 font-semibold text-sm">
                                                                    {report?.risk_management.rick_metrics.drawdown_periods}
                                                                </span>
                                                            </div>

                                                            <div className="flex justify-between items-center p-2 bg-trang rounded-lg shadow-cts-sm">
                                                                <span className="text-sm text-cts-gray-700 whitespace-nowrap">{t('AvgDrawdownDuration')}</span>
                                                                <span className="text-cts-orange-600 font-semibold text-sm">
                                                                    {Number(report?.risk_management.rick_metrics.avg_drawdown_duration).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} {t('Days')}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>)}
                            </div>
                            <div className={`px-20`}>
                                <FooterPart page={46} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <HeaderPart />
                            <div className="px-20 pt-10" style={{ height: '1396px', overflow: 'hidden' }}>
                                <p className="text-2xl mt-30" style={{ color: '#00789D' }}>
                                    {t('Khuyến cáo & Miễn trừ trách nhiệm')}
                                </p>
                                <p className="text-2xl mb-4" style={{ color: '#00789D', lineHeight: '34px' }}>
                                    {t('Tài liệu này chỉ mang tính chất')}
                                    <strong> {t('hướng dẫn tham khảo')} </strong>
                                    {t('cho trader trong việc quản lý rủi ro và tối ưu giao dịch. Các ngưỡng, chỉ số, và rule cảnh báo được xây dựng dựa trên dữ liệu thống kê và thực tiễn nhưng')}
                                    <strong> {t('không đảm bảo lợi nhuận')} </strong>
                                    {t('hay loại trừ hoàn toàn rủi ro.')}
                                </p>
                                <p className="text-2xl mb-2" style={{ color: '#00789D' }}>
                                    {t('Người sử dụng cần:')}
                                </p>
                                <ul className="list-disc list-inside text-2xl space-y-2 mb-4" style={{ color: '#00789D' }}>
                                    <li>{t('Tự chịu trách nhiệm với quyết định giao dịch của mình.')}</li>
                                    <li>{t('Điều chỉnh chỉ số/Ranges phù hợp với tài khoản, chiến lược, và khẩu vị rủi ro riêng.')}</li>
                                    <li>{t('Hiểu rằng giao dịch ký quỹ/CFD/forex luôn tiềm ẩn nguy cơ mất mát vốn cao, có thể thua lỗ toàn bộ.')}</li>
                                </ul>
                                <p className="text-2xl leading-relaxed" style={{ color: '#00789D', lineHeight: '34px' }}>
                                    <span className="font-semibold text-cts-cyan-700" style={{ color: '#00789D' }}>
                                        {t('Nhà phát triển và LUMIR không chịu trách nhiệm')}
                                    </span>{' '}
                                    {t('với bất kỳ thiệt hại trực tiếp, gián tiếp, hay hệ quả nào phát sinh từ việc sử dụng tài liệu hoặc hệ thống rule tự động.')}
                                </p>
                                {/* <div className="mt-10 mb-4">
                            <img src="/downPdf/ftmo8.png" alt={t('Group 4')} className="w-100 h-100" />
                          </div> */}
                                {/* Primary Metrics */}
                                <div className={`grid ${isDown ? 'grid-cols-4' : 'grid-cols-1 md:grid-cols-2'} lg:grid-cols-4 gap-2 mb-6 mt-8`}>
                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-cts-indigo-100 rounded-lg flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-cts-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                                </svg>
                                            </div>
                                            <div>
                                                <Tooltip title="Real-time account value including open PnL."><p className="text-sm text-cts-gray-600 whitespace-nowrap">{t("Equity")}</p></Tooltip>
                                                <p className="text-lg font-semibold text-cts-gray-900">
                                                    ${liveTrade?.equity ? Number(liveTrade.equity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : report ? Number(report?.today_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-cts-cyan-100 rounded-lg flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-cts-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <Tooltip title="Account cash balance after closed trades only (no open PnL)."><p className="text-sm text-cts-gray-600 whitespace-nowrap">{t("Balance")}</p></Tooltip>
                                                <p className="text-lg font-semibold text-cts-gray-900">
                                                    ${report ? Number(report?.today_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-cts-blue-100 rounded-lg flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-cts-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                </svg>
                                            </div>
                                            <div>
                                                <Tooltip title="Total number of executed trades (closed orders)."><p className="text-sm text-cts-gray-600 whitespace-nowrap">{t('TotalTrades')}</p></Tooltip>
                                                <p className="text-lg font-semibold text-cts-gray-900">{report?.total_trades || 0}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-cts-green-100 rounded-lg flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-cts-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <Tooltip title="Percentage of trades that closed with profit."><p className="text-sm text-cts-gray-600 whitespace-nowrap">{t('winrate')}</p></Tooltip>
                                                <p className="text-lg font-semibold text-cts-green-600">{(report?.win_rate * 100)?.toFixed(1)}%</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Extended Metrics */}
                                <div className={`grid ${isDown ? 'grid-cols-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'} gap-2 mb-6`}>
                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-cts-orange-100 rounded-lg flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-cts-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <Tooltip title="Ratio of gross profit to gross loss (values > 1 indicate profitability)."><p className="text-sm text-cts-gray-600 whitespace-nowrap">{t('profitfactor')}</p></Tooltip>
                                                <p className="text-lg font-semibold text-cts-blue-600">
                                                    {Number(report?.profit_factor)?.toFixed(2)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-cts-cyan-100 rounded-lg flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-cts-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                </svg>
                                            </div>
                                            <div>
                                                <Tooltip title="Total profit after subtracting losses and fees."><p className="text-sm text-cts-gray-600 whitespace-nowrap">{t('NetProfit')}</p></Tooltip>
                                                <p className="text-lg font-semibold text-cts-cyan-600">
                                                    ${Number(report?.performance_summary?.net_profit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-cts-emerald-100 rounded-lg flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-cts-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                </svg>
                                            </div>
                                            <div>
                                                <Tooltip title="Total profit from winning trades before subtracting losses and fees."><p className="text-sm text-cts-gray-600 whitespace-nowrap">{t('GrossProfit')}</p></Tooltip>
                                                <p className="text-lg font-semibold text-cts-green-600">
                                                    ${Number(report?.performance_summary?.gross_profit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-cts-red-100 rounded-lg flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-cts-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                                </svg>
                                            </div>
                                            <div>
                                                <Tooltip title="Sum of trading costs such as commissions and swaps."><p className="text-sm text-cts-gray-600 whitespace-nowrap">{t('TotalFees')}</p></Tooltip>
                                                <p className="text-lg font-semibold text-cts-red-600">
                                                    ${Number(report?.performance_summary?.total_fees || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-cts-green-100 rounded-lg flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-cts-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <Tooltip title="Number of trades that ended with profit."><p className="text-sm text-cts-gray-600 whitespace-nowrap">{t('ProfitableTrades')}</p></Tooltip>
                                                <p className="text-lg font-semibold text-cts-green-600">{report?.performance_summary?.profitable_trades || 0}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-cts-red-100 rounded-lg flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-cts-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </div>
                                            <div>
                                                <Tooltip title="Number of trades that ended with a loss."><p className="text-sm text-cts-gray-600 whitespace-nowrap">{t('LosingTrades')}</p></Tooltip>
                                                <p className="text-lg font-semibold text-cts-red-600">{report?.performance_summary?.losing_trades || 0}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-cts-yellow-100 rounded-lg flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-cts-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                </svg>
                                            </div>
                                            <div>
                                                <Tooltip title="Total traded volume measured in standard lots (1 lot = 100,000 units FX)."><p className="text-sm text-cts-gray-600 whitespace-nowrap">{t("Lots")}</p></Tooltip>
                                                <p className="text-lg font-semibold text-cts-yellow-600">
                                                    {Number(report?.sum_volume || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-cts-violet-100 rounded-lg flex items-center justify-center mr-3">
                                                <svg className="w-4 h-4 text-cts-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <Tooltip title="Risk-adjusted return: average excess return divided by its volatility."><p className="text-sm text-cts-gray-600 whitespace-nowrap">{t("Sharpe Ratio")}</p></Tooltip>
                                                <p className="text-lg font-semibold text-cts-violet-600">
                                                    {Number(report?.sharpe_ratio || 0)?.toFixed(2)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Performance Metrics */}
                                <div className={`grid ${isDown ? 'grid-cols-3' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-4 `}>
                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <h4 className="text-sm font-semibold text-cts-gray-700 mb-3"><Tooltip title="Measures stability of your trading performance over time. Higher is more consistent."><span className='text-sm'>{t('consistency_score')}</span></Tooltip></h4>
                                        <div className="flex items-center justify-between">
                                            <div className="w-16 h-16 bg-cts-emerald-100 rounded-full flex items-center justify-center">
                                                <span className="text-lg font-bold text-cts-emerald-8000">
                                                    {((report?.consistency_score || 0) * 100)?.toFixed(0)}%
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs text-cts-gray-500">{t('Performance')}</p>
                                                <p className={`text-sm font-medium ${(report?.consistency_score || 0) >= 0.8 ? 'text-cts-green-600' :
                                                    (report?.consistency_score || 0) >= 0.5 ? 'text-cts-yellow-600' : 'text-cts-red-600'
                                                    }`}>
                                                    {(report?.consistency_score || 0) >= 0.8 ? 'Excellent' :
                                                        (report?.consistency_score || 0) >= 0.5 ? 'Good' : 'Needs Improvement'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <h4 className="text-sm font-semibold text-cts-gray-700 mb-3"><Tooltip title="Key measures of trading risk such as drawdowns and losses."><span className='text-sm'>{t('RiskMetrics')}</span></Tooltip></h4>
                                        <div className="space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Largest single loss across all trades."><span className="text-cts-gray-600 whitespace-nowrap">{t('max_loss')}:</span></Tooltip>
                                                <span className="font-medium text-cts-red-600">
                                                    ${Math.abs(Number(report?.max_loss || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Largest loss incurred in a single day."><span className="text-cts-gray-600 whitespace-nowrap">{t('max_daily_loss')}:</span></Tooltip>
                                                <span className="font-medium text-cts-red-600">
                                                    ${Math.abs(Number(report?.max_daily_loss || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Average expected profit per trade (win/loss adjusted)."><span className="text-cts-gray-600 whitespace-nowrap">{t("Expectancy")}:</span></Tooltip>
                                                <span className={`font-medium ${Number(report?.expectancy || 0) >= 0 ? 'text-cts-green-600' : 'text-cts-red-600'}`}>
                                                    ${Number(report?.expectancy || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <h4 className="text-sm font-semibold text-cts-gray-700 mb-3"><Tooltip title="Overview of trading activity, counts, and averages."><span className='text-sm'>{t('TradingSummary')}</span></Tooltip></h4>
                                        <div className="space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Number of distinct days with at least one trade."><span className="text-cts-gray-600 whitespace-nowrap">{t('trading_days')}:</span></Tooltip>
                                                <span className="font-medium text-cts-gray-900">{report?.count_trading_days || 0}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Average profit/loss per trade across all trades."><span className="text-cts-gray-600 whitespace-nowrap">{t('AvgProfitPerTrade')}:</span></Tooltip>
                                                <span className="font-medium text-cts-green-600">
                                                    ${Number(report?.performance_summary?.avg_profit_per_trade || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Average profit of winning trades."><span className="text-cts-gray-600 whitespace-nowrap">{t('AvgProfit')}:</span></Tooltip>
                                                <span className="font-medium text-cts-green-600">
                                                    ${Number(report?.mean_profitable || report?.performance_summary?.avg_profit_per_win || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Average loss of losing trades."><span className="text-cts-gray-600 whitespace-nowrap">{t('AvgLoss')}:</span></Tooltip>
                                                <span className="font-medium text-cts-red-600">
                                                    ${Math.abs(Number(report?.mean_loss || report?.performance_summary?.avg_loss_per_loss || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <h4 className="text-sm font-semibold text-cts-gray-700 mb-3"><Tooltip title="Performance breakdown including best/worst trades and RRR."><span className='text-sm'>{t('TradePerformance')}</span></Tooltip></h4>
                                        <div className="space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Highest profit from a single trade."><span className="text-cts-gray-600 whitespace-nowrap">{t('BestTrade')}:</span></Tooltip>
                                                <span className="font-medium text-cts-green-600">
                                                    ${Number(report?.performance_summary?.best_trade?.profit || report?.performance_summary?.best_trade?.profit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Largest loss from a single trade."><span className="text-cts-gray-600 whitespace-nowrap">{t('WorstTrade')}:</span></Tooltip>
                                                <span className="font-medium text-cts-red-600">
                                                    ${Math.abs(Number(report?.performance_summary?.worst_trade.loss || report?.performance_summary?.worst_trade?.loss || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Average risk-reward ratio across trades."><span className="text-cts-gray-600 whitespace-nowrap">{t('AvgRRR')}:</span></Tooltip>
                                                <span className="font-medium text-cts-blue-600">
                                                    {Number(report?.mean_RRR || 0)?.toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <h4 className="text-sm font-semibold text-cts-gray-700 mb-3"><Tooltip title="Additional indicators providing deeper analytical insight."><span className='text-sm'>{t('AdvancedMetrics')}</span></Tooltip></h4>
                                        <div className="space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Risk-adjusted return: average excess return divided by its volatility."><span className="text-cts-gray-600 whitespace-nowrap">{t('Sharpe Ratio')}:</span></Tooltip>
                                                <span className="font-medium text-cts-violet-600">
                                                    {Number(report?.sharpe_ratio || 0)?.toFixed(2)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Ratio of gross profit to gross loss (values > 1 indicate profitability)."><span className="text-cts-gray-600 whitespace-nowrap">{t('profitfactor')}:</span></Tooltip>
                                                <span className="font-medium text-cts-blue-600">
                                                    {Number(report?.profit_factor || 0)?.toFixed(2)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Total traded volume measured in standard lots (1 lot = 100,000 units FX)."><span className="text-cts-gray-600 whitespace-nowrap">{t('TotalLots')}:</span></Tooltip>
                                                <span className="font-medium text-cts-yellow-600">
                                                    {Number(report?.sum_volume || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-trang rounded-lg px-4 py-2 shadow-cts-sm border border-cts-emerald-200">
                                        <h4 className="text-sm font-semibold text-cts-gray-700 mb-3"><Tooltip title="Aggregated profit and loss breakdown across your trades."><span className='text-sm'>{t('FinancialSummary')}</span></Tooltip></h4>
                                        <div className="space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Total profit from winning trades before subtracting losses and fees."><span className="text-cts-gray-600 whitespace-nowrap">{t('GrossProfit')}:</span></Tooltip>
                                                <span className="font-medium text-cts-green-600">
                                                    ${Number(report?.performance_summary?.gross_profit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Sum of trading costs such as commissions and swaps."><span className="text-cts-gray-600 whitespace-nowrap">{t('TotalFees')}:</span></Tooltip>
                                                <span className="font-medium text-cts-red-600">
                                                    ${Number(report?.performance_summary?.total_fees || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <Tooltip title="Total profit after subtracting losses and fees."><span className="text-cts-gray-600 whitespace-nowrap">{t('NetProfit')}:</span></Tooltip>
                                                <span className={`font-medium ${Number(report?.sum_net_profit || 0) >= 0 ? 'text-cts-green-600' : 'text-cts-red-600'}`}>
                                                    ${Number(report?.sum_net_profit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="px-20">
                                <FooterPart page={47} />
                            </div>
                        </div>

                        <div style={{ height: '1696px', overflow: 'hidden' }} className="w-full p-12">
                            <div className="text-center my-8" style={{ marginTop: '780px', color: '#000' }}>
                                <p className="font-bold text-2xl mb-2">{t('Cảm ơn bạn đã dành thời gian đọc báo cáo.')}</p>
                                <p className="font-bold text-2xl">{t('Chúc bạn luôn giữ vững bản lĩnh và tiến xa trên con đường trở thành Elite Trader. 🚀')}</p>
                            </div>
                            <div className="flex justify-center mt-12 mb-4">
                                <img src="/downPdf/logo.png" alt={t('Group 4')} className="w-40 h-40" />
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};


const HeaderPart = () => {
    return (
        <div className='relative w-full max-w-[1122px] flex flex-col items-center pt-4'>
            <div className="flex justify-end w-full absolute -top-0 right-2">
                <img src="/downPdf/logo.png" className='w-24 h-24' alt="" />
            </div>
            <div className="flex justify-start w-full items-start ">
                <p className='text-center yellow-color text-xl pt-8' style={{
                    paddingLeft: '7rem',
                }}>www.lumir.finance</p>
            </div>
            <p className='w-[80%] h-0.5 mb-8 bg-black-cts' />
        </div>
    )
}

const FooterPart = (props: any) => {

    const { page } = props

    return (
        <div className={`pt-14`}>
            <p className='w-full h-0.5 mt-2 mb-8 bg-black-cts' />
            <div className="flex justify-between items-center w-full">
                <div className="flex gap-3 items-center">
                    <img src="/downPdf/genIcon.svg" className='w-10 h-10' style={{ transform: 'translateY(8px)' }} alt="" />
                    <div className="flex gap-6 items-center">
                        <p className='m-0 whitespace-nowrap font-bold text-2xl text-black-normal'>BeQ Ops Lumir</p>
                        <p className='m-0 w-0.5 h-8 bg-yellow-color' style={{ transform: 'translateY(8px)' }} />
                        <p className='m-0 whitespace-nowrap font-bold text-2xl yellow-color'>Metrix Rules Report</p>
                    </div>
                </div>
                <div className="flex gap-1 items-center text-xl text-black-normal">
                    Page <span className='font-bold yellow-color'>{page}</span>
                </div>
            </div>
        </div>
    )
}

export default MetrixPDF;